import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Descriptions,
  Dropdown,
  Input,
  Modal,
  Popconfirm,
  Popover,
  Select,
  Space as AntSpace,
  Spin,
  Table,
  Tag,
  Form,
  Tooltip,
  message,
} from 'antd';
import type { InputRef } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import type { ColumnsType } from 'antd/es/table';
import { Resizable } from 'react-resizable';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExpandAltOutlined,
  FilterOutlined,
  GroupOutlined,
  PlusOutlined,
  PushpinOutlined,
  ReloadOutlined,
  SearchOutlined,
  DownloadOutlined,
  HolderOutlined,
  SettingOutlined,
  SortAscendingOutlined,
  TableOutlined,
  UndoOutlined,
  UploadOutlined,
  CalculatorOutlined,
  SwapOutlined,
  ThunderboltOutlined,
  MoreOutlined,
  MenuOutlined,
  ColumnHeightOutlined,
  LinkOutlined,
  ApiOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  InboxOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import type {
  FieldDefinition,
  FieldReplacement,
  FilterLeaf,
  ItemListResponse,
  ItemRow,
  ItemType,
  KanbanViewConfig,
  SortEntry,
  SpaceMember,
  SpaceUserGroup,
  View,
} from '@lens/shared';
import dayjs from 'dayjs';
import {
  expandUserGroupSelection,
  withUserGroupOptions,
} from './userGroupOptions.ts';
import { renderUserBulkPopup } from './UserBulkActions.tsx';
import { dateFieldValue, datePickerFormat, DATE_FORMAT } from '../../shared/lib/dateValue';
import * as XLSX from 'xlsx';
import { ApiError, apiRequest } from '../../shared/api/client.ts';
import { uploadAttachment } from '../../shared/api/attachments.ts';
import { RemoteSourcePanel } from '../../shared/import/RemoteSourcePanel.tsx';
import { matchHeaders, readWorkbook, resolveSheetName } from '../../shared/import/workbook.ts';
import { renderFieldValue, fieldValueToText } from '../../shared/fields/renderFieldValue.tsx';
import { MarkdownEditorModal } from '../../shared/fields/MarkdownEditorModal.tsx';
import { MarkdownInlineEditor } from '../../shared/fields/MarkdownInlineEditor.tsx';
import {
  FilterValueInput,
  OPERATOR_LABELS,
  getOperatorsForFieldType,
} from '../../shared/components/FilterRow.tsx';
import { getSpace, listSpaceUserGroups, resolveSpaceSlug } from './api.ts';
import { getDictionary, listEntries as listDictEntries } from '../dictionaries/api.ts';
import { entryLabel } from '../dictionaries/labelHelpers.ts';
import { updateView } from '../views/api.ts';
import { listItemTypes, getItemType } from '../item-types/api.ts';
import { createItem, deleteItem, getItem, patchItem, queryItems, reorderItems, bulkDeleteItems, bulkDuplicateItems, bulkUpdateItems, duplicateItem, importItemsCsv } from '../items/api.ts';
import { useJobs } from '../jobs/JobsContext.tsx';
import { useNotifications } from '../notifications/NotificationsContext.tsx';
import { canRefreshBoard, isFocusInEditor } from './boardRefresh.ts';
import type { ItemRowWithRefs } from '../items/api.ts';
import { listViews, createView, duplicateView, reorderViews } from '../views/api.ts';
import { emitSpacesChanged } from '../../shared/lib/spacesBus.ts';
import { defaultsFromFilters, defaultsFromFieldConfigs, carryFieldsToType, autofillCurrentUserDefaults, currentUserFilterSlugs } from '../views/defaults.ts';
import { KanbanBoard, buildKanbanLayout, ITEM_TYPE_SLUG } from '../views/KanbanBoard.tsx';
import { WhiteboardSummaryContext, useWhiteboardSummary } from '../whiteboard/useWhiteboardSummary.ts';
import { WhiteboardBadge } from '../whiteboard/WhiteboardBadge.tsx';
import type { KanbanCardDropResult } from '../views/KanbanBoard.tsx';
import { ItemDrawer } from '../items/ItemDrawer.tsx';
import { useAuth } from '../auth/AuthContext.tsx';
import { useAiPanel } from '../ai/AiPanelContext.tsx';
import { SyncInboxModal } from '../jira/SyncInboxModal.tsx';
import { EstimateInboxModal } from '../hermes/EstimateInboxModal.tsx';
import { useHermesSessions } from '../hermes/HermesSessionsContext.tsx';
import { HERMES_FINALIZED_EVENT, type HermesFinalizedDetail } from '../hermes/HermesSessionDock.tsx';
import { WeightValidationDrawer } from '../jira/WeightValidationDrawer.tsx';
import type {
  LinkAggregateColumnConfig,
  LinkedAggregateListEntry,
  LinkedAggregateValue,
  LinkRule,
  Space as SpaceDto,
  ViewComputedColumnConfig,
  ReferenceAttributeColumnConfig,
} from '@lens/shared';
import { resolveTitleField } from '@lens/shared';
import { LinkAggregateColumnModal } from './LinkAggregateColumnModal.tsx';
import {
  ViewComputedColumnModal,
  extractViewComputedColumns,
} from './ViewComputedColumnModal.tsx';
import {
  ReferenceAttributeColumnModal,
  extractReferenceAttributeColumns,
  referenceAttributeColumnSlug,
} from './ReferenceAttributeColumnModal.tsx';
import { buildColumnsConfig, isFieldColumn, readSavedColumns } from '../views/columnsConfig.ts';
import {
  collectRefAttrDictionaryIds,
  getFinalRefAttrColumn,
  getRefAttrPath,
  renderRefAttrPath,
  walkRefAttrPath,
  type RefAttrCtx,
} from './refAttrPath.ts';
import { KanbanColumnPathPicker } from './KanbanColumnPathPicker.tsx';
import { QuickFilterBar } from '../../shared/table/QuickFilterBar.tsx';
import { makeGroupKeyComparator } from '../../shared/table/groupTree';
import { ColumnFilterPopover } from '../../shared/table/ColumnFilterPopover.tsx';
import { QuickFiltersManagerModal } from './QuickFiltersManagerModal.tsx';
import { combineFilterGroupsAnd, extractQuickFilters, type QuickFilter } from '../../shared/table/quickFilters.ts';
import { applyLinkRuleBulk, listLinkRules } from '../link-rules/api.ts';
import { spacing, radius, statusCategory } from '../../shared/theme/tokens.ts';
import { useThemeMode } from '../../shared/theme/ThemeProvider.tsx';
import { useDocumentTitle } from '../../shared/hooks/useDocumentTitle.ts';
import { useTableSettings } from '../../shared/hooks/useTableSettings.ts';
import { writePref, pushRecentSpace } from '../../shared/prefs/store.ts';
import { SpaceIconPicker } from '../../shared/components/SpaceIconPicker.tsx';
import { getSpaceIconDef, resolveSpaceIconKey, resolveSpaceColor } from '../../shared/components/spaceIcons.ts';
import { JiraIssueLink } from '../../shared/components/JiraIssueLink.tsx';
import { AiSparkIcon } from '../../shared/components/AiSparkIcon.tsx';

let suppressHeaderSortUntil = 0;

// Компактное относительное время для колонок Создано/Обновлено.
// Полная дата уходит в Tooltip title. Без сторонних плагинов dayjs.
function formatRelativeTime(iso: string): string {
  const d = dayjs(iso);
  if (!d.isValid()) return '';
  const now = dayjs();
  const diffSec = now.diff(d, 'second');
  if (diffSec < 0) return d.format('DD.MM.YYYY');
  if (diffSec < 60) return 'только что';
  const diffMin = now.diff(d, 'minute');
  if (diffMin < 60) return `${diffMin} мин. назад`;
  const diffHour = now.diff(d, 'hour');
  if (now.isSame(d, 'day')) return `${diffHour} ч. назад`;
  if (d.isSame(now.subtract(1, 'day'), 'day')) return 'вчера';
  const diffDay = now.diff(d, 'day');
  if (diffDay < 30) return `${diffDay} дн. назад`;
  // Старше 30 дней — однозначная дата с годом (год не теряем, P3).
  return d.format('DD.MM.YYYY');
}

// Типы полей, для которых доступен режим «заменить подстроку» (todos #4).
const TEXT_FIELD_TYPES = ['text', 'textarea', 'markdown', 'url'];

// Период фонового обновления доски (ITEM-16). 45 секунд — компромисс: запрос
// тянет до 500 записей со связями и вычисляемыми полями, поэтому чаще дёргать
// накладно, а реже — доска снова выглядит «замороженной».
const BOARD_REFRESH_MS = 45_000;

// Одна операция массового редактирования (объединённая модалка).
interface BulkEditOp {
  key: string;
  fieldSlug: string | null;
  // 'set' — задать значение всем; 'replace' — заменить подстроку (текст. поля).
  mode: 'set' | 'replace';
  value: unknown; // для mode='set'
  search: string; // для mode='replace'
  replace: string; // для mode='replace'
}

function makeBulkEditOp(fieldSlug: string | null, isText: boolean): BulkEditOp {
  return {
    key: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()),
    fieldSlug,
    mode: isText ? 'replace' : 'set',
    value: undefined,
    search: '',
    replace: '',
  };
}

function formatViewComputedNumber(value: number, decimals = 0): string {
  return decimals >= 0 ? value.toFixed(decimals) : String(value);
}

function viewComputedColumnSlug(key: string): string {
  return `_vc:${key}`;
}

function sortableSyntheticValue(value: LinkedAggregateValue | number | string | null | undefined): number | string | null {
  if (Array.isArray(value)) return value.length;
  if (typeof value === 'number' || typeof value === 'string') return value;
  return null;
}

function compareSortableValues(a: unknown, b: unknown, direction: 'asc' | 'desc'): number {
  const emptyA = a === null || a === undefined || a === '';
  const emptyB = b === null || b === undefined || b === '';
  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;
  if (emptyB) return -1;
  let result: number;
  if (typeof a === 'number' && typeof b === 'number') {
    result = a - b;
  } else {
    result = String(a).localeCompare(String(b), 'ru', { numeric: true });
  }
  return direction === 'asc' ? result : -result;
}

interface ColumnSettingsOption {
  slug: string;
  name: string;
  field?: FieldDefinition;
}

// ─── Resizable + Sortable column header ─────────────────────────────────────

interface ResizableTitleProps extends React.HTMLAttributes<HTMLTableCellElement> {
  onResize?: (e: React.SyntheticEvent, data: { size: { width: number } }) => void;
  width?: number;
  'data-col-slug'?: string;
}

function ResizableTitle({ onResize, width, 'data-col-slug': colSlug, ...restProps }: ResizableTitleProps) {
  const thRef = useRef<HTMLTableCellElement | null>(null);
  const sortable = useSortable({ id: colSlug ?? '__none__', disabled: !colSlug });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;
  const setRefs = useCallback((node: HTMLTableCellElement | null) => {
    thRef.current = node;
    setNodeRef(node);
  }, [setNodeRef]);

  // Локальная ширина во время drag — НЕ поднимаем state наверх до отпускания.
  // Резайз ставит ширину напрямую в `<col>` (header + body), коммитим в outer state
  // только в onResizeStop. Это убирает full-table re-render на каждый mousemove.
  const [liveWidth, setLiveWidth] = useState<number | undefined>(width);
  useEffect(() => { setLiveWidth(width); }, [width]);

  const applyDomWidth = useCallback((newWidth: number) => {
    const th = thRef.current;
    if (!th) return;
    const idx = th.cellIndex;
    if (idx < 0) return;
    const tableWrap = th.closest('.ant-table');
    if (!tableWrap) return;
    const colgroups = tableWrap.querySelectorAll('colgroup');
    colgroups.forEach((cg) => {
      const col = cg.children[idx] as HTMLElement | undefined;
      if (col) col.style.width = `${newWidth}px`;
    });
  }, []);

  const thStyle: React.CSSProperties = {
    ...restProps.style,
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
  };

  // Resize handle — visible on hover (see global.css .lens-resize-handle)
  const resizeHandle = onResize ? (
    <span
      className="lens-resize-handle"
      style={{
        position: 'absolute',
        right: -5,
        top: 0,
        bottom: 0,
        width: 10,
        cursor: 'col-resize',
        zIndex: 4,
      }}
      onMouseDown={(e) => {
        suppressHeaderSortUntil = Date.now() + 400;
        e.stopPropagation();
        if (!width && thRef.current && onResize) {
          onResize(e, { size: { width: thRef.current.offsetWidth } });
        }
      }}
      onPointerDown={(e) => {
        suppressHeaderSortUntil = Date.now() + 400;
        e.stopPropagation();
      }}
      onClick={(e) => {
        suppressHeaderSortUntil = Date.now() + 400;
        e.stopPropagation();
      }}
    />
  ) : null;

  // Wrap children with DnD drag handle (the text part of the header)
  const { children, className, ...thRest } = restProps;
  const inner = colSlug ? (
    <div
      className="lens-col-header"
      style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}
    >
      <span
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        className="lens-col-drag"
        style={{ cursor: 'grab', display: 'inline-flex', flex: '0 0 auto', touchAction: 'none' }}
        aria-label="Переместить колонку"
      >
        <HolderOutlined />
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>{children}</span>
    </div>
  ) : children;

  if (!width) {
    return (
      <th {...thRest} ref={setRefs} className={className} style={thStyle} data-col-slug={colSlug}>
        {inner}
        {resizeHandle}
      </th>
    );
  }

  return (
    <Resizable
      width={liveWidth ?? width!}
      height={0}
      handle={resizeHandle!}
      onResize={(_e, { size }) => {
        setLiveWidth(size.width);
        applyDomWidth(size.width);
      }}
      onResizeStart={() => {
        suppressHeaderSortUntil = Date.now() + 400;
      }}
      onResizeStop={(e, data) => {
        onResize?.(e, data);
      }}
      draggableOpts={{ enableUserSelectHack: false }}
      minConstraints={[50, 0]}
    >
      <th {...thRest} ref={setRefs} className={className} style={thStyle} data-col-slug={colSlug}>
        {inner}
      </th>
    </Resizable>
  );
}

// ─── Inline-editable cell ───────────────────────────────────────────────────

interface CellEditorProps {
  value: unknown;
  field: FieldDefinition;
  onSave: (value: unknown) => void;
  resolvedRefs?: Record<string, string>;
  referenceOptions?: Array<{ value: string; label: string }>;
  loadReferenceOptions?: (field: FieldDefinition) => Promise<Array<{ value: string; label: string }>>;
  onResolvedRefsLoaded?: (refs: Record<string, string>) => void;
  itemId?: string;
  /** Загрузчик изображений для markdown-редактора (cmd+v / drag-drop). */
  onUploadImage?: (file: File) => Promise<string>;
  /**
   * Группы пользователей пространства — отдельный раздел в списке поля
   * «участник»: выбор группы подставляет её участников по отдельности.
   */
  userGroups?: SpaceUserGroup[];
}

function CellEditor({
  value,
  field,
  onSave,
  resolvedRefs,
  referenceOptions,
  loadReferenceOptions,
  onResolvedRefsLoaded,
  itemId,
  onUploadImage,
  userGroups = [],
}: CellEditorProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(
    value !== null && value !== undefined ? String(value) : '',
  );
  const inputRef = useRef<InputRef>(null);
  const [refOptions, setRefOptions] = useState<Array<{ value: string; label: string }>>(referenceOptions ?? []);
  const triggerProps = (fn: () => void) => ({ onClick: fn, onFocus: fn });

  // Computed fields always read-only
  if (field.fieldType === 'computed') {
    // Числовой computed выравниваем вправо (P1-6): значение — число либо
    // строка, состоящая только из числа.
    const computedIsNumeric =
      typeof value === 'number'
      || (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value)));
    return (
      <div
        style={{
          minHeight: 22,
          padding: '0 4px',
          ...(computedIsNumeric ? { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' } : null),
        }}
      >
        {renderFieldValue(value, field, resolvedRefs, 'cell')}
      </div>
    );
  }

  const startEdit = () => {
    setDraft(value !== null && value !== undefined ? String(value) : '');
    setEditing(true);
    setTimeout(() => inputRef.current?.focus?.(), 0);
  };

  const commit = () => {
    setEditing(false);
    let parsed: unknown = draft || null;
    if (field.fieldType === 'number') {
      const n = Number(draft);
      parsed = draft.trim() === '' ? null : Number.isNaN(n) ? null : n;
    }
    if (parsed !== value) {
      onSave(parsed);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') setEditing(false);
  };

  // Reference/user field — select from dictionary or space members
  if (field.fieldType === 'reference' || field.fieldType === 'user') {
    const config = field.config as { dictionaryId?: string; multiple?: boolean } | null;
    const isMulti = config?.multiple ?? false;
    const options = refOptions.length > 0 ? refOptions : (referenceOptions ?? []);
    const renderReferenceFallback = () => {
      const labelById = new Map(options.map((option) => [option.value, option.label]));
      if (isMulti && Array.isArray(value)) {
        const labels = value.map((entryId) => labelById.get(String(entryId)) ?? resolvedRefs?.[String(entryId)] ?? String(entryId));
        return labels.length > 0 ? labels.join(', ') : '—';
      }
      if (typeof value === 'string') {
        return labelById.get(value) ?? resolvedRefs?.[value] ?? value;
      }
      return null;
    };
    if (!editing) {
      return (
        <div
          style={{ minHeight: 22, cursor: 'pointer', padding: '0 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          {...triggerProps(() => {
            setEditing(true);
            if (options.length === 0 && loadReferenceOptions) {
              loadReferenceOptions(field)
                .then((loadedOptions) => {
                  setRefOptions(loadedOptions);
                  onResolvedRefsLoaded?.(
                    Object.fromEntries(loadedOptions.map((option) => [option.value, option.label])),
                  );
                })
                .catch(() => {});
            } else if (config?.dictionaryId && options.length === 0) {
              Promise.all([
                getDictionary(config.dictionaryId),
                listDictEntries(config.dictionaryId, { limit: 200 }),
              ])
                .then(([dict, res]) => {
                  const loadedOptions = res.items.map((e) => ({ value: e.id, label: entryLabel(e, dict) }));
                  setRefOptions(loadedOptions);
                  onResolvedRefsLoaded?.(
                    Object.fromEntries(loadedOptions.map((option) => [option.value, option.label])),
                  );
                })
                .catch(() => {});
            }
          })}
          tabIndex={0}
          role="button"
        >
          {renderReferenceFallback() ?? renderFieldValue(value, field, resolvedRefs)}
        </div>
      );
    }
    const handleChange = (v: unknown) => {
      // Группу в значении не оставляем — сразу разворачиваем в участников.
      const next = field.fieldType === 'user' ? expandUserGroupSelection(v, userGroups) : v;
      const selectedIds = Array.isArray(next) ? next : (typeof next === 'string' ? [next] : []);
      const nextResolvedRefs = Object.fromEntries(
        options
          .filter((option) => selectedIds.includes(option.value))
          .map((option) => [option.value, option.label]),
      );
      if (Object.keys(nextResolvedRefs).length > 0) {
        onResolvedRefsLoaded?.(nextResolvedRefs);
      }
      onSave(next ?? null);
      setEditing(false);
    };
    return (
      <Select
        size="small"
        mode={isMulti ? 'multiple' : undefined}
        value={isMulti ? (Array.isArray(value) ? value : []) : (typeof value === 'string' ? value : undefined)}
        style={{ minWidth: 140 }}
        allowClear
        options={
          (field.fieldType === 'user'
            ? withUserGroupOptions(options, userGroups, isMulti)
            : options) as DefaultOptionType[]
        }
        showSearch
        filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        popupRender={
          field.fieldType === 'user' && isMulti
            ? (menu) => renderUserBulkPopup(menu, options.map((o) => o.value), value, handleChange)
            : undefined
        }
        onChange={handleChange}
        autoFocus
        open
        onBlur={() => setEditing(false)}
        placeholder="Выберите..."
      />
    );
  }

  if (field.fieldType === 'select') {
    const config = field.config as { options?: Array<{ value: string; label: string; category?: string }> };
    const options = config.options ?? [];
    if (!editing) {
      // Статусное поле: опция несёт category → локальный чип категории с точкой,
      // цвета строго из statusCategory (единый источник правды с канбаном).
      const opt = options.find((o) => o.value === value);
      const cat = opt?.category as keyof typeof statusCategory | undefined;
      const sc = cat ? statusCategory[cat] : undefined;
      return (
        <div
          style={{ minHeight: 22, cursor: 'pointer', padding: '0 4px' }}
          {...triggerProps(startEdit)}
          tabIndex={0}
          role="button"
        >
          {sc ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: spacing.xs,
                background: `var(--status-${cat}-bg)`,
                color: `var(--status-${cat}-fg)`,
                borderRadius: radius.md,
                padding: '1px 8px',
                fontSize: 12,
                lineHeight: 1.6,
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: `var(--status-${cat}-dot)`,
                  flex: '0 0 auto',
                }}
              />
              {opt?.label || String(value)}
            </span>
          ) : (
            renderFieldValue(value, field, resolvedRefs)
          )}
        </div>
      );
    }
    return (
      <Select
        size="small"
        value={typeof value === 'string' ? value : undefined}
        style={{ minWidth: 120 }}
        allowClear
        options={options.map((o) => ({ value: o.value, label: o.label || o.value }))}
        onChange={(v) => { onSave(v ?? null); setEditing(false); }}
        autoFocus
        open
        onBlur={() => setEditing(false)}
      />
    );
  }

  // Multi-select field
  if (field.fieldType === 'multi_select') {
    const config = field.config as { options?: Array<{ value: string; label: string }> };
    const options = config.options ?? [];
    if (!editing) {
      return (
        <div
          style={{ minHeight: 22, cursor: 'pointer', padding: '0 4px' }}
          {...triggerProps(startEdit)}
          tabIndex={0}
          role="button"
        >
          {renderFieldValue(value, field, resolvedRefs)}
        </div>
      );
    }
    return (
      <Select
        size="small"
        mode="multiple"
        value={Array.isArray(value) ? value : []}
        style={{ minWidth: 140 }}
        options={options.map((o) => ({ value: o.value, label: o.label || o.value }))}
        onChange={(v) => { onSave(v.length > 0 ? v : null); setEditing(false); }}
        autoFocus
        open
        onBlur={() => setEditing(false)}
      />
    );
  }

  if (field.fieldType === 'boolean') {
    if (!editing) {
      return (
        <div
          style={{ minHeight: 22, cursor: 'pointer', padding: '0 4px' }}
          {...triggerProps(startEdit)}
          tabIndex={0}
          role="button"
        >
          {renderFieldValue(value, field, resolvedRefs)}
        </div>
      );
    }
    return (
      <Select
        size="small"
        value={typeof value === 'boolean' ? value : undefined}
        style={{ minWidth: 100 }}
        allowClear
        options={[
          { value: true, label: 'Да' },
          { value: false, label: 'Нет' },
        ]}
        onChange={(v) => { onSave(v ?? null); setEditing(false); }}
        autoFocus
        open
        onBlur={() => setEditing(false)}
      />
    );
  }

  // Date field — AntD DatePicker
  if (field.fieldType === 'date') {
    const config = field.config as { includeTime?: boolean };
    if (!editing) {
      return (
        <div
          style={{ minHeight: 22, cursor: 'pointer', padding: '0 4px' }}
          {...triggerProps(startEdit)}
          tabIndex={0}
          role="button"
        >
          {renderFieldValue(value, field, resolvedRefs)}
        </div>
      );
    }
    return (
      <DatePicker
        size="small"
        showTime={config.includeTime ?? false}
        format={datePickerFormat(config.includeTime ?? false)}
        value={value ? dayjs(String(value)) : null}
        onChange={(d) => {
          onSave(dateFieldValue(d, config.includeTime ?? false));
          setEditing(false);
        }}
        // Не onBlur: клик по стрелкам месяца/года в панели календаря уводит
        // фокус с инпута, и blur закрывал бы редактор до переключения месяца
        onOpenChange={(open) => {
          if (!open) setEditing(false);
        }}
        autoFocus
        open
      />
    );
  }

  // Markdown / textarea — inline-превью + кнопка-«развернуть» открывает модалку
  if (field.fieldType === 'markdown' || field.fieldType === 'textarea') {
    return (
      <>
        <div
          className="lens-mdcell"
          // Клик по ссылке в тексте — переход по ней, а не открытие редактора.
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('a[href]')) return;
            setEditing(true);
          }}
          role="button"
          tabIndex={0}
          aria-label="Открыть поле в редакторе"
          title="Нажмите, чтобы открыть полный текст"
        >
          <div className="lens-mdcell__body">
            {renderFieldValue(value, field, resolvedRefs, 'cell')}
          </div>
          <Button
            size="small"
            type="text"
            icon={<EditOutlined />}
            aria-label="Редактировать поле в большом окне"
            onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            className="lens-mdcell__edit"
          />
        </div>
        <MarkdownEditorModal
          open={editing}
          value={value !== null && value !== undefined ? String(value) : ''}
          title={field.name}
          mode={field.fieldType === 'markdown' ? 'markdown' : 'textarea'}
          fieldName={field.name}
          itemId={itemId}
          onUploadImage={onUploadImage}
          onClose={() => setEditing(false)}
          onSave={(next) => {
            const parsed = next.trim() === '' ? null : next;
            if (parsed !== value) onSave(parsed);
          }}
        />
      </>
    );
  }

  if (editing) {
    return (
      <Input
        ref={inputRef}
        size="small"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        style={{ minWidth: 100 }}
      />
    );
  }

  const isNumber = field.fieldType === 'number';
  return (
    <div
      style={{
        minHeight: 22,
        cursor: 'text',
        padding: '0 4px',
        ...(isNumber ? { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' } : null),
      }}
      {...triggerProps(startEdit)}
      tabIndex={0}
      role="button"
    >
      {renderFieldValue(value, field, resolvedRefs, 'cell')}
    </div>
  );
}

// ─── Filter / Sort controls (M6) ──────────────────────────────────────────

interface FilterBarProps {
  fields: FieldDefinition[];
  filters: FilterLeaf[];
  onFiltersChange: (filters: FilterLeaf[]) => void;
  sorts: SortEntry[];
  onSortsChange: (sorts: SortEntry[]) => void;
  search: string;
  onSearchChange: (search: string) => void;
  ensureReferenceOptions?: (field: FieldDefinition) => Promise<Array<{ value: string; label: string }>>;
  resolvedRefs?: Record<string, string>;
  extraButtons?: React.ReactNode;
  /** Дополнительные имена для виртуальных полей сортировки (key → label). */
  sortNames?: Record<string, string>;
  /** Фиксированные условия представления (read-only, без удаления). P1-4. */
  viewFilters?: FilterLeaf[];
}

/** Человекочитаемая запись условия фильтра для read-only chip'а. */
function describeFilter(
  filter: FilterLeaf,
  fields: FieldDefinition[],
  resolvedRefs?: Record<string, string>,
): string {
  const fd = fields.find((f) => f.slug === filter.fieldSlug);
  const fieldName = fd?.name ?? filter.fieldSlug;
  const op = OPERATOR_LABELS[filter.operator] ?? filter.operator;
  if (filter.operator === 'is_empty' || filter.operator === 'is_not_empty') {
    return `${fieldName} ${op}`;
  }
  const v = filter.value;
  const resolveOne = (raw: unknown): string => {
    const key = String(raw);
    if (resolvedRefs?.[key]) return resolvedRefs[key];
    if (fd?.fieldType === 'select' || fd?.fieldType === 'multi_select') {
      const opts = (fd.config as { options?: Array<{ value: string; label: string }> } | null)?.options;
      const found = opts?.find((o) => o.value === key);
      if (found) return found.label || found.value;
    }
    return key;
  };
  const valueStr = Array.isArray(v)
    ? v.map(resolveOne).join(', ')
    : v === null || v === undefined ? '' : resolveOne(v);
  return `${fieldName} ${op} ${valueStr}`.trim();
}

function isFilterApplicable(filter: FilterLeaf): boolean {
  if (filter.operator === 'is_empty' || filter.operator === 'is_not_empty') return true;
  const v = filter.value;
  if (v === undefined || v === null) return false;
  if (typeof v === 'string' && v.trim() === '') return false;
  if (Array.isArray(v) && v.length === 0) return false;
  return true;
}

function FilterBar({
  fields,
  filters,
  onFiltersChange,
  sorts,
  onSortsChange,
  search,
  onSearchChange,
  ensureReferenceOptions,
  resolvedRefs,
  extraButtons,
  sortNames,
  viewFilters = [],
}: FilterBarProps): JSX.Element {
  // Редакторы фильтра и сортировки по умолчанию свёрнуты (раскрываются
  // поповером по клику на компактный триггер в тулбаре). Раньше редактор
  // фильтра «прибивался» открытым, если условия приходили из view config —
  // это создавало конструкторный шум и давило на таблицу. Теперь активные
  // условия видны как «· N» на кнопке, а полный редактор — только по запросу.
  const [showFilterEditor, setShowFilterEditor] = useState(false);
  const [showSortAdd, setShowSortAdd] = useState(false);

  const updateFilter = (idx: number, patch: Partial<FilterLeaf>) => {
    onFiltersChange(filters.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };
  const removeFilter = (idx: number) => {
    onFiltersChange(filters.filter((_, i) => i !== idx));
  };
  const addEmptyFilter = () => {
    const firstField = fields[0];
    if (!firstField) return;
    const operators = firstField.slug.startsWith('_lr:')
      ? ['is_empty', 'is_not_empty']
      : getOperatorsForFieldType(firstField.fieldType);
    onFiltersChange([
      ...filters,
      {
        fieldSlug: firstField.slug,
        operator: operators[0] ?? 'eq',
        value: undefined,
        ...(filters.length > 0 ? { combinator: 'and' as const } : {}),
      },
    ]);
    setShowFilterEditor(true);
  };
  const clearAllFilters = () => onFiltersChange([]);

  const addSort = (fieldSlug: string, direction: 'asc' | 'desc') => {
    onSortsChange([...sorts, { fieldSlug, direction }]);
    setShowSortAdd(false);
  };
  const removeSort = (idx: number) => {
    onSortsChange(sorts.filter((_, i) => i !== idx));
  };

  const hasFilters = filters.length > 0;
  const hasViewFilters = viewFilters.length > 0;
  // Иконка «Фильтр» подсвечена, если активны пользовательские ИЛИ фиксированные
  // (заданные представлением) условия — грид в любом случае отфильтрован (P1-4).
  const filterActive = hasFilters || hasViewFilters;
  const hasSorts = sorts.length > 0;

  // Сводка-метка для компактного триггера фильтра: «Фильтр · 2 (+1)».
  const filterCountLabel = hasFilters ? `${filters.length}` : undefined;

  // Содержимое поповера фильтра: фиксированные условия представления (read-only)
  // + полноценный редактор пользовательских условий. Раскрывается по клику,
  // в свёрнутом состоянии на таблицу не давит (спокойнее, меньше шума).
  const filterPopoverContent = (
    <div
      style={{ minWidth: 340, maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 8 }}
      data-testid="filter-editor"
      onKeyDown={(e) => {
        // Esc закрывает поповер фильтра, если фокус не в открытом dropdown'е
        // (там Esc сначала закрывает сам список AntD-селекта).
        if (e.key === 'Escape' && !document.querySelector('.ant-select-open')) {
          setShowFilterEditor(false);
        }
      }}
    >
      {/* Фиксированные условия представления — read-only chips, без удаления (P1-4). */}
      {hasViewFilters && (
        <div
          style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}
          data-testid="view-filters"
        >
          <span className="lens-sort-label">
            <FilterOutlined style={{ fontSize: 12 }} />
            Задано представлением
          </span>
          {viewFilters.map((f, i) => (
            <Tooltip key={`vf-${i}`} title="Условие задано представлением и не редактируется здесь">
              <Tag color="default" style={{ cursor: 'default' }}>
                {describeFilter(f, fields, resolvedRefs)}
              </Tag>
            </Tooltip>
          ))}
        </div>
      )}
      <div className="lens-filter-editor">
        <FilterEditor
          fields={fields}
          filters={filters}
          onUpdate={updateFilter}
          onRemove={removeFilter}
          onAdd={addEmptyFilter}
          onClear={clearAllFilters}
          ensureReferenceOptions={ensureReferenceOptions}
          resolvedRefs={resolvedRefs}
        />
      </div>
    </div>
  );

  // Содержимое поповера сортировки: текущие сортировки компактными чипами
  // + добавление нового уровня. Раньше чипы висели отдельной строкой всегда.
  const sortPopoverContent = (
    <div
      style={{ minWidth: 280, display: 'flex', flexDirection: 'column', gap: 8 }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !document.querySelector('.ant-select-open')) {
          setShowSortAdd(false);
        }
      }}
    >
      {hasSorts && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {sorts.map((s, i) => (
            <Tag
              key={`s-${i}`}
              closable
              onClose={() => removeSort(i)}
              className="lens-sort-chip"
            >
              {sortNames?.[s.fieldSlug] ?? fields.find((fd) => fd.slug === s.fieldSlug)?.name ?? s.fieldSlug}{' '}
              {s.direction === 'asc' ? '↑' : '↓'}
            </Tag>
          ))}
        </div>
      )}
      <SortAddPopover fields={fields} onAdd={addSort} onCancel={() => setShowSortAdd(false)} />
    </div>
  );

  return (
    // Нижний отступ задаёт родительская строка тулбара: свой marginBottom здесь
    // складывался с её отступом, и зазор под кнопками получался вдвое больше
    // остальных полос над таблицей.
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Row 1: search + compact control triggers (collapsed by default) */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Поиск..."
          size="small"
          style={{ width: 220 }}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          allowClear
        />
        <Popover
          content={filterPopoverContent}
          title="Фильтр"
          trigger="click"
          open={showFilterEditor}
          onOpenChange={(o) => {
            setShowFilterEditor(o);
            if (o) setShowSortAdd(false); // взаимоисключение поповеров тулбара
          }}
          placement="bottomLeft"
        >
          <Tooltip
            title={`Фильтр${
              hasFilters ? ` · ${filters.length}` : ''
            }${hasViewFilters ? ` · ${viewFilters.length} от представления` : ''}`}
          >
            <Button
              size="small"
              icon={<FilterOutlined />}
              type={filterActive ? 'primary' : 'default'}
              data-testid="filter-toggle"
              aria-label="Фильтр"
            >
              {filterCountLabel}
            </Button>
          </Tooltip>
        </Popover>
        <Popover
          content={sortPopoverContent}
          title="Сортировка"
          trigger="click"
          open={showSortAdd}
          onOpenChange={(o) => {
            setShowSortAdd(o);
            if (o) setShowFilterEditor(false); // взаимоисключение поповеров тулбара
          }}
          placement="bottomLeft"
        >
          <Tooltip title={`Сортировка${hasSorts ? ` · ${sorts.length}` : ''}`}>
            <Button
              size="small"
              icon={<SortAscendingOutlined />}
              // Единый индикатор активности: подсвечиваем, когда сортировка задана
              // (как у «Фильтр»/«Группировка»).
              type={hasSorts ? 'primary' : 'default'}
              aria-label="Сортировка"
            >
              {hasSorts ? `${sorts.length}` : undefined}
            </Button>
          </Tooltip>
        </Popover>
        {extraButtons}
      </div>
    </div>
  );
}

// ─── Inline filter editor (поддерживает AND/OR + массовую правку) ──────────

interface FilterEditorProps {
  fields: FieldDefinition[];
  filters: FilterLeaf[];
  onUpdate: (idx: number, patch: Partial<FilterLeaf>) => void;
  onRemove: (idx: number) => void;
  onAdd: () => void;
  onClear: () => void;
  onClose?: () => void;
  ensureReferenceOptions?: (field: FieldDefinition) => Promise<Array<{ value: string; label: string }>>;
  resolvedRefs?: Record<string, string>;
}

function FilterEditor({
  fields,
  filters,
  onUpdate,
  onRemove,
  onAdd,
  onClear,
  onClose,
  ensureReferenceOptions,
}: FilterEditorProps): JSX.Element {
  const [refOptionsByField, setRefOptionsByField] = useState<Record<string, Array<{ value: string; label: string }>>>({});

  const ensureRefForField = useCallback(
    async (field: FieldDefinition) => {
      if (refOptionsByField[field.slug]) return;
      if (!ensureReferenceOptions) return;
      const opts = await ensureReferenceOptions(field).catch(() => [] as Array<{ value: string; label: string }>);
      setRefOptionsByField((prev) => ({ ...prev, [field.slug]: opts }));
    },
    [ensureReferenceOptions, refOptionsByField],
  );

  // Прогружаем опции для уже выбранных reference-полей
  useEffect(() => {
    for (const f of filters) {
      const fd = fields.find((x) => x.slug === f.fieldSlug);
      if (fd?.fieldType === 'reference' || fd?.fieldType === 'user') void ensureRefForField(fd);
    }
  }, [filters, fields, ensureRefForField]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {(filters.length > 0 || onClose) && (
        <div className="lens-filter-editor__head">
          <span className="lens-filter-editor__title">
            <FilterOutlined style={{ fontSize: 12 }} />
            Фильтр
            {filters.length > 0 && (
              <span className="lens-filter-editor__count">{filters.length}</span>
            )}
          </span>
          <div className="lens-filter-editor__head-actions">
            {filters.length > 0 && (
              <Button
                size="small"
                type="text"
                danger
                onClick={onClear}
                className="lens-filter-editor__clear"
              >
                Очистить всё
              </Button>
            )}
            {onClose && (
              <Tooltip title={filters.length > 0 ? 'Свернуть редактор (фильтры останутся применёнными)' : 'Скрыть редактор'}>
                <Button
                  size="small"
                  onClick={onClose}
                  icon={<CloseOutlined />}
                  className="lens-filter-editor__close"
                >
                  Скрыть
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      )}

      {filters.map((f, i) => {
        const fd = fields.find((x) => x.slug === f.fieldSlug) ?? null;
        const operators = f.fieldSlug.startsWith('_lr:')
          ? ['is_empty', 'is_not_empty']
          : fd ? getOperatorsForFieldType(fd.fieldType) : ['eq', 'neq'];
        const noValue = ['is_empty', 'is_not_empty'].includes(f.operator);
        return (
          <div key={`fr-${i}`} className="lens-filter-editor__row">
            {/* Combinator (для всех кроме первого) */}
            {i === 0 ? (
              <span className="lens-filter-editor__lead">где</span>
            ) : (
              <Select
                size="small"
                style={{ width: 72, flexShrink: 0 }}
                className="lens-filter-editor__combinator"
                value={f.combinator === 'or' ? 'or' : 'and'}
                onChange={(v) => onUpdate(i, { combinator: v as 'and' | 'or' })}
                options={[
                  { value: 'and', label: 'И' },
                  { value: 'or', label: 'ИЛИ' },
                ]}
                data-testid={`filter-combinator-${i}`}
              />
            )}
            <Select
              size="small"
              style={{ flex: '0 1 220px', minWidth: 160 }}
              showSearch
              filterOption={(input, opt) => (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())}
              value={f.fieldSlug}
              onChange={(slug) => {
                const newField = fields.find((x) => x.slug === slug);
                const ops = slug.startsWith('_lr:')
                  ? ['is_empty', 'is_not_empty']
                  : newField ? getOperatorsForFieldType(newField.fieldType) : ['eq'];
                onUpdate(i, { fieldSlug: slug, operator: ops[0] ?? 'eq', value: undefined });
                if (newField?.fieldType === 'reference' || newField?.fieldType === 'user') void ensureRefForField(newField);
              }}
              options={fields.map((x) => ({ value: x.slug, label: x.name }))}
              placeholder="Поле"
            />
            <Select
              size="small"
              style={{ flex: '0 1 140px', minWidth: 120 }}
              value={f.operator}
              onChange={(op) => onUpdate(i, { operator: op, value: ['is_empty', 'is_not_empty'].includes(op) ? undefined : f.value })}
              options={operators.map((op) => ({ value: op, label: OPERATOR_LABELS[op] ?? op }))}
            />
            {!noValue && (
              <FilterValueInput
                field={fd}
                operator={f.operator}
                value={f.value}
                onChange={(v) => onUpdate(i, { value: v })}
                refOptions={fd ? refOptionsByField[fd.slug] ?? [] : []}
              />
            )}
            <Button
              size="small"
              type="text"
              icon={<DeleteOutlined />}
              onClick={() => onRemove(i)}
              aria-label="Удалить условие"
              className="lens-filter-editor__remove"
            />
          </div>
        );
      })}

      <div>
        <Button
          size="small"
          type="dashed"
          icon={<PlusOutlined />}
          onClick={onAdd}
          className="lens-filter-editor__add"
        >
          Добавить условие
        </Button>
      </div>
    </div>
  );
}


// ─── Grouping popover with multi-level DnD ──────────────────────────────────

import type { GroupingLevel } from '../../shared/hooks/useTableSettings.ts';

interface GroupingPopoverProps {
  levels: GroupingLevel[];
  fields: FieldDefinition[];
  onAdd: (fieldSlug: string) => void;
  onRemove: (fieldSlug: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onChangeOrder: (fieldSlug: string, order: 'name' | 'count') => void;
  onClearAll: () => void;
}

function SortableGroupLevel({
  level,
  fieldName,
  onRemove,
  onChangeOrder,
}: {
  level: GroupingLevel;
  fieldName: string;
  onRemove: () => void;
  onChangeOrder: (order: 'name' | 'count') => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: level.fieldSlug });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="lens-group-row">
      <span {...attributes} {...listeners} className="lens-group-row__handle">
        <HolderOutlined />
      </span>
      <span className="lens-group-row__name">{fieldName}</span>
      <Select
        size="small"
        variant="borderless"
        style={{ width: 100 }}
        value={level.order}
        onChange={onChangeOrder}
        options={[
          { value: 'name', label: 'A→Я' },
          { value: 'count', label: '# кол-во' },
        ]}
      />
      <Button
        size="small"
        type="text"
        icon={<CloseOutlined style={{ fontSize: 10 }} />}
        onClick={onRemove}
        className="lens-group-row__remove"
      />
    </div>
  );
}

function GroupingPopover({ levels, fields, onAdd, onRemove, onReorder, onChangeOrder, onClearAll }: GroupingPopoverProps) {
  const groupableFields = fields.filter(
    (f) =>
      ['select', 'multi_select', 'boolean', 'reference', 'user'].includes(f.fieldType)
      || f.slug.startsWith('_refattr:')
      || f.slug === '_created_by',
  );
  const availableFields = groupableFields.filter((f) => !levels.some((l) => l.fieldSlug === f.slug));

  const groupDndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = levels.findIndex((l) => l.fieldSlug === active.id);
    const newIdx = levels.findIndex((l) => l.fieldSlug === over.id);
    if (oldIdx !== -1 && newIdx !== -1) onReorder(oldIdx, newIdx);
  };

  const fieldNameMap = new Map(fields.map((f) => [f.slug, f.name]));

  const content = (
    <div className="lens-group-popover">
      {levels.length > 0 && (
        <DndContext sensors={groupDndSensors} collisionDetection={closestCenter} onDragEnd={handleGroupDragEnd}>
          <SortableContext items={levels.map((l) => l.fieldSlug)} strategy={verticalListSortingStrategy}>
            {levels.map((level) => (
              <SortableGroupLevel
                key={level.fieldSlug}
                level={level}
                fieldName={fieldNameMap.get(level.fieldSlug) ?? level.fieldSlug}
                onRemove={() => onRemove(level.fieldSlug)}
                onChangeOrder={(order) => onChangeOrder(level.fieldSlug, order)}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
      {availableFields.length > 0 && (
        <Select
          size="small"
          className="lens-group-popover__add-select"
          style={{ width: '100%' }}
          placeholder={levels.length === 0 ? 'Группировать по…' : '+ Добавить уровень'}
          value={undefined}
          onChange={(slug) => { if (slug) onAdd(slug); }}
          options={availableFields.map((f) => ({ value: f.slug, label: f.name }))}
        />
      )}
      {levels.length > 0 && (
        <div className="lens-group-popover__footer">
          <Button
            size="small"
            type="text"
            danger
            onClick={onClearAll}
            className="lens-group-popover__clear"
          >
            Убрать группировку
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Popover content={content} title="Группировка" trigger="click" placement="bottomRight">
      <Tooltip title="Группировка">
        <Button
          icon={<GroupOutlined />}
          size="small"
          type={levels.length > 0 ? 'primary' : 'default'}
          data-testid="grouping-button"
          aria-label="Группировка"
        >
          {levels.length > 0 ? `${levels.length}` : undefined}
        </Button>
      </Tooltip>
    </Popover>
  );
}

// ─── Droppable group header + Draggable row for inter-group DnD ─────────────

// Разделитель значений внутри ключа группы для многозначных (reference /
// multi_select) полей. Запись с набором значений [A, B] образует ОДНУ группу
// «A, B», а не дублируется по группам A и B. Символ \u0001  не встречается в
// id справочников и option-value, поэтому безопасен и для парсинга метки, и
// для compositeKey (там разделители — «|» и «=»).
const GROUP_MULTI_SEP = '\u0001';

interface DroppableGroupSectionProps {
  groupKey: string;
  fieldSlug: string;
  rawValue: string;
  header: React.ReactNode;
  children?: React.ReactNode;
}

function DroppableGroupSection({ groupKey, fieldSlug, rawValue, header, children }: DroppableGroupSectionProps) {
  const { mode } = useThemeMode();
  const { setNodeRef, isOver } = useDroppable({
    id: `group:${groupKey}`,
    data: { type: 'group', fieldSlug, rawValue },
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transition: 'box-shadow 0.2s, border-color 0.2s, background 0.2s',
        boxShadow: isOver ? '0 0 0 2px #1677ff inset' : 'none',
        borderRadius: 6,
        background: isOver ? (mode === 'dark' ? 'rgba(22,119,255,0.16)' : '#e6f4ff') : 'transparent',
        padding: 4,
        minHeight: 44,
      }}
    >
      {header}
      {children}
    </div>
  );
}

/** Context to pass drag listeners from row to the drag-handle cell */
const DragHandleContext = React.createContext<{
  attributes: any;
  listeners: any;
}>({ attributes: {}, listeners: undefined });

function DraggableTableRow(props: React.HTMLAttributes<HTMLTableRowElement> & { 'data-row-key'?: string }) {
  const rowKey = props['data-row-key'];

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: rowKey ? `item:${rowKey}` : '__none__',
    data: { type: 'item', itemId: rowKey },
    disabled: !rowKey,
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.4 : 1,
  };

  const handleCtx = useMemo(() => ({ attributes, listeners }), [attributes, listeners]);

  return (
    <DragHandleContext.Provider value={handleCtx}>
      <tr {...props} ref={setNodeRef} style={style} />
    </DragHandleContext.Provider>
  );
}

/** Drag handle cell — renders the grip icon. Put as first column in grouped tables. */
function DragHandleCell() {
  const { attributes, listeners } = useContext(DragHandleContext);
  return (
    <span
      {...attributes}
      {...(listeners as any)}
      onClick={(e) => e.stopPropagation()}
      style={{
        cursor: 'grab',
        color: '#bbb',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px 4px',
        touchAction: 'none',
        width: '100%',
      }}
    >
      <HolderOutlined style={{ fontSize: 14 }} />
    </span>
  );
}


function SortAddPopover({
  fields,
  onAdd,
  onCancel,
}: {
  fields: FieldDefinition[];
  onAdd: (slug: string, dir: 'asc' | 'desc') => void;
  onCancel: () => void;
}): JSX.Element {
  const [fieldSlug, setFieldSlug] = useState<string | null>(null);
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc');

  return (
    <div className="lens-sort-add">
      <span className="lens-sort-add__hint">
        <SortAscendingOutlined style={{ fontSize: 12 }} />
        Сортировать
      </span>
      <Select
        size="small"
        style={{ width: 160 }}
        placeholder="Поле"
        value={fieldSlug}
        onChange={setFieldSlug}
        options={fields.map((f) => ({ value: f.slug, label: f.name }))}
      />
      <Select
        size="small"
        style={{ width: 120 }}
        value={direction}
        onChange={setDirection}
        options={[
          { value: 'asc', label: 'По возр.' },
          { value: 'desc', label: 'По убыв.' },
        ]}
      />
      <Button
        size="small"
        type="primary"
        disabled={!fieldSlug}
        onClick={() => fieldSlug && onAdd(fieldSlug, direction)}
        style={{ borderRadius: 999 }}
      >
        Добавить
      </Button>
      <Button size="small" onClick={onCancel} style={{ borderRadius: 999 }}>
        Отмена
      </Button>
    </div>
  );
}

// ─── Items table with views ──────────────────────────────────────────────────

interface ItemsTableProps {
  spaceId: string;
  view: View;
  allItemTypes: ItemType[];
  views: View[];
  setViews: React.Dispatch<React.SetStateAction<View[]>>;
  activeViewId: string | null;
  setActiveViewId: (id: string | null) => void;
  showCreateView: boolean;
  setShowCreateView: (show: boolean) => void;
  space?: SpaceDto | null;
}

function ItemsTable({ spaceId, view, allItemTypes, views, setViews, activeViewId, setActiveViewId, showCreateView, setShowCreateView, space }: ItemsTableProps): JSX.Element {
  const { user } = useAuth();
  const { mode: themeMode } = useThemeMode();
  const isDark = themeMode === 'dark';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [response, setResponse] = useState<ItemListResponse | null>(null);
  // У каких записей есть доска — значок рядом с названием.
  const whiteboardBoards = useWhiteboardSummary(spaceId);
  const [resolvedRefs, setResolvedRefs] = useState<Record<string, string>>({});
  const [dictionaryOptionsById, setDictionaryOptionsById] = useState<Record<string, Array<{ value: string; label: string }>>>({});
  const [userOptions, setUserOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [userGroups, setUserGroups] = useState<SpaceUserGroup[]>([]);
  // Аватарки участников (userId → avatarUrl) для карточек канбана и т.п.
  // Заполняется из /members; при отсутствии URL — фолбэк на инициалы.
  const [userAvatarById, setUserAvatarById] = useState<Record<string, string>>({});
  // Полные значения записей справочника (для reference_attribute-колонок).
  // Ключ — entryId, значение — values entry'а (column slug → значение).
  const [dictionaryEntryValues, setDictionaryEntryValues] = useState<Record<string, Record<string, unknown>>>({});
  // Колонки справочника по dictionaryId — нужны для отрисовки select-значений
  // в reference_attribute-колонках.
  const [dictionaryColumnsById, setDictionaryColumnsById] = useState<Record<string, Array<{ slug: string; columnType: string; config: unknown }>>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Непрочитанные уведомления по записям: подсветка строки/карточки и снятие
  // подсветки при открытии записи.
  const { unreadByItem, markItemRead } = useNotifications();
  const rowClassName = useCallback(
    (record: ItemRow, index: number): string => {
      const striped = index % 2 === 1 ? 'lens-table-row-striped' : '';
      const unread = unreadByItem[record.id] ? 'lens-table-row-unread' : '';
      return [striped, unread].filter(Boolean).join(' ');
    },
    [unreadByItem],
  );

  const [drawerItem, setDrawerItem] = useState<ItemRow | null>(null);
  // Последняя открытая в карточке запись. Нужна для «поздних» сохранений:
  // поля карточки коммитятся по blur, а blur иногда приходит уже ПОСЛЕ того,
  // как drawerItem обнулился (закрытие по Escape/крестику). Без этого ref
  // такой PATCH молча терялся вместе с введённым текстом.
  const lastDrawerItemRef = useRef<ItemRow | null>(null);
  if (drawerItem) lastDrawerItemRef.current = drawerItem;
  const { openSession: openHermesSession } = useHermesSessions();

  // Интерактивная оценка финализирована в доке → перечитываем запись целиком,
  // чтобы в гриде появились ЗАПИСАННЫЕ значения полей (а не только статус-чип).
  useEffect(() => {
    function onFinalized(e: Event) {
      const detail = (e as CustomEvent<HermesFinalizedDetail>).detail;
      if (!detail || detail.spaceId !== spaceId) return;
      // Оптимистично патчим статус сразу.
      setResponse((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((it) =>
                it.id === detail.itemId ? ({ ...it, hermesStatus: detail.status } as ItemRow) : it,
              ),
            }
          : prev,
      );
      // И подтягиваем свежие значения полей (writeback на бэке уже произошёл).
      void getItem(spaceId, detail.itemId)
        .then((fresh) => {
          const refs = (fresh as { resolvedRefs?: Record<string, string> }).resolvedRefs;
          if (refs) mergeResolvedRefs(refs);
          setResponse((prev) =>
            prev
              ? { ...prev, items: prev.items.map((it) => (it.id === fresh.id ? fresh : it)) }
              : prev,
          );
          // Открытая карточка (drawer) — тоже перечитываем, иначе панель
          // «Свойства» покажет старые значения после записи сценария в поля.
          setDrawerItem((prev) => (prev && prev.id === fresh.id ? fresh : prev));
        })
        .catch(() => {
          /* транзиент — статус уже пропатчен, значения подтянутся при перезагрузке */
        });
    }
    window.addEventListener(HERMES_FINALIZED_EVENT, onFinalized);
    return () => window.removeEventListener(HERMES_FINALIZED_EVENT, onFinalized);
  }, [spaceId]);

  // Аватарки участников грузим сразу (не лениво, как userOptions): они нужны
  // на карточках канбана с первого рендера, до любого редактирования user-поля.
  useEffect(() => {
    let cancelled = false;
    apiRequest<{ members: SpaceMember[] }>(`/api/spaces/${spaceId}/members?limit=200`)
      .then((res) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const m of res.members) if (m.avatarUrl) map[m.userId] = m.avatarUrl;
        setUserAvatarById((prev) => ({ ...prev, ...map }));
      })
      .catch(() => { /* аватарки — украшение; фолбэк на инициалы */ });
    return () => { cancelled = true; };
  }, [spaceId]);

  // Группы пользователей пространства — раздел в списке поля «участник».
  // Грузим сразу: список нужен и в гриде, и в форме создания записи, а он
  // маленький (десятки строк).
  useEffect(() => {
    let cancelled = false;
    listSpaceUserGroups(spaceId)
      .then((res) => { if (!cancelled) setUserGroups(res.groups); })
      .catch(() => { /* без групп поле работает как раньше */ });
    return () => { cancelled = true; };
  }, [spaceId]);

  // Per-view state, персистится в localStorage по space — поэтому уход на другую
  // страницу и возврат не сбрасывают пользовательские настройки таблицы.
  const viewStateKey = `lens:viewstate:${spaceId}`;
  const [filtersByView, setFiltersByView] = useState<Record<string, FilterLeaf[]>>(() => {
    try {
      const raw = localStorage.getItem(`${viewStateKey}:filters`);
      return raw ? (JSON.parse(raw) as Record<string, FilterLeaf[]>) : {};
    } catch { return {}; }
  });
  const [sortsByView, setSortsByView] = useState<Record<string, SortEntry[]>>(() => {
    try {
      const raw = localStorage.getItem(`${viewStateKey}:sorts`);
      return raw ? (JSON.parse(raw) as Record<string, SortEntry[]>) : {};
    } catch { return {}; }
  });
  const [searchByView, setSearchByView] = useState<Record<string, string>>(() => {
    try {
      const raw = localStorage.getItem(`${viewStateKey}:search`);
      return raw ? (JSON.parse(raw) as Record<string, string>) : {};
    } catch { return {}; }
  });
  // P1-1: мета-колонки «Создано/Обновлено/Кто создал» по умолчанию СКРЫТЫ.
  // Здесь храним, какие из них пользователь явно включил в каждом view.
  // (Колонки, явно заданные в сохранённом view.config.columns, видимы независимо
  // от этого набора — см. metaShownSlugs ниже.)
  const [metaShownByView, setMetaShownByView] = useState<Record<string, string[]>>(() => {
    try {
      const raw = localStorage.getItem(`${viewStateKey}:metacols`);
      return raw ? (JSON.parse(raw) as Record<string, string[]>) : {};
    } catch { return {}; }
  });
  // Какие быстрые фильтры сейчас включены — личное per-view состояние (сами
  // определения быстрых фильтров лежат в общем view.config.quickFilters).
  const [activeQuickFiltersByView, setActiveQuickFiltersByView] = useState<Record<string, string[]>>(() => {
    try {
      const raw = localStorage.getItem(`${viewStateKey}:quickfilters`);
      return raw ? (JSON.parse(raw) as Record<string, string[]>) : {};
    } catch { return {}; }
  });
  // serverKey без префикса `lens:` (viewStateKey уже его содержит) — store
  // сам добавит префикс при записи в localStorage-кэш и сделает debounced PUT.
  const viewStateServerKey = viewStateKey.slice('lens:'.length);
  useEffect(() => {
    writePref(`${viewStateServerKey}:filters`, filtersByView);
  }, [viewStateServerKey, filtersByView]);
  useEffect(() => {
    writePref(`${viewStateServerKey}:sorts`, sortsByView);
  }, [viewStateServerKey, sortsByView]);
  useEffect(() => {
    writePref(`${viewStateServerKey}:search`, searchByView);
  }, [viewStateServerKey, searchByView]);
  useEffect(() => {
    writePref(`${viewStateServerKey}:metacols`, metaShownByView);
  }, [viewStateServerKey, metaShownByView]);
  useEffect(() => {
    writePref(`${viewStateServerKey}:quickfilters`, activeQuickFiltersByView);
  }, [viewStateServerKey, activeQuickFiltersByView]);
  const userFilters = useMemo(() => filtersByView[view.id] ?? [], [filtersByView, view.id]);
  const userSorts = useMemo(() => sortsByView[view.id] ?? [], [sortsByView, view.id]);
  const search = searchByView[view.id] ?? '';
  const setUserFilters = useCallback(
    (next: FilterLeaf[] | ((prev: FilterLeaf[]) => FilterLeaf[])) => {
      setFiltersByView((prev) => {
        const cur = prev[view.id] ?? [];
        const value = typeof next === 'function' ? (next as (p: FilterLeaf[]) => FilterLeaf[])(cur) : next;
        return { ...prev, [view.id]: value };
      });
    },
    [view.id],
  );
  const setUserSorts = useCallback(
    (next: SortEntry[] | ((prev: SortEntry[]) => SortEntry[])) => {
      setSortsByView((prev) => {
        const cur = prev[view.id] ?? [];
        const value = typeof next === 'function' ? (next as (p: SortEntry[]) => SortEntry[])(cur) : next;
        return { ...prev, [view.id]: value };
      });
    },
    [view.id],
  );
  const setSearch = useCallback(
    (next: string) => {
      setSearchByView((prev) => ({ ...prev, [view.id]: next }));
    },
    [view.id],
  );
  const activeQuickFilterIds = useMemo(
    () => activeQuickFiltersByView[view.id] ?? [],
    [activeQuickFiltersByView, view.id],
  );
  const toggleQuickFilter = useCallback(
    (id: string) => {
      setActiveQuickFiltersByView((prev) => {
        const cur = prev[view.id] ?? [];
        const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
        return { ...prev, [view.id]: next };
      });
    },
    [view.id],
  );
  const [showColSettings, setShowColSettings] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Динамическая высота тела таблицы: вместо магического calc(100vh - 360px)
  // меряем реальное расстояние от верха таблицы до низа окна, чтобы таблица
  // занимала всю доступную высоту (и снизу не оставалось пустого места), и
  // пересчитываем при ресайзе/раскрытии панели фильтров/смене вида.
  const tableAreaRef = useRef<HTMLDivElement | null>(null);
  const [tableScrollY, setTableScrollY] = useState(420);
  // Активная ячейка (строка/колонка в пределах страницы) — рамка как в Excel.
  // Двигается стрелками, Tab и Enter; клик по ячейке тоже её ставит.
  const [activeCell, setActiveCell] = useState<{ row: number; col: number } | null>(null);
  // Номер страницы держим в состоянии: без него не посчитать сквозную нумерацию
  // строк («1» на второй странице должно быть 51, а не снова 1).
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!isFullscreen) return;
    document.body.classList.add('lens-fullscreen-table');
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Не глушим Esc, если открыт modal или drawer — пусть сначала закрывается оверлей.
      if (document.querySelector('.ant-modal-mask:not(.ant-modal-mask-hidden), .ant-drawer-mask')) return;
      setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('lens-fullscreen-table');
      window.removeEventListener('keydown', onKey);
    };
  }, [isFullscreen]);
  const { openPanel: openAiPanel } = useAiPanel();
  // Multi-type view: type selector for creating items
  // Kanban: при «Добавить в колонку» в multi-type view создание идёт через модалку
  // с выбором типа; сюда кладётся предзаполнение поля-колонки.

  const isSingleType = (view.itemTypeIds ?? []).length === 1;
  const singleTypeId = isSingleType ? (view.itemTypeIds ?? [])[0]! : null;

  const [editingViewId, setEditingViewId] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [syncInboxOpen, setSyncInboxOpen] = useState(false);
  // todos #3: быстрая sync по выбранным строкам — когда массив непустой, та же
  // SyncInboxModal открывается с restrictToItemIds.
  const [syncInboxRestrictIds, setSyncInboxRestrictIds] = useState<string[] | null>(null);
  // Групповая оценка Hermes для выбранных записей.
  const [estimateInboxOpen, setEstimateInboxOpen] = useState(false);
  const [estimateInboxItemIds, setEstimateInboxItemIds] = useState<string[]>([]);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [weightDrawerOpen, setWeightDrawerOpen] = useState(false);
  // Объединённое массовое редактирование (todos #4): одна модалка со списком
  // операций. Каждая операция — поле + режим: «set» (задать значение всем) или
  // «replace» (заменить подстроку, только для текстовых полей). Несколько замен
  // применяются последовательно на бэке. set уходит в `fields`, replace — в
  // массив `replacements`.
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditOps, setBulkEditOps] = useState<BulkEditOp[]>([]);
  const [linkRulesByType, setLinkRulesByType] = useState<Record<string, LinkRule[]>>({});
  const [applyingLinkRuleId, setApplyingLinkRuleId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalTypeId, setCreateModalTypeId] = useState<string | null>(null);
  const [createModalFields, setCreateModalFields] = useState<Record<string, unknown>>({});
  const [createModalAutofilled, setCreateModalAutofilled] = useState<Record<string, unknown>>({});
  const [createModalExpandedField, setCreateModalExpandedField] = useState<{
    slug: string;
    name: string;
    value: string;
    mode: 'markdown' | 'textarea';
  } | null>(null);
  // Режим модалки: создание новой записи / дублирование одной / дублирование выбранных.
  // В режиме дублирования значения по умолчанию = значения исходной записи (single)
  // или пусто (bulk); поля, оставленные пустыми, на сервере подменяются значениями из
  // исходной записи; заполненные — становятся overrides и побеждают источник.
  const [createModalMode, setCreateModalMode] = useState<'create' | 'duplicate-single' | 'duplicate-bulk'>('create');
  const [createModalSourceItemId, setCreateModalSourceItemId] = useState<string | null>(null);
  const [createModalSourceItemIds, setCreateModalSourceItemIds] = useState<string[] | null>(null);
  // Slug-и полей, которые пользователь явно пометил «Очистить» в режиме дублирования.
  // На сабмите они уходят как `null` в overrides — бэк трактует это как «очистить значение».
  const [createModalClearedSlugs, setCreateModalClearedSlugs] = useState<Set<string>>(() => new Set());
  // todos #4: замена подстроки в текстовых полях при дублировании. Ключ — slug
  // поля, значение — что найти и на что заменить. Пустой `search` = замена не
  // применяется. Работает поверх копируемого значения каждой исходной записи.
  const [createModalReplacements, setCreateModalReplacements] = useState<
    Record<string, { search: string; replace: string }>
  >({});

  // Inter-group drag state
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);

  const mergeResolvedRefs = useCallback((refs: Record<string, string>) => {
    if (Object.keys(refs).length === 0) return;
    setResolvedRefs((prev) => ({ ...prev, ...refs }));
  }, []);

  // Merge fields from all item types referenced by the view (dedup by slug, first wins)
  const mergedFields = useMemo(() => {
    const seen = new Set<string>();
    const result: FieldDefinition[] = [];
    for (const typeId of (view.itemTypeIds ?? [])) {
      const it = allItemTypes.find((t) => t.id === typeId);
      if (!it) continue;
      for (const f of it.fields) {
        if (!seen.has(f.slug)) {
          seen.add(f.slug);
          result.push(f);
        }
      }
    }
    return result;
  }, [view.itemTypeIds, allItemTypes]);

  // Заголовок записи для сессии Hermes — поле с признаком «название» (как в
  // карточке/канбане), а не ключ. Берём значение title-поля у строки.
  const hermesTitleField = useMemo(() => resolveTitleField(mergedFields), [mergedFields]);
  const hermesRowTitle = useCallback(
    (rec: ItemRow | undefined, key?: string): string => {
      if (!hermesTitleField) return key || 'Запись';
      const raw = (rec as { fields?: Record<string, unknown> } | undefined)?.fields?.[
        hermesTitleField.slug
      ];
      // reference-заголовок: резолвим entryId в метку справочника, а не UUID.
      if (hermesTitleField.fieldType === 'reference') {
        const first = Array.isArray(raw)
          ? (raw as unknown[]).find((v): v is string => typeof v === 'string')
          : typeof raw === 'string' ? raw : undefined;
        const label = first ? (resolvedRefs[first] ?? first) : undefined;
        return label && label.trim() ? label.trim() : key || 'Запись';
      }
      return typeof raw === 'string' && raw.trim() ? raw.trim() : key || 'Запись';
    },
    [hermesTitleField, resolvedRefs],
  );

  const ensureLinkRulesFor = useCallback(async (typeId: string): Promise<LinkRule[]> => {
    const cached = linkRulesByType[typeId];
    if (cached) return cached;
    try {
      const rules = await listLinkRules(spaceId, typeId);
      setLinkRulesByType((prev) => ({ ...prev, [typeId]: rules }));
      return rules;
    } catch {
      setLinkRulesByType((prev) => ({ ...prev, [typeId]: [] }));
      return [];
    }
  }, [linkRulesByType, spaceId]);

  // Prefetch правил по типам view — нужно всем ролям: для пункта «Связать»
  // (admin) и для синтетических полей-фильтров (любая роль).
  useEffect(() => {
    if (!space?.myRole) return;
    for (const typeId of view.itemTypeIds ?? []) {
      if (!linkRulesByType[typeId]) void ensureLinkRulesFor(typeId);
    }
  }, [space?.myRole, view.itemTypeIds, linkRulesByType, ensureLinkRulesFor]);

  // M11.6: link_aggregate columns — конфиги читаем из view.config; значения
  // приходят в основном /items/query response в поле `linkedAggregates` и
  // отдельным запросом не дёргаются (см. handleLoad ниже).
  const [linkedAggregatesMap, setLinkedAggregatesMap] = useState<
    Record<string, Record<string, LinkedAggregateValue>>
  >({});
  const [linkAggregateModalOpen, setLinkAggregateModalOpen] = useState(false);
  const [editingLinkAggregateColumn, setEditingLinkAggregateColumn] = useState<LinkAggregateColumnConfig | null>(null);
  const [viewComputedMap, setViewComputedMap] = useState<
    Record<string, Record<string, number | string | null>>
  >({});
  const [viewComputedModalOpen, setViewComputedModalOpen] = useState(false);
  const linkAggregateColumns = useMemo<LinkAggregateColumnConfig[]>(() => {
    const cols = (view.config as { columns?: unknown[] } | null)?.columns;
    if (!Array.isArray(cols)) return [];
    return cols.filter(
      (c): c is LinkAggregateColumnConfig =>
        !!c && typeof c === 'object' && (c as { kind?: unknown }).kind === 'link_aggregate',
    );
  }, [view.config]);
  const linkAggregateKeySet = useMemo(
    () => new Set(linkAggregateColumns.map((col) => col.key)),
    [linkAggregateColumns],
  );
  const serverSorts = useMemo(
    () => userSorts.filter(
      (sort) =>
        !linkAggregateKeySet.has(sort.fieldSlug)
        && !sort.fieldSlug.startsWith('_refattr:'),
    ),
    [userSorts, linkAggregateKeySet],
  );
  // Порядок карточек канбана задан сортировкой — сохранённой в представлении
  // или личной пользователя. Тогда перетаскивание внутри колонки отключено.
  const kanbanOrderLocked = useMemo(() => {
    if (view.viewType !== 'kanban') return false;
    const viewSorts = (view.config as { sorts?: SortEntry[] } | null)?.sorts ?? [];
    return viewSorts.length > 0 || serverSorts.length > 0;
  }, [view.viewType, view.config, serverSorts]);
  const viewComputedColumns = useMemo<ViewComputedColumnConfig[]>(
    () => extractViewComputedColumns(view.config),
    [view.config],
  );
  const referenceAttributeColumns = useMemo<ReferenceAttributeColumnConfig[]>(
    () => extractReferenceAttributeColumns(view.config),
    [view.config],
  );
  const referenceAttributeKeySet = useMemo(
    () => new Set(referenceAttributeColumns.map((col) => referenceAttributeColumnSlug(col.key))),
    [referenceAttributeColumns],
  );
  // Быстрые фильтры (пресеты-чипы) — общие определения из view.config, активные
  // выбираются пользователем (activeQuickFilterIds, per-view).
  const quickFilters = useMemo(() => extractQuickFilters(view.config), [view.config]);
  const activeQuickFilters = useMemo(
    () => quickFilters.filter((q) => activeQuickFilterIds.includes(q.id)),
    [quickFilters, activeQuickFilterIds],
  );
  // Все наборы фильтров, комбинируемые по AND: ручной фильтр + каждый включённый
  // быстрый фильтр (запись должна пройти каждый набор).
  const filterGroups = useMemo<FilterLeaf[][]>(
    () => [userFilters, ...activeQuickFilters.map((q) => q.filters)],
    [userFilters, activeQuickFilters],
  );

  // Фильтры по link_aggregate и reference_attribute колонкам применяются клиентски.
  // Разделяем каждый набор на server/client-часть и комбинируем по AND внутри
  // категории (combineFilterGroupsAnd раскрывает OR в DNF, чтобы плоская модель
  // фильтров выразила AND нескольких наборов).
  const serverFilters = useMemo(
    () => combineFilterGroupsAnd(
      filterGroups.map((g) => g.filter(
        (f) => !linkAggregateKeySet.has(f.fieldSlug) && !referenceAttributeKeySet.has(f.fieldSlug),
      )),
    ),
    [filterGroups, linkAggregateKeySet, referenceAttributeKeySet],
  );
  const linkAggregateFilters = useMemo(
    () => combineFilterGroupsAnd(
      filterGroups.map((g) => g.filter((f) => linkAggregateKeySet.has(f.fieldSlug))),
    ),
    [filterGroups, linkAggregateKeySet],
  );
  const referenceAttributeFilters = useMemo(
    () => combineFilterGroupsAnd(
      filterGroups.map((g) => g.filter((f) => referenceAttributeKeySet.has(f.fieldSlug))),
    ),
    [filterGroups, referenceAttributeKeySet],
  );
  const [referenceAttributeModalOpen, setReferenceAttributeModalOpen] = useState(false);
  const [editingReferenceAttributeColumn, setEditingReferenceAttributeColumn] =
    useState<ReferenceAttributeColumnConfig | null>(null);
  const [quickFiltersModalOpen, setQuickFiltersModalOpen] = useState(false);
  const persistQuickFilters = useCallback(
    async (next: QuickFilter[]) => {
      const currentConfig = (view.config as Record<string, unknown> | null) ?? {};
      const nextConfig = { ...currentConfig, quickFilters: next };
      const updated = await updateView(spaceId, view.id, { config: nextConfig });
      setViews((prev) => prev.map((v) => (v.id === view.id ? updated : v)));
      // Снимаем активность с удалённых быстрых фильтров.
      const keepIds = new Set(next.map((q) => q.id));
      setActiveQuickFiltersByView((prev) => {
        const cur = prev[view.id] ?? [];
        const filtered = cur.filter((id) => keepIds.has(id));
        return filtered.length === cur.length ? prev : { ...prev, [view.id]: filtered };
      });
    },
    [spaceId, view.id, view.config],
  );

  const ensureReferenceOptions = useCallback(async (field: FieldDefinition): Promise<Array<{ value: string; label: string }>> => {
    if (field.fieldType === 'user') {
      if (userOptions.length > 0) return userOptions;
      const res = await apiRequest<{ members: SpaceMember[] }>(`/api/spaces/${spaceId}/members?limit=200`);
      const loadedOptions = res.members.map((m) => ({ value: m.userId, label: m.displayName || m.login }));
      setUserOptions(loadedOptions);
      mergeResolvedRefs(Object.fromEntries(loadedOptions.map((option) => [option.value, option.label])));
      setUserAvatarById((prev) => {
        const next = { ...prev };
        for (const m of res.members) if (m.avatarUrl) next[m.userId] = m.avatarUrl;
        return next;
      });
      return loadedOptions;
    }

    const config = field.config as { dictionaryId?: string } | null;
    const dictionaryId = config?.dictionaryId;
    if (!dictionaryId) return [];

    const cached = dictionaryOptionsById[dictionaryId];
    if (cached && cached.length > 0 && dictionaryColumnsById[dictionaryId]) return cached;

    const [dict, result] = await Promise.all([
      getDictionary(dictionaryId),
      listDictEntries(dictionaryId, { limit: 200 }),
    ]);
    const loadedOptions = result.items.map((entry) => ({ value: entry.id, label: entryLabel(entry, dict) }));

    setDictionaryOptionsById((prev) => ({ ...prev, [dictionaryId]: loadedOptions }));
    // Запоминаем полные values entry'ев — нужно для reference_attribute-колонок.
    setDictionaryEntryValues((prev) => {
      const next = { ...prev };
      for (const entry of result.items) {
        next[entry.id] = (entry.values as Record<string, unknown>) ?? {};
      }
      return next;
    });
    setDictionaryColumnsById((prev) => ({
      ...prev,
      [dictionaryId]: (dict.columns ?? []).map((c) => ({
        slug: c.slug,
        columnType: c.columnType,
        config: c.config,
      })),
    }));
    mergeResolvedRefs(Object.fromEntries(loadedOptions.map((option) => [option.value, option.label])));
    return loadedOptions;
  }, [dictionaryColumnsById, dictionaryOptionsById, mergeResolvedRefs, spaceId, userOptions]);

  const referenceOptionsForField = useCallback((field: FieldDefinition): Array<{ value: string; label: string }> | undefined => {
    if (field.fieldType === 'user') return userOptions;
    const dictionaryId = (field.config as { dictionaryId?: string } | null)?.dictionaryId;
    return dictionaryId ? dictionaryOptionsById[dictionaryId] : undefined;
  }, [dictionaryOptionsById, userOptions]);

  // Table settings (column widths, order, visibility, pinning) — persisted in localStorage
  const columnSettingsOptions = useMemo<ColumnSettingsOption[]>(
    () => [
      ...mergedFields.map((field) => ({ slug: field.slug, name: field.name, field })),
      // Виртуальная колонка «Тип записи» — только для multi-type views.
      ...((view.itemTypeIds ?? []).length > 1
        ? [{ slug: '_item_type', name: 'Тип записи', field: undefined }]
        : []),
      // Псевдо-колонка «Ключ» (displayKey) — отдельная скрываемая колонка.
      { slug: '_display_key', name: 'Ключ', field: undefined },
      // Метаданные записи (created_at / updated_at / created_by).
      { slug: '_created_at', name: 'Создано', field: undefined },
      { slug: '_updated_at', name: 'Обновлено', field: undefined },
      { slug: '_created_by', name: 'Кто создал', field: undefined },
      // Колонка интеграции с Jira — доступна только если у пространства она включена.
      ...(space?.jiraIntegrationEnabled
        ? [{ slug: '_jira', name: 'Jira', field: undefined }]
        : []),
      // Колонка статуса последней оценки Hermes — всегда показывается, если у
      // пространства включена интеграция оценки.
      ...(space?.hermesIntegrationEnabled
        ? [{ slug: '_hermes', name: 'Оценка', field: undefined }]
        : []),
      // Кнопка запуска интерактивной оценки живёт отдельной узкой колонкой:
      // в одной ячейке со статусом она съезжала вслед за шириной тега, и
      // столбик кнопок выглядел рваным. Наблюдателю запускать нечего.
      ...(space?.hermesIntegrationEnabled && space?.myRole !== 'viewer'
        ? [{ slug: '_hermes_run', name: 'Кнопка оценки', field: undefined }]
        : []),
      ...linkAggregateColumns.map((col) => ({
        slug: col.key,
        name: col.label,
        field: undefined,
      })),
      ...viewComputedColumns.map((col) => ({
        slug: viewComputedColumnSlug(col.key),
        name: col.label,
        field: undefined,
      })),
      ...referenceAttributeColumns.map((col) => ({
        slug: referenceAttributeColumnSlug(col.key),
        name: col.label,
        field: undefined,
      })),
    ],
    [mergedFields, linkAggregateColumns, viewComputedColumns, referenceAttributeColumns, view.itemTypeIds, space?.jiraIntegrationEnabled, space?.hermesIntegrationEnabled, space?.myRole],
  );
  // View-level visible columns: если у представления сохранён непустой список колонок, он
  // ограничивает то, что вообще доступно таблице (localStorage-настройки скрытия — слой поверх).
  const viewVisibleSlugs = useMemo<string[] | null>(() => {
    const cols = (view.config as Record<string, unknown>)?.columns as
      | Array<{ fieldSlug?: string; key?: string; kind?: string }>
      | undefined;
    if (!cols || cols.length === 0) return null;
    // Для field-колонок ключ — slug, для synthetic view-only колонок — col.key.
    return cols
      .map((c) => (
        c.kind === 'link_aggregate'
          ? c.key
          : c.kind === 'view_computed'
            ? viewComputedColumnSlug(c.key ?? '')
          : c.kind === 'reference_attribute'
            ? referenceAttributeColumnSlug(c.key ?? '')
          : c.fieldSlug
      ))
      .filter((s): s is string => typeof s === 'string' && s.length > 0);
  }, [view.config]);
  const fieldSlugs = useMemo(() => {
    const allSlugs = columnSettingsOptions.map((column) => column.slug);
    if (!viewVisibleSlugs) return allSlugs;
    // Сохраняем порядок из view.config; неупомянутые слаги отбрасываются.
    const filtered = viewVisibleSlugs.filter((s) => allSlugs.includes(s));
    // Виртуальная колонка «Тип записи» доступна всегда для multi-type views,
    // даже если в сохранённом view.config её нет (старые представления).
    const isMultiType = (view.itemTypeIds ?? []).length > 1;
    let result = filtered;
    if (isMultiType && allSlugs.includes('_item_type') && !result.includes('_item_type')) {
      result = ['_item_type', ...result];
    }
    // Псевдо-колонка «Ключ» доступна всегда — даже если view сохранён без неё.
    if (allSlugs.includes('_display_key') && !result.includes('_display_key')) {
      result = [...result, '_display_key'];
    }
    // Колонка «Jira» (если интеграция включена) — доступна всегда, даже если
    // в сохранённом view.config её нет.
    if (allSlugs.includes('_jira') && !result.includes('_jira')) {
      result = [...result, '_jira'];
    }
    // Колонка статуса оценки Hermes — доступна всегда при включённой интеграции,
    // даже если в сохранённом view.config её нет.
    if (allSlugs.includes('_hermes') && !result.includes('_hermes')) {
      result = [...result, '_hermes'];
    }
    if (allSlugs.includes('_hermes_run') && !result.includes('_hermes_run')) {
      result = [...result, '_hermes_run'];
    }
    // Мета-колонки «Создано / Обновлено / Кто создал» доступны всегда: они не
    // настраиваются через EditViewModal и редактируются только чекбоксами в
    // чузере. Без этого fallback они навсегда исчезают из чузера, как только
    // view.config.columns был хоть раз сохранён без них.
    for (const meta of ['_created_at', '_updated_at', '_created_by'] as const) {
      if (allSlugs.includes(meta) && !result.includes(meta)) {
        result = [...result, meta];
      }
    }
    return result;
  }, [columnSettingsOptions, viewVisibleSlugs, view.itemTypeIds]);
  // P1-1: мета-колонки, скрытые по умолчанию. Видимы только если явно включены —
  // либо сохранены в view.config.columns, либо отмечены пользователем (metaShownByView).
  const META_DEFAULT_HIDDEN = useMemo(() => ['_created_at', '_updated_at', '_created_by'] as const, []);
  const metaShownSlugs = useMemo(() => {
    const set = new Set<string>(metaShownByView[view.id] ?? []);
    // Если мета-колонка явно перечислена в сохранённых колонках представления —
    // считаем её видимой (старые view с сохранённым набором не ломаем).
    if (viewVisibleSlugs) {
      for (const meta of META_DEFAULT_HIDDEN) {
        if (viewVisibleSlugs.includes(meta)) set.add(meta);
      }
    }
    return set;
  }, [metaShownByView, view.id, viewVisibleSlugs, META_DEFAULT_HIDDEN]);
  const isMetaColShown = useCallback(
    (slug: string) => !(META_DEFAULT_HIDDEN as readonly string[]).includes(slug) || metaShownSlugs.has(slug),
    [META_DEFAULT_HIDDEN, metaShownSlugs],
  );
  const toggleMetaCol = useCallback(
    (slug: string) => {
      setMetaShownByView((prev) => {
        const cur = new Set(prev[view.id] ?? []);
        if (cur.has(slug)) cur.delete(slug); else cur.add(slug);
        return { ...prev, [view.id]: Array.from(cur) };
      });
    },
    [view.id],
  );
  // Дефолтная группировка представления (FR-4): хранится в view.config.grouping
  // (массив уровней либо legacy-объект). Прокидываем в useTableSettings как
  // initial — она применяется, пока пользователь сам не менял группировку.
  const viewDefaultGrouping = useMemo<GroupingLevel[]>(() => {
    const raw = (view.config as { grouping?: unknown } | null)?.grouping;
    if (Array.isArray(raw)) {
      return raw
        .filter((g): g is { fieldSlug: string; order?: 'name' | 'count' } =>
          !!g && typeof g === 'object' && typeof (g as { fieldSlug?: unknown }).fieldSlug === 'string')
        .map((g) => ({ fieldSlug: g.fieldSlug, order: g.order ?? 'name' }));
    }
    if (raw && typeof raw === 'object' && typeof (raw as { fieldSlug?: unknown }).fieldSlug === 'string') {
      const single = raw as { fieldSlug: string; order?: 'name' | 'count' };
      return [{ fieldSlug: single.fieldSlug, order: single.order ?? 'name' }];
    }
    return [];
  }, [view.config]);
  const tableSettings = useTableSettings(`${spaceId}:${view.id}`, fieldSlugs, viewDefaultGrouping);

  // DnD sensors for column reorder
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = tableSettings.orderedSlugs.indexOf(active.id as string);
    const newIndex = tableSettings.orderedSlugs.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    tableSettings.setColOrder(arrayMove(tableSettings.orderedSlugs, oldIndex, newIndex));
  }, [tableSettings]);

  // Deep-link: open item drawer from ?item=ID
  useEffect(() => {
    const itemId = searchParams.get('item');
    if (!itemId) return;
    getItem(spaceId, itemId)
      .then((row) => setDrawerItem(row))
      .catch(() => void message.error('Не удалось загрузить запись по ссылке'));
  }, [searchParams, spaceId]);

  const openDrawer = useCallback((item: ItemRow) => {
    setDrawerItem(item);
    // В URL предпочитаем человекочитаемый displayKey (`ITEM-238`); если его
    // нет (старые записи без ключа) — fallback на UUID.
    const key = item.displayKey ?? item.id;
    setSearchParams((prev) => { prev.set('item', key); return prev; }, { replace: true });
  }, [setSearchParams]);

  // Открыли запись — уведомления по ней прочитаны, подсветка снимается.
  // Эффект на id, а не вызов в openDrawer: карточку открывает ещё и deep-link
  // `?item=`, а дублировать вызов в двух точках — способ забыть про одну.
  useEffect(() => {
    if (!drawerItem) return;
    void markItemRead(drawerItem.id);
  }, [drawerItem, markItemRead]);

  const closeDrawer = useCallback(() => {
    setDrawerItem(null);
    setSearchParams((prev) => { prev.delete('item'); return prev; }, { replace: true });
  }, [setSearchParams]);

  // При открытии карточки/панели догружаем ПОЛНЫЙ resolvedRefs записи: грид
  // резолвит только видимые колонки, поэтому поля карточки, которых нет в гриде
  // (assignee/reporter/руководитель и т.п.), иначе показывались бы сырыми UUID.
  // GET /items/:id возвращает resolvedRefs по всем ссылочным/user-полям записи.
  const openItemId = drawerItem?.id ?? null;
  useEffect(() => {
    if (!openItemId) return;
    let cancelled = false;
    getItem(spaceId, openItemId)
      .then((row) => {
        if (cancelled) return;
        const refs = (row as ItemRowWithRefs).resolvedRefs;
        if (refs && Object.keys(refs).length > 0) {
          setResolvedRefs((prev) => ({ ...prev, ...refs }));
        }
      })
      .catch(() => {
        /* резолв ссылок — вспомогательный; молча деградируем */
      });
    return () => {
      cancelled = true;
    };
  }, [openItemId, spaceId]);

  const handleLinkedItemClick = useCallback((itemId: string, targetSpaceId?: string) => {
    if (!targetSpaceId || targetSpaceId === spaceId) {
      // Same space — open drawer without changing view
      getItem(spaceId, itemId)
        .then((row) => openDrawer(row))
        .catch(() => void message.error('Не удалось загрузить связанную запись'));
    } else {
      // Different space — navigate
      navigate(`/spaces/${targetSpaceId}?item=${itemId}`);
    }
  }, [spaceId, openDrawer, navigate]);

  const activeView = views.find((v) => v.id === activeViewId) ?? null;
  const isKanban = activeView?.viewType === 'kanban';

  // Views are loaded at SpacePage level and passed as props

  // `silent` — фоновый рефетч без спиннера (напр. после reorder карточек канбана):
  // порядок на доске уже отрисован локально, спиннер вызвал бы лишнее мигание.
  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const applicableFilters = serverFilters.filter(isFilterApplicable);
      const data = await queryItems(spaceId, {
        viewId: view.id,
        additionalFilters: applicableFilters.length > 0 ? applicableFilters : undefined,
        sorts: serverSorts.length > 0 ? serverSorts : undefined,
        search: search || undefined,
        limit: 500,
      });
      setResponse(data);
      if (data.resolvedRefs) mergeResolvedRefs(data.resolvedRefs);
      setLinkedAggregatesMap(data.linkedAggregates ?? {});
      setViewComputedMap(data.viewComputed ?? {});
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить записи');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [mergeResolvedRefs, spaceId, view.id, serverFilters, serverSorts, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRemoveLinkAggregateLink = useCallback(
    async (ownerItemId: string, linkId: string) => {
      try {
        await apiRequest(`/api/spaces/${spaceId}/items/${ownerItemId}/links/${linkId}`, {
          method: 'DELETE',
        });
        void message.success('Связь удалена');
        void load();
      } catch (err) {
        void message.error(err instanceof ApiError ? err.message : 'Не удалось удалить связь');
      }
    },
    [load, spaceId],
  );

  // ITEM-13: best-effort превью автоподстановки текущего пользователя в
  // reference-поля формы создания. Авторитетная подстановка происходит на
  // бэкенде в createItem (по ключевой колонке справочника: login, затем
  // email); здесь лишь заранее показываем значение в форме, если запись
  // справочника нашлась. Неудача резолва — не ошибка: поле остаётся пустым,
  // бэкенд всё равно подставит при сохранении.
  const previewAutofillCurrentUserRefs = (
    targetFields: FieldDefinition[],
    alreadySet: ReadonlySet<string>,
    /** Поля, приравненные к текущему пользователю фильтром (`= я`) — их резолвим так же. */
    fromFilters?: ReadonlySet<string>,
  ): void => {
    if (!user) return;
    const currentUser = user;
    const candidates = targetFields.filter((f) => {
      if (f.fieldType !== 'reference') return false;
      const cfg = (f.config ?? {}) as Record<string, unknown>;
      const wanted = cfg.autofillCurrentUser === true || fromFilters?.has(f.slug) === true;
      return wanted && typeof cfg.dictionaryId === 'string' && !alreadySet.has(f.slug);
    });
    for (const field of candidates) {
      const cfg = field.config as { dictionaryId: string; multiple?: boolean };
      void (async () => {
        try {
          const dict = await getDictionary(cfg.dictionaryId);
          const keySlug = (dict.columns ?? []).find((c) => c.id === dict.keyColumnId)?.slug;
          if (!keySlug) return;
          const matchEntry = async (needle: string) => {
            const res = await listDictEntries(cfg.dictionaryId, { search: needle, limit: 50 });
            return res.items.find(
              (e) => ((e.values as Record<string, unknown> | null)?.[keySlug]) === needle,
            ) ?? null;
          };
          const entry =
            (await matchEntry(currentUser.login)) ??
            (currentUser.email ? await matchEntry(currentUser.email) : null);
          if (!entry) return;
          // Прогреваем опции и resolvedRefs, чтобы Select показал label, а не uuid.
          void ensureReferenceOptions(field);
          mergeResolvedRefs({ [entry.id]: entryLabel(entry, dict) });
          setCreateModalFields((prev) => {
            const cur = prev[field.slug];
            if (cur !== undefined && cur !== null && !(Array.isArray(cur) && cur.length === 0)) return prev;
            return { ...prev, [field.slug]: cfg.multiple ? [entry.id] : entry.id };
          });
        } catch {
          // превью необязательно — бэкенд подставит значение при создании
        }
      })();
    }
  };

  const handleCreate = async (
    typeIdOverride?: string,
    kanbanColumn?: { field: string; value: string },
  ) => {
    // Для multi-type представления не открываем отдельное окно выбора типа —
    // тип выбирается прямо в форме создания (canPickType). По умолчанию берём
    // первый тип представления.
    const targetTypeId = typeIdOverride ?? singleTypeId ?? (view.itemTypeIds ?? [])[0] ?? null;
    if (!targetTypeId) return;
    // Автозаполнение из фильтров представления (FR-4.1):
    // фильтры из view.config + активные userFilters с оператором `eq` подставляются в новую запись.
    const targetType = allItemTypes.find((t) => t.id === targetTypeId);
    const targetFields = targetType?.fields ?? mergedFields;
    const viewFilters = ((view.config as { filters?: FilterLeaf[] } | null)?.filters ?? []);
    const activeFilters = [...viewFilters, ...userFilters, ...activeQuickFilters.flatMap((q) => q.filters)];
    const autofilled = defaultsFromFilters(activeFilters, targetFields);
    // Поля, приравненные фильтром к текущему пользователю («Исполнитель = я»):
    // user-поле заполняем сразу, reference-поле — асинхронным резолвом ниже.
    const meSlugs = new Set(currentUserFilterSlugs(activeFilters, targetFields));
    const meAutofill: Record<string, unknown> = {};
    if (user?.id) {
      for (const f of targetFields) {
        if (f.fieldType !== 'user' || !meSlugs.has(f.slug)) continue;
        meAutofill[f.slug] = (f.config as { multiple?: boolean } | null)?.multiple === true ? [user.id] : user.id;
      }
    }
    // Дефолты из конфига каждого поля (FieldConfig.defaultValue + статусный backlog).
    // Сами по себе не показываются как «из фильтра» — это просто стартовое значение.
    // Фильтры представления имеют приоритет (более специфичны для контекста).
    const fieldDefaults = defaultsFromFieldConfigs(targetFields);
    // ITEM-13: автоподстановка текущего пользователя (user-поля — синхронно).
    // Фильтры представления специфичнее и перекрывают её.
    const userAutofill = autofillCurrentUserDefaults(targetFields, user?.id);
    // Предзаполнение поля-колонки при создании из канбана — имеет приоритет
    // над дефолтами/автозаполнением, т.к. задаёт целевую колонку. Передаётся
    // аргументом: через useState значение не успевало дойти до этого вызова,
    // и карточка падала в колонку по умолчанию.
    const kanbanPrefill = kanbanColumn
      ? { [kanbanColumn.field]: kanbanColumn.value || undefined }
      : {};
    setCreateModalMode('create');
    setCreateModalSourceItemId(null);
    setCreateModalSourceItemIds(null);
    setCreateModalTypeId(targetTypeId);
    const initialFields = { ...fieldDefaults, ...userAutofill, ...autofilled, ...meAutofill, ...kanbanPrefill };
    setCreateModalFields(initialFields);
    setCreateModalAutofilled(autofilled);
    setCreateModalClearedSlugs(new Set());
    setCreateModalOpen(true);
    previewAutofillCurrentUserRefs(
      targetFields,
      new Set(
        Object.entries(initialFields)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([slug]) => slug),
      ),
      meSlugs,
    );
    // Прогреваем опции user/reference-полей, получивших дефолт: Select в
    // модалке грузит их только onFocus, и без прогрева value светился бы
    // сырым uuid вместо label (см. lens-pitfalls §9).
    for (const f of targetFields) {
      if (f.fieldType !== 'user' && f.fieldType !== 'reference') continue;
      if (initialFields[f.slug] === undefined || initialFields[f.slug] === null) continue;
      void ensureReferenceOptions(f);
    }
  };

  // Смена типа записи прямо в форме создания (до сохранения). Совместимые
  // значения переносятся (carryFieldsToType), несовместимые/чужие — отбрасываются,
  // дефолты нового типа добавляются для незаполненных полей.
  const handleChangeCreateType = (nextTypeId: string) => {
    if (nextTypeId === createModalTypeId) return;
    const fromFields = allItemTypes.find((t) => t.id === createModalTypeId)?.fields ?? [];
    const toFields = allItemTypes.find((t) => t.id === nextTypeId)?.fields ?? [];
    const carried = carryFieldsToType(createModalFields, fromFields, toFields);
    const fieldDefaults = defaultsFromFieldConfigs(toFields);
    const userAutofill = autofillCurrentUserDefaults(toFields, user?.id);
    setCreateModalTypeId(nextTypeId);
    const nextFields = { ...fieldDefaults, ...userAutofill, ...carried };
    setCreateModalFields(nextFields);
    previewAutofillCurrentUserRefs(
      toFields,
      new Set(
        Object.entries(nextFields)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([slug]) => slug),
      ),
    );
    for (const f of toFields) {
      if (f.fieldType !== 'user' && f.fieldType !== 'reference') continue;
      if (nextFields[f.slug] === undefined || nextFields[f.slug] === null) continue;
      void ensureReferenceOptions(f);
    }
    // Автозаполнение «из фильтра» больше не релевантно для другого типа.
    setCreateModalAutofilled((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([slug]) => slug in carried)),
    );
    setCreateModalClearedSlugs(new Set());
  };

  // Открыть модалку «Дублировать запись» с предзаполненными значениями исходной записи.
  // Поля, которые пользователь оставит как есть, по сути будут отправлены как overrides
  // равные исходному значению — то есть запись скопируется. Что пользователь изменит —
  // станет новым значением.
  const handleOpenDuplicateSingle = (sourceItem: ItemRow) => {
    const targetType = allItemTypes.find((t) => t.id === sourceItem.itemTypeId);
    const typeFields = targetType?.fields ?? mergedFields;
    const editable = typeFields.filter((f) => f.fieldType !== 'computed');
    const base: Record<string, unknown> = {};
    for (const f of editable) {
      const val = (sourceItem.fields as Record<string, unknown> | undefined)?.[f.slug];
      if (val !== undefined && val !== null && !(Array.isArray(val) && val.length === 0)) {
        base[f.slug] = val;
      }
    }
    setCreateModalMode('duplicate-single');
    setCreateModalSourceItemId(sourceItem.id);
    setCreateModalSourceItemIds(null);
    setCreateModalTypeId(sourceItem.itemTypeId);
    setCreateModalFields({ ...base });
    setCreateModalAutofilled({});
    setCreateModalClearedSlugs(new Set());
    setCreateModalReplacements({});
    setCreateModalOpen(true);
  };

  // Открыть модалку массового дублирования. Поля пустые: что заполнят — будет
  // применено ко всем выбранным записям как override; что не заполнят — останется
  // как в каждой исходной записи.
  const handleOpenDuplicateBulk = (sourceIds: string[]) => {
    if (sourceIds.length === 0) return;
    // Определяем тип: если все выбранные одного типа — берём его поля; иначе
    // показываем общие поля представления (как в multi-type create).
    const sourceItems = (response?.items ?? []).filter((it) => sourceIds.includes(it.id));
    const typeIds = Array.from(new Set(sourceItems.map((it) => it.itemTypeId)));
    const targetTypeId: string | null = typeIds.length === 1 ? (typeIds[0] ?? null) : null;
    setCreateModalMode('duplicate-bulk');
    setCreateModalSourceItemId(null);
    setCreateModalSourceItemIds(sourceIds);
    setCreateModalTypeId(targetTypeId);
    setCreateModalFields({});
    setCreateModalAutofilled({});
    setCreateModalClearedSlugs(new Set());
    setCreateModalReplacements({});
    setCreateModalOpen(true);
  };

  // Собирает массив замен из createModalReplacements: только записи с непустым
  // `search`. undefined — если замен нет (чтобы не слать пустой массив).
  const buildModalReplacements = (): FieldReplacement[] | undefined => {
    const list: FieldReplacement[] = Object.entries(createModalReplacements)
      .filter(([, r]) => r.search.length > 0)
      .map(([fieldSlug, r]) => ({ fieldSlug, search: r.search, replace: r.replace }));
    return list.length > 0 ? list : undefined;
  };

  /** Сброс формы создания в исходное состояние. */
  const resetCreateModal = (): void => {
    setCreateModalOpen(false);
    setCreateModalFields({});
    setCreateModalAutofilled({});
    setCreateModalExpandedField(null);
    setCreateModalMode('create');
    setCreateModalSourceItemId(null);
    setCreateModalSourceItemIds(null);
    setCreateModalClearedSlugs(new Set());
    setCreateModalReplacements({});
  };

  /**
   * Закрытие формы создания — всегда через подтверждение.
   *
   * Раньше любой промах мимо окна (maskClosable по умолчанию) молча стирал всё
   * набранное: onCancel очищает поля, черновика у формы нет. Спрашиваем
   * безусловно, а не «только если что-то введено»: часть полей формы
   * предзаполнена (статус по умолчанию, значения из фильтра представления), и
   * любое правило «что считать вводом» — догадка, которая ошибается в обе
   * стороны. Лишний вопрос дешевле потерянного текста.
   */
  const handleCreateModalCancel = (): void => {
    Modal.confirm({
      title: 'Закрыть форму?',
      content: 'Введённые данные не сохранятся.',
      okText: 'Закрыть без сохранения',
      okButtonProps: { danger: true },
      cancelText: 'Продолжить ввод',
      onOk: resetCreateModal,
    });
  };

  const handleCreateConfirm = async () => {
    if (createModalMode === 'duplicate-single') {
      if (!createModalSourceItemId) return;
      setCreating(true);
      try {
        // overrides = всё, что сейчас в модалке. Поля, которых нет (undefined),
        // сервер возьмёт из источника; заполненные — заменят значения источника.
        // Поля из createModalClearedSlugs уходят как null — бэк это очистит.
        const overrides: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(createModalFields)) {
          if (v !== undefined) overrides[k] = v;
        }
        for (const slug of createModalClearedSlugs) {
          overrides[slug] = null;
        }
        const replacements = buildModalReplacements();
        const row = await duplicateItem(spaceId, createModalSourceItemId, overrides, replacements);
        if (row.resolvedRefs) mergeResolvedRefs(row.resolvedRefs);
        setResponse((prev) =>
          prev ? { ...prev, items: [row, ...prev.items] } : { items: [row], nextCursor: null, total: 1 },
        );
        void message.success('Запись скопирована');
        setCreateModalOpen(false);
        setCreateModalFields({});
        setCreateModalAutofilled({});
        setCreateModalMode('create');
        setCreateModalSourceItemId(null);
        setCreateModalClearedSlugs(new Set());
        openDrawer(row);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Не удалось скопировать запись');
      } finally {
        setCreating(false);
      }
      return;
    }
    if (createModalMode === 'duplicate-bulk') {
      if (!createModalSourceItemIds || createModalSourceItemIds.length === 0) return;
      setCreating(true);
      try {
        const overrides: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(createModalFields)) {
          if (v !== undefined) overrides[k] = v;
        }
        for (const slug of createModalClearedSlugs) {
          overrides[slug] = null;
        }
        const replacements = buildModalReplacements();
        const result = await bulkDuplicateItems(spaceId, {
          itemIds: createModalSourceItemIds,
          overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
          replacements,
        });
        void message.success(`Скопировано: ${result.duplicated}`);
        setSelectedRowKeys([]);
        setCreateModalOpen(false);
        setCreateModalFields({});
        setCreateModalAutofilled({});
        setCreateModalMode('create');
        setCreateModalSourceItemIds(null);
        setCreateModalClearedSlugs(new Set());
        void load();
      } catch (err) {
        void message.error(err instanceof ApiError ? err.message : 'Ошибка копирования');
      } finally {
        setCreating(false);
      }
      return;
    }
    if (!createModalTypeId) return;
    setCreating(true);
    try {
      const row = await createItem(spaceId, { itemTypeId: createModalTypeId, fields: createModalFields });
      if (row.resolvedRefs) mergeResolvedRefs(row.resolvedRefs);
      setResponse((prev) =>
        prev ? { ...prev, items: [row, ...prev.items] } : { items: [row], nextCursor: null, total: 1 },
      );
      setCreateModalOpen(false);
      setCreateModalFields({});
      setCreateModalAutofilled({});
      message.success('Запись создана');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось создать запись');
    } finally {
      setCreating(false);
    }
  };

  const handleFieldSave = async (itemId: string, fieldSlug: string, value: unknown) => {
    try {
      const updated = await patchItem(spaceId, itemId, { fields: { [fieldSlug]: value } });
      if (updated.resolvedRefs) mergeResolvedRefs(updated.resolvedRefs);
      setResponse((prev) =>
        prev
          ? { ...prev, items: prev.items.map((it) => (it.id === itemId ? updated : it)) }
          : prev,
      );
      // Update drawer item if it's the same record
      setDrawerItem((prev) => (prev && prev.id === itemId ? updated : prev));
    } catch (err) {
      void message.error(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    }
  };

  // Редактор значения для режима «Задать значение» в объединённой bulk-модалке
  // (todos #4). Возвращает контрол по типу поля; value/onChange — состояние операции.
  const renderBulkSetEditor = (
    field: FieldDefinition,
    value: unknown,
    onChange: (v: unknown) => void,
  ): JSX.Element => {
    const ft = field.fieldType;
    if (ft === 'select') {
      const opts = (field.config as { options?: Array<{ value: string; label: string }> })?.options ?? [];
      return (
        <Select
          style={{ width: '100%' }}
          placeholder="Значение"
          allowClear
          value={(value as string) || undefined}
          onChange={(v) => onChange(v ?? undefined)}
          options={opts.map((o) => ({ value: o.value, label: o.label || o.value }))}
        />
      );
    }
    if (ft === 'multi_select') {
      const opts = (field.config as { options?: Array<{ value: string; label: string }> })?.options ?? [];
      return (
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Значения"
          value={(value as string[]) ?? []}
          onChange={(v: string[]) => onChange(v.length > 0 ? v : undefined)}
          options={opts.map((o) => ({ value: o.value, label: o.label || o.value }))}
        />
      );
    }
    if (ft === 'boolean') {
      return (
        <Select
          style={{ width: '100%' }}
          placeholder="Значение"
          allowClear
          value={value as boolean | undefined}
          onChange={(v) => onChange(v ?? undefined)}
          options={[{ value: true, label: 'Да' }, { value: false, label: 'Нет' }]}
        />
      );
    }
    if (ft === 'date') {
      return (
        <DatePicker
          style={{ width: '100%' }}
          format={DATE_FORMAT}
          value={value ? dayjs(value as string) : null}
          onChange={(d) => onChange(dateFieldValue(d) ?? undefined)}
        />
      );
    }
    if (ft === 'number') {
      return (
        <Input
          type="number"
          placeholder="Значение"
          value={value != null ? String(value) : ''}
          onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
        />
      );
    }
    if (ft === 'reference' || ft === 'user') {
      const isMulti = ((field.config as { multiple?: boolean })?.multiple) ?? false;
      const opts = referenceOptionsForField(field) ?? [];
      return (
        <Select
          style={{ width: '100%' }}
          placeholder="Значение"
          allowClear
          showSearch
          mode={isMulti ? 'multiple' : undefined}
          value={(value as string | string[]) || undefined}
          onChange={(v) => {
            // Группа разворачивается в участников сразу при выборе.
            const next = ft === 'user' ? expandUserGroupSelection(v, userGroups) : v;
            onChange(next ?? undefined);
          }}
          popupRender={
            ft === 'user' && isMulti
              ? (menu) => renderUserBulkPopup(menu, opts.map((o) => o.value), value, (next) => onChange(next.length > 0 ? next : undefined))
              : undefined
          }
          onFocus={() => { void ensureReferenceOptions(field); }}
          filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          options={(ft === 'user' ? withUserGroupOptions(opts, userGroups, isMulti) : opts) as DefaultOptionType[]}
        />
      );
    }
    return (
      <Input
        placeholder="Значение"
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      />
    );
  };

  // Inter-group DnD sensors (separate from column-reorder)
  const groupDragSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleGroupDragStart = useCallback((event: DragStartEvent) => {
    const itemId = (event.active.data?.current as { itemId?: string })?.itemId;
    if (itemId) setDraggedItemId(itemId);
  }, []);

  const handleGroupDragEnd = (event: DragEndEvent) => {
    setDraggedItemId(null);
    const { active, over } = event;
    if (!over) return;

    const activeData = active.data?.current as { type?: string; itemId?: string } | undefined;
    const overData = over.data?.current as { type?: string; fieldSlug?: string; rawValue?: string } | undefined;

    if (activeData?.type !== 'item' || overData?.type !== 'group') return;
    if (!activeData.itemId || !overData.fieldSlug) return;
    // Комбинированная группа многозначного поля («A, B») не является валидным
    // значением поля — перетаскивание в неё игнорируем, иначе поле получит
    // склеенный ключ.
    if (overData.rawValue?.includes(GROUP_MULTI_SEP)) return;

    // Skip if dropping back into the same group
    const item = (response?.items ?? []).find((it) => it.id === activeData.itemId);
    const currentVal = item?.fields[overData.fieldSlug];
    const targetVal = overData.rawValue === '__empty__' ? null : overData.rawValue ?? null;
    if (String(currentVal ?? '') === String(targetVal ?? '')) return;

    void handleFieldSave(activeData.itemId, overData.fieldSlug, targetVal);
  };

  const draggedItem = useMemo(
    () => draggedItemId ? (response?.items ?? []).find((it) => it.id === draggedItemId) ?? null : null,
    [draggedItemId, response?.items],
  );

  const handleDelete = async (itemId: string) => {
    try {
      await deleteItem(spaceId, itemId);
      setResponse((prev) =>
        prev ? { ...prev, items: prev.items.filter((it) => it.id !== itemId) } : prev,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось удалить запись');
    }
  };

  // Канбан drag&drop: смена колонки = PATCH статуса, порядок внутри колонки =
  // reorder (общий для всех, пишется в items.sort_order). Порядок на доске уже
  // отрисован локально в KanbanBoard, поэтому запросы шлём фоном и в конце делаем
  // тихий refetch за каноническим порядком; спиннер не поднимаем.
  const handleCardDrop = async ({
    itemId,
    fromColumn,
    toColumn,
    orderedIdsInTarget,
  }: KanbanCardDropResult) => {
    if (!activeView || activeView.viewType !== 'kanban') return;
    const kanbanConfig = activeView.config as unknown as KanbanViewConfig;
    const columnChanged = fromColumn !== toColumn;
    try {
      if (columnChanged) {
        const updated = await patchItem(spaceId, itemId, {
          fields: { [kanbanConfig.columnField]: toColumn || null },
        });
        if (updated.resolvedRefs) mergeResolvedRefs(updated.resolvedRefs);
      }
      // Когда порядок карточек задан сортировкой (представления или личной),
      // ручной порядок по слотам sort_order не пишем — сервер всё равно вернёт
      // записи в порядке сортировки, а запись sort_order сломала бы порядок
      // в других представлениях.
      if (!kanbanOrderLocked) await reorderItems(spaceId, orderedIdsInTarget);
      await load({ silent: true });
    } catch (err) {
      void message.error(err instanceof ApiError ? err.message : 'Не удалось переместить');
      await load({ silent: true }); // откат к серверному состоянию
    }
  };

  // M8: добавить запись в колонку (создать + проставить поле-колонку)
  const handleAddCardToColumn = async (columnValue: string) => {
    if (!activeView || activeView.viewType !== 'kanban') return;
    const kanbanConfig = activeView.config as unknown as KanbanViewConfig;
    // Предзаполняем поле-колонку и открываем обычную форму создания записи
    // (`handleCreate`), а не создаём пустую карточку напрямую. Для single-type
    // откроется модалка создания, для multi-type — сначала выбор типа.
    await handleCreate(undefined, { field: kanbanConfig.columnField, value: columnValue });
  };

  // Загрузка изображения из markdown-редактора (cmd+v / drag-drop).
  // Возвращает URL вложения для вставки в текст; ошибку показываем тостом.
  const handleUploadImage = useCallback(
    async (file: File): Promise<string> => {
      try {
        const saved = await uploadAttachment(spaceId, file);
        return saved.url;
      } catch (err) {
        void message.error(
          err instanceof ApiError ? err.message : 'Не удалось загрузить изображение',
        );
        throw err;
      }
    },
    [spaceId],
  );

  const loadMore = useCallback(async () => {
    if (!response?.nextCursor) return;
    try {
      const applicableFilters = serverFilters.filter(isFilterApplicable);
      const data = await queryItems(spaceId, {
        viewId: view.id,
        additionalFilters: applicableFilters.length > 0 ? applicableFilters : undefined,
        sorts: serverSorts.length > 0 ? serverSorts : undefined,
        search: search || undefined,
        cursor: response.nextCursor,
        limit: 500,
      });
      setResponse((prev) =>
        prev ? { ...prev, items: [...prev.items, ...data.items], nextCursor: data.nextCursor } : data,
      );
      if (data.resolvedRefs) mergeResolvedRefs(data.resolvedRefs);
      if (data.linkedAggregates) {
        setLinkedAggregatesMap((prev) => ({ ...prev, ...data.linkedAggregates }));
      }
      if (data.viewComputed) {
        setViewComputedMap((prev) => ({ ...prev, ...data.viewComputed }));
      }
    } catch (err) {
      void message.error(err instanceof ApiError ? err.message : 'Ошибка загрузки');
    }
  }, [mergeResolvedRefs, response?.nextCursor, spaceId, view.id, serverFilters, serverSorts, search]);

  // Auto-load remaining pages so client-side Antd pagination видит все записи,
  // а не только первые 100 (счётчик "X из Y" иначе показывает заниженный total).
  useEffect(() => {
    if (response?.nextCursor && !loading) {
      void loadMore();
    }
  }, [response?.nextCursor, loading, loadMore]);

  // ── Фоновое обновление доски (ITEM-16) ────────────────────────────────────
  // Раньше данные жили с момента открытия страницы: правки коллег не появлялись,
  // пока не перезагрузишь вкладку. Полноценный realtime архитектурой исключён
  // (`ARCHITECTURE.md` §4 «Нет realtime», ROADMAP «Realtime-синхронизация —
  // явно исключено»), а ADR предписывает «Polling в MVP, SSE позже» — его и
  // делаем.
  //
  // Тихий рефетч заменяет `response` целиком, поэтому он ЗАПРЕЩЁН, пока
  // пользователь в середине работы: иначе исчезнет несохранённый ввод и собьётся
  // выделение — тот же класс потерь, что ловили в ITEM-14.
  // Сами условия — в boardRefresh.ts (чистая функция, покрыта тестами).
  const canAutoRefresh = useCallback((): boolean => canRefreshBoard({
    hidden: document.hidden,
    loading,
    drawerOpen: !!drawerItem,
    dragging: !!draggedItemId,
    selectionCount: selectedRowKeys.length,
    hasMorePages: !!response?.nextCursor,
    focusInEditor: isFocusInEditor(document.activeElement),
  }), [loading, drawerItem, draggedItemId, selectedRowKeys.length, response?.nextCursor]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (canAutoRefresh()) void load({ silent: true });
    }, BOARD_REFRESH_MS);
    return () => clearInterval(timer);
  }, [canAutoRefresh, load]);

  // Вернулись во вкладку — подтянуть сразу, не дожидаясь таймера: типичный
  // сценарий «переключился в Jira/почту, вернулся» иначе показывает старое.
  useEffect(() => {
    const refreshIfIdle = (): void => {
      if (canAutoRefresh()) void load({ silent: true });
    };
    document.addEventListener('visibilitychange', refreshIfIdle);
    window.addEventListener('focus', refreshIfIdle);
    return () => {
      document.removeEventListener('visibilitychange', refreshIfIdle);
      window.removeEventListener('focus', refreshIfIdle);
    };
  }, [canAutoRefresh, load]);

  const visibleItems = useMemo(() => {
    let items = response?.items ?? [];

    // Клиентская фильтрация по link_aggregate колонкам.
    // Семантика AND/OR идентична view-computed: ИЛИ начинает новую AND-группу,
    // итог = хотя бы одна AND-группа прошла целиком.
    const applicableLaFilters = linkAggregateFilters.filter(isFilterApplicable);
    if (applicableLaFilters.length > 0) {
      const evalLaLeaf = (filter: FilterLeaf, itemId: string): boolean => {
        const rawVal = linkedAggregatesMap[itemId]?.[filter.fieldSlug];
        const numVal = typeof rawVal === 'number' ? rawVal : (rawVal != null ? Number(rawVal) : null);
        const strVal = rawVal != null ? String(rawVal) : null;
        const op = filter.operator;
        const fv = filter.value;
        if (op === 'is_empty') return rawVal == null || rawVal === 0;
        if (op === 'is_not_empty') return rawVal != null && rawVal !== 0;
        if (op === 'eq') return numVal !== null && numVal === Number(fv);
        if (op === 'neq') return numVal === null || numVal !== Number(fv);
        if (op === 'gt') return numVal !== null && numVal > Number(fv);
        if (op === 'gte') return numVal !== null && numVal >= Number(fv);
        if (op === 'lt') return numVal !== null && numVal < Number(fv);
        if (op === 'lte') return numVal !== null && numVal <= Number(fv);
        if (op === 'contains') return strVal !== null && strVal.toLowerCase().includes(String(fv).toLowerCase());
        if (op === 'not_contains') return strVal === null || !strVal.toLowerCase().includes(String(fv).toLowerCase());
        return true;
      };
      // Разбиваем на OR-группы (каждая = AND внутри).
      type LaGroup = FilterLeaf[];
      const laGroups: LaGroup[] = [[]];
      for (const f of applicableLaFilters) {
        if (f.combinator === 'or' && laGroups[laGroups.length - 1]!.length > 0) {
          laGroups.push([f]);
        } else {
          laGroups[laGroups.length - 1]!.push(f);
        }
      }
      items = items.filter((item) =>
        laGroups.some((group) => group.every((f) => evalLaLeaf(f, item.id))),
      );
    }

    // Клиентская фильтрация по reference_attribute колонкам.
    const applicableRaFilters = referenceAttributeFilters.filter(isFilterApplicable);
    if (applicableRaFilters.length > 0) {
      const evalRaLeaf = (filter: FilterLeaf, item: ItemRow): boolean => {
        // Найти колонку по slug (_refattr:{key} → key)
        const slug = filter.fieldSlug;
        const colKey = slug.startsWith('_refattr:') ? slug.slice('_refattr:'.length) : slug;
        const col = referenceAttributeColumns.find((c) => c.key === colKey);
        if (!col) return true;
        const refField = mergedFields.find((f) => f.slug === col.referenceFieldSlug);
        const rootDictId = (refField?.config as { dictionaryId?: string } | null)?.dictionaryId;
        if (!rootDictId) return true;
        const refValue = item.fields[col.referenceFieldSlug];
        const ids: string[] = Array.isArray(refValue)
          ? (refValue as unknown[]).filter((v): v is string => typeof v === 'string')
          : typeof refValue === 'string' && refValue ? [refValue] : [];
        // Собираем строковые значения атрибута (по всей цепочке) для всех ref-entry.
        const ctx: RefAttrCtx = {
          dictionaryEntryValues,
          dictionaryColumnsById,
          dictionaryOptionsById,
          resolvedRefs,
        };
        const leaves = walkRefAttrPath(ids, getRefAttrPath(col), rootDictId, ctx);
        const strValues = leaves
          .filter((v) => v !== null && v !== undefined && v !== '')
          .map((v) => String(v));
        const strVal = strValues.length > 0 ? strValues.join(' ') : null;
        const op = filter.operator;
        const fv = filter.value;
        if (op === 'is_empty') return strVal === null;
        if (op === 'is_not_empty') return strVal !== null;
        if (strVal === null) return false;
        if (op === 'eq') return strValues.some((v) => v === String(fv));
        if (op === 'neq') return strValues.every((v) => v !== String(fv));
        if (op === 'in') {
          const set = new Set((Array.isArray(fv) ? fv : []).map((v) => String(v)));
          return strValues.some((v) => set.has(v));
        }
        if (op === 'not_in') {
          const set = new Set((Array.isArray(fv) ? fv : []).map((v) => String(v)));
          return strValues.every((v) => !set.has(v));
        }
        if (op === 'contains') return strVal.toLowerCase().includes(String(fv).toLowerCase());
        if (op === 'not_contains') return !strVal.toLowerCase().includes(String(fv).toLowerCase());
        return true;
      };
      type RaGroup = FilterLeaf[];
      const raGroups: RaGroup[] = [[]];
      for (const f of applicableRaFilters) {
        if (f.combinator === 'or' && raGroups[raGroups.length - 1]!.length > 0) {
          raGroups.push([f]);
        } else {
          raGroups[raGroups.length - 1]!.push(f);
        }
      }
      items = items.filter((item) =>
        raGroups.some((group) => group.every((f) => evalRaLeaf(f, item))),
      );
    }

    if (userSorts.length === 0) {
      // Дефолтная сортировка грида — по алфавиту поля-названия (config.isTitle
      // → slug title/name → первое текстовое), а не по порядку создания
      // (sortOrder/id). Ручного порядка строк нет (drag-drop переставляет
      // только колонки), поэтому sortOrder здесь — лишь стабильный tiebreaker.
      const titleField = resolveTitleField(mergedFields);
      if (!titleField) return items;
      const titleSlug = titleField.slug;
      const isRefTitle = titleField.fieldType === 'reference';
      // reference-заголовок: сортируем по display-метке справочника, а не по
      // сырому entryId (UUID) — иначе алфавитный порядок был бы бессмысленным.
      const titleSortValue = (it: ItemRow): unknown => {
        const raw = it.fields[titleSlug];
        if (!isRefTitle) return raw;
        const first = Array.isArray(raw)
          ? (raw as unknown[]).find((v): v is string => typeof v === 'string')
          : typeof raw === 'string' ? raw : undefined;
        return first ? (resolvedRefs[first] ?? first) : null;
      };
      return [...items].sort((a, b) => {
        const compared = compareSortableValues(titleSortValue(a), titleSortValue(b), 'asc');
        if (compared !== 0) return compared;
        return a.sortOrder - b.sortOrder || a.id.localeCompare(b.id);
      });
    }
    const resolveRefAttrSortValue = (item: ItemRow, slug: string): unknown => {
      const colKey = slug.slice('_refattr:'.length);
      const col = referenceAttributeColumns.find((c) => c.key === colKey);
      if (!col) return null;
      const refField = mergedFields.find((f) => f.slug === col.referenceFieldSlug);
      const rootDictId = (refField?.config as { dictionaryId?: string } | null)?.dictionaryId;
      if (!rootDictId) return null;
      const refValue = item.fields[col.referenceFieldSlug];
      const ids: string[] = Array.isArray(refValue)
        ? (refValue as unknown[]).filter((v): v is string => typeof v === 'string')
        : typeof refValue === 'string' && refValue ? [refValue] : [];
      if (ids.length === 0) return null;
      const ctx: RefAttrCtx = {
        dictionaryEntryValues,
        dictionaryColumnsById,
        dictionaryOptionsById,
        resolvedRefs,
      };
      // Сортируем по первому root entry, склеивая multi-листья через ', '.
      const leaves = walkRefAttrPath([ids[0]!], getRefAttrPath(col), rootDictId, ctx);
      if (leaves.length === 0) return null;
      if (leaves.length === 1) return leaves[0];
      return leaves.map((v) => String(v)).join(', ');
    };
    const sortFieldTypeBySlug = new Map(mergedFields.map((f) => [f.slug, f.fieldType]));
    // Резолв значения reference/user-поля в человекочитаемую метку для сортировки.
    // Значение — id или массив id; берём метку первого через resolvedRefs.
    const resolveRefSortValue = (raw: unknown): string | null => {
      const first = Array.isArray(raw)
        ? (raw as unknown[]).find((v): v is string => typeof v === 'string')
        : typeof raw === 'string' ? raw : undefined;
      if (!first) return null;
      return resolvedRefs[first] ?? first;
    };
    return [...items].sort((a, b) => {
      for (const sort of userSorts) {
        let aValue: unknown;
        let bValue: unknown;
        if (sort.fieldSlug === '_item_type') {
          aValue = allItemTypes.find((t) => t.id === a.itemTypeId)?.name ?? '';
          bValue = allItemTypes.find((t) => t.id === b.itemTypeId)?.name ?? '';
        } else if (sort.fieldSlug === '_created_at') {
          aValue = a.createdAt ?? null;
          bValue = b.createdAt ?? null;
        } else if (sort.fieldSlug === '_updated_at') {
          aValue = a.updatedAt ?? null;
          bValue = b.updatedAt ?? null;
        } else if (sort.fieldSlug === '_created_by') {
          aValue = (a as { createdByDisplayName?: string | null }).createdByDisplayName ?? null;
          bValue = (b as { createdByDisplayName?: string | null }).createdByDisplayName ?? null;
        } else if (linkAggregateKeySet.has(sort.fieldSlug)) {
          aValue = sortableSyntheticValue(linkedAggregatesMap[a.id]?.[sort.fieldSlug]);
          bValue = sortableSyntheticValue(linkedAggregatesMap[b.id]?.[sort.fieldSlug]);
        } else if (sort.fieldSlug.startsWith('_vc:')) {
          // view_computed считается на бэке (applyViewComputedSorts), значения
          // лежат в viewComputedMap, а НЕ в record.fields. Без этой ветки slug
          // падал в else, читал undefined из fields → compare=0 → сортировка
          // сваливалась на sortOrder-tiebreaker и рушила серверный порядок.
          const vcKey = sort.fieldSlug.slice('_vc:'.length);
          aValue = sortableSyntheticValue(viewComputedMap[a.id]?.[vcKey]);
          bValue = sortableSyntheticValue(viewComputedMap[b.id]?.[vcKey]);
        } else if (sort.fieldSlug.startsWith('_refattr:')) {
          aValue = resolveRefAttrSortValue(a, sort.fieldSlug);
          bValue = resolveRefAttrSortValue(b, sort.fieldSlug);
        } else if (sortFieldTypeBySlug.get(sort.fieldSlug) === 'reference' || sortFieldTypeBySlug.get(sort.fieldSlug) === 'user') {
          // reference/user хранят id (логин/UUID) — сортируем по человекочитаемой
          // метке (название справочника / ФИО), иначе порядок идёт по сырому id.
          aValue = resolveRefSortValue(a.fields[sort.fieldSlug]);
          bValue = resolveRefSortValue(b.fields[sort.fieldSlug]);
        } else {
          aValue = a.fields[sort.fieldSlug];
          bValue = b.fields[sort.fieldSlug];
        }
        const compared = compareSortableValues(aValue, bValue, sort.direction);
        if (compared !== 0) return compared;
      }
      return a.sortOrder - b.sortOrder || a.id.localeCompare(b.id);
    });
  }, [response?.items, userSorts, linkAggregateKeySet, linkedAggregatesMap, viewComputedMap, linkAggregateFilters, referenceAttributeFilters, referenceAttributeColumns, dictionaryEntryValues, dictionaryColumnsById, dictionaryOptionsById, resolvedRefs, mergedFields, allItemTypes]);

  // Порядок листания записей из карточки: «что вижу, то и листаю».
  // - Таблица: порядок = видимые строки грида (visibleItems — тот же
  //   отфильтрованный/отсортированный набор, что в таблице/экспорте).
  // - Канбан: порядок = обход колонок слева-направо, внутри колонки — как
  //   лежат карточки (buildKanbanLayout из ТЕХ ЖЕ items, что отданы доске),
  //   иначе стрелки листали бы по плоскому алфавитному списку, а не по доске
  //   (баг ITEM-11: «переключение стрелочками работает не по порядку»).
  const navItems = useMemo(() => {
    if (isKanban && activeView) {
      // mergedFields (а не отфильтрованный `fields`, объявленный ниже) — buildKanbanLayout
      // использует список полей лишь чтобы найти поле-колонку по slug, результат тот же.
      return buildKanbanLayout({
        items: response?.items ?? [],
        fields: mergedFields,
        config: activeView.config as unknown as KanbanViewConfig,
        resolvedRefs,
        dictionaryColumnsById,
        dictionaryEntryValues,
        dictionaryOptionsById,
      }).orderedItems;
    }
    return visibleItems;
  }, [
    isKanban,
    activeView,
    response?.items,
    mergedFields,
    resolvedRefs,
    dictionaryColumnsById,
    dictionaryEntryValues,
    dictionaryOptionsById,
    visibleItems,
  ]);

  // Запись, открытая по deep-link'у вне текущего фильтра, в списке не находится —
  // навигация просто скрывается (navPosition === null).
  const drawerNav = useMemo(() => {
    if (!drawerItem) return null;
    const index = navItems.findIndex((it) => it.id === drawerItem.id);
    if (index === -1) return null;
    return { index, total: navItems.length };
  }, [drawerItem, navItems]);

  const handleDrawerNavigate = useCallback((direction: 1 | -1) => {
    if (!drawerItem) return;
    const index = navItems.findIndex((it) => it.id === drawerItem.id);
    if (index === -1) return;
    const next = navItems[index + direction];
    if (next) openDrawer(next);
  }, [drawerItem, navItems, openDrawer]);

  // Авто-автоширина при первом открытии представления: если пользователь ещё
  // не задавал ширины колонок (нет сохранённых colWidths), подгоняем колонки
  // под содержимое один раз после отрисовки строк. Дальше autoSize пишет
  // colWidths в settings → условие перестаёт выполняться, повторов нет.
  // Намеренно без cleanup-отмены таймера: частые ре-рендеры (новый ref у
  // settings.colWidths) не должны срывать единственный отложенный запуск.
  const autoSizedViewsRef = useRef<Set<string>>(new Set());
  // slug → тип поля: автоширине он нужен для потолков ширины и для того, чтобы
  // не переносить дату/число/статус. Заполняется ниже по ходу рендера
  // (mergedFields объявлены дальше по файлу) — эффектам этого хватает, они
  // выполняются уже после рендера.
  const columnTypesRef = useRef<Record<string, string>>({});
  const runAutoSize = useCallback((mode: 'content' | 'fit') => {
    tableSettings.autoSizeColumns({ mode, types: columnTypesRef.current });
  }, [tableSettings.autoSizeColumns]);
  // Режим подгонки связан с переносом строк: перенос выключен — колонки под
  // текст в одну строку; включён — ширина экрана делится между колонками.
  const autoSizeMode: 'content' | 'fit' = tableSettings.settings.wrapRows ? 'fit' : 'content';

  useEffect(() => {
    if (autoSizedViewsRef.current.has(view.id)) return;
    if (visibleItems.length === 0) return;
    // Уже есть пользовательские ширины — ничего не делаем, помечаем как обработанное.
    if (Object.keys(tableSettings.settings.colWidths).length > 0) {
      autoSizedViewsRef.current.add(view.id);
      return;
    }
    autoSizedViewsRef.current.add(view.id);
    window.setTimeout(() => runAutoSize(autoSizeMode), 80);
  }, [view.id, visibleItems.length, tableSettings.settings.colWidths, runAutoSize, autoSizeMode]);

  // Ширины режима 'fit' зависят от ширины экрана и состава колонок, поэтому их
  // приходится пересчитывать: при изменении набора видимых колонок и при
  // ресайзе окна. Ручной ресайз колонки переводит widthMode в 'manual' —
  // после него пересчёт больше не вмешивается.
  const widthMode = tableSettings.settings.widthMode;
  const visibleColsKey = tableSettings.orderedSlugs.join('|');
  useEffect(() => {
    if (widthMode !== 'fit') return;
    if (visibleItems.length === 0) return;
    const timer = window.setTimeout(() => runAutoSize('fit'), 120);
    return () => window.clearTimeout(timer);
  }, [widthMode, visibleColsKey, visibleItems.length, runAutoSize]);

  useEffect(() => {
    if (widthMode !== 'fit') return;
    let timer = 0;
    const onResize = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => runAutoSize('fit'), 200);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, [widthMode, runAutoSize]);

  const handleCreateView = async (value: CreateViewFormValue) => {
    try {
      // For multi-type table views, only include columns common to all selected types
      let tableColumns: Array<{ fieldSlug: string }> = [];
      if (value.viewType === 'table' && value.itemTypeIds.length > 1) {
        const fieldArrays = value.itemTypeIds.map((tid: string) => {
          const it = allItemTypes.find((t: ItemType) => t.id === tid);
          return it?.fields ?? [];
        });
        const commonSlugs = new Set(fieldArrays[0]?.map((f: FieldDefinition) => f.slug) ?? []);
        for (let i = 1; i < fieldArrays.length; i++) {
          const slugs = new Set(fieldArrays[i]!.map((f: FieldDefinition) => f.slug));
          for (const s of commonSlugs) {
            if (!slugs.has(s)) commonSlugs.delete(s);
          }
        }
        // Preserve field order from first type
        tableColumns = (fieldArrays[0] ?? [])
          .filter((f: FieldDefinition) => commonSlugs.has(f.slug))
          .map((f: FieldDefinition) => ({ fieldSlug: f.slug }));
      }

      const config =
        value.viewType === 'kanban'
          ? {
              kind: 'kanban' as const,
              columnField: value.columnField!,
              ...(value.columnFieldPath && value.columnFieldPath.length > 0
                ? { columnFieldPath: value.columnFieldPath }
                : {}),
              visibleColumnValues: [],
              filters: value.filters,
              cardTitleField: value.cardTitleField!,
              cardSubtitleField: value.cardSubtitleField,
              cardExtraFields: [],
            }
          : { kind: 'table' as const, columns: tableColumns, filters: value.filters, sorts: [] };

      const created = await createView(spaceId, {
        itemTypeIds: value.itemTypeIds,
        name: value.name,
        icon: value.icon,
        viewType: value.viewType,
        config,
        isDefault: views.length === 0,
      });
      setViews((prev) => [...prev, created]);
      setActiveViewId(created.id);
      setShowCreateView(false);
      message.success('Представление создано');
      emitSpacesChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось создать представление');
    }
  };

  const handleArchiveView = async (viewId: string) => {
    try {
      await updateView(spaceId, viewId, { isArchived: true });
      setViews((prev) => prev.filter((v) => v.id !== viewId));
      if (activeViewId === viewId) {
        setActiveViewId(views.find((v) => v.id !== viewId)?.id ?? null);
      }
      emitSpacesChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось архивировать представление');
    }
  };

  const handleDuplicateView = async (viewId: string) => {
    try {
      const newView = await duplicateView(spaceId, viewId);
      // view.config (колонки/фильтры/сортировки/группировка) уже скопирован на бэкенде.
      // Локальные per-view настройки (фильтры/сортировки/поиск + ширины/порядок/закрепление
      // колонок таблицы) живут в localStorage и завязаны на viewId — переносим их вручную,
      // иначе у дублированного представления будет дефолтное состояние.
      setFiltersByView((prev) => (prev[viewId] ? { ...prev, [newView.id]: prev[viewId] } : prev));
      setSortsByView((prev) => (prev[viewId] ? { ...prev, [newView.id]: prev[viewId] } : prev));
      setSearchByView((prev) => (prev[viewId] ? { ...prev, [newView.id]: prev[viewId] } : prev));
      try {
        const tableSettingsRaw = localStorage.getItem(`lens:table:${spaceId}:${viewId}`);
        if (tableSettingsRaw) {
          // Копируем настройки таблицы на новый viewId: localStorage-кэш + сервер.
          writePref(`table:${spaceId}:${newView.id}`, JSON.parse(tableSettingsRaw));
        }
      } catch { /* quota / unavailable / битый JSON */ }
      setViews((prev) => [...prev, newView]);
      setActiveViewId(newView.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось дублировать представление');
    }
  };

  // M11.6: persist link_aggregate-колонок в view.config.
  const persistViewColumns = useCallback(
    async (nextColumns: unknown[]) => {
      const currentConfig = (view.config as Record<string, unknown> | null) ?? {};
      const prevColumns = Array.isArray(currentConfig.columns)
        ? (currentConfig.columns as unknown[])
        : [];
      // Если до сохранения у view не было явного списка колонок, viewVisibleSlugs
      // считал, что показываются все поля. После записи непустого массива он начнёт
      // ограничивать таблицу только тем, что в нём перечислено — поэтому при первом
      // переходе из «пусто» в «не пусто» нужно засеять baseline-список реальных
      // полей, иначе они исчезнут из таблицы (см. issue: «когда добавляю атрибут
      // справочника, пропадают все другие»).
      let columnsToPersist = nextColumns;
      if (prevColumns.length === 0 && nextColumns.length > 0) {
        const baseline = mergedFields.map((f) => ({ fieldSlug: f.slug }));
        columnsToPersist = [...baseline, ...nextColumns];
      }
      const nextConfig = { ...currentConfig, columns: columnsToPersist };
      const updated = await updateView(spaceId, view.id, { config: nextConfig });
      setViews((prev) => prev.map((v) => (v.id === view.id ? updated : v)));
    },
    [spaceId, view.id, view.config, mergedFields],
  );

  // Итоги по числовым колонкам (sum/avg/min/max) — читаем из view.config.columns.
  const aggregateBySlug = useMemo(() => {
    const m = new Map<string, 'sum' | 'avg' | 'min' | 'max'>();
    const cols = Array.isArray((view.config as { columns?: unknown[] } | null)?.columns)
      ? (view.config as { columns: unknown[] }).columns
      : [];
    for (const c of cols) {
      if (c && typeof c === 'object') {
        const o = c as Record<string, unknown>;
        if (
          (o.kind === 'field' || o.kind === undefined) &&
          typeof o.fieldSlug === 'string' &&
          (o.aggregate === 'sum' || o.aggregate === 'avg' || o.aggregate === 'min' || o.aggregate === 'max')
        ) {
          m.set(o.fieldSlug, o.aggregate);
        }
      }
    }
    return m;
  }, [view.config]);

  // Установить/снять агрегат для числовой колонки. Пишем прямо в view.config.columns,
  // не через persistViewColumns: его baseline-prepend сработал бы при пустом списке
  // и продублировал бы поля.
  const handleSetColumnAggregate = useCallback(
    async (fieldSlug: string, agg: 'sum' | 'avg' | 'min' | 'max' | null) => {
      const currentConfig = (view.config as Record<string, unknown> | null) ?? {};
      const prevColumns = Array.isArray(currentConfig.columns)
        ? (currentConfig.columns as Array<Record<string, unknown>>)
        : [];
      let nextColumns: Array<Record<string, unknown>>;
      if (prevColumns.length === 0) {
        // Засеваем baseline всех полей, проставляя агрегат на нужное.
        nextColumns = mergedFields.map((f) =>
          f.slug === fieldSlug
            ? { fieldSlug: f.slug, ...(agg ? { aggregate: agg } : {}) }
            : { fieldSlug: f.slug },
        );
      } else {
        let found = false;
        nextColumns = prevColumns.map((c) => {
          const isFieldCol =
            c && (c.kind === 'field' || c.kind === undefined) && c.fieldSlug === fieldSlug;
          if (!isFieldCol) return c;
          found = true;
          const rest = { ...c };
          delete (rest as Record<string, unknown>).aggregate;
          return agg ? { ...rest, aggregate: agg } : rest;
        });
        if (!found) {
          nextColumns = [...nextColumns, { fieldSlug, ...(agg ? { aggregate: agg } : {}) }];
        }
      }
      try {
        const nextConfig = { ...currentConfig, columns: nextColumns };
        const updated = await updateView(spaceId, view.id, { config: nextConfig });
        setViews((prev) => prev.map((v) => (v.id === view.id ? updated : v)));
      } catch (err) {
        void message.error(err instanceof ApiError ? err.message : 'Не удалось сохранить итог колонки');
      }
    },
    [view.config, view.id, spaceId, mergedFields],
  );

  const handleAddLinkAggregateColumn = useCallback(
    async (cfg: Omit<LinkAggregateColumnConfig, 'key'>) => {
      const key = `link_${
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2, 10)
      }`;
      const currentColumns = Array.isArray((view.config as { columns?: unknown[] } | null)?.columns)
        ? ((view.config as { columns: unknown[] }).columns as unknown[])
        : [];
      await persistViewColumns([...currentColumns, { ...cfg, key }]);
      // Перезагрузка не нужна — useEffect на load() сработает после смены view.config
      // через setViews → новый объект view → пересборка load deps.
    },
    [view.config, persistViewColumns],
  );

  const handleUpdateLinkAggregateColumn = useCallback(
    async (columnKey: string, cfg: Omit<LinkAggregateColumnConfig, 'key'>) => {
      const currentColumns = Array.isArray((view.config as { columns?: unknown[] } | null)?.columns)
        ? ((view.config as { columns: unknown[] }).columns as Array<Record<string, unknown>>)
        : [];
      const next = currentColumns.map((c) =>
        c && c.kind === 'link_aggregate' && c.key === columnKey
          ? { ...c, ...cfg, key: columnKey }
          : c,
      );
      await persistViewColumns(next);
    },
    [view.config, persistViewColumns],
  );

  const handleAddViewComputedColumn = useCallback(
    async (cfg: Omit<ViewComputedColumnConfig, 'key'>) => {
      const key =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2, 10);
      const currentColumns = Array.isArray((view.config as { columns?: unknown[] } | null)?.columns)
        ? ((view.config as { columns: unknown[] }).columns as unknown[])
        : [];
      await persistViewColumns([...currentColumns, { ...cfg, key }]);
    },
    [view.config, persistViewColumns],
  );

  const handleRemoveLinkAggregateColumn = useCallback(
    async (columnKey: string) => {
      const currentColumns = Array.isArray((view.config as { columns?: unknown[] } | null)?.columns)
        ? ((view.config as { columns: unknown[] }).columns as Array<Record<string, unknown>>)
        : [];
      const next = currentColumns.filter(
        (c) => !(c && c.kind === 'link_aggregate' && c.key === columnKey),
      );
      try {
        await persistViewColumns(next);
      } catch (err) {
        void message.error(err instanceof ApiError ? err.message : 'Не удалось удалить колонку');
      }
    },
    [view.config, persistViewColumns],
  );

  const handleSubmitLinkAggregateColumn = useCallback(
    async (cfg: Omit<LinkAggregateColumnConfig, 'key'>) => {
      if (editingLinkAggregateColumn) {
        await handleUpdateLinkAggregateColumn(editingLinkAggregateColumn.key, cfg);
        setEditingLinkAggregateColumn(null);
      } else {
        await handleAddLinkAggregateColumn(cfg);
      }
    },
    [editingLinkAggregateColumn, handleAddLinkAggregateColumn, handleUpdateLinkAggregateColumn],
  );

  const handleRemoveViewComputedColumn = useCallback(
    async (columnKey: string) => {
      const currentColumns = Array.isArray((view.config as { columns?: unknown[] } | null)?.columns)
        ? ((view.config as { columns: unknown[] }).columns as Array<Record<string, unknown>>)
        : [];
      const next = currentColumns.filter(
        (c) => !(c && c.kind === 'view_computed' && c.key === columnKey),
      );
      try {
        await persistViewColumns(next);
      } catch (err) {
        void message.error(err instanceof ApiError ? err.message : 'Не удалось удалить колонку');
      }
    },
    [view.config, persistViewColumns],
  );

  const handleAddReferenceAttributeColumn = useCallback(
    async (cfg: Omit<ReferenceAttributeColumnConfig, 'key'>) => {
      const key =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2, 10);
      const currentColumns = Array.isArray((view.config as { columns?: unknown[] } | null)?.columns)
        ? ((view.config as { columns: unknown[] }).columns as unknown[])
        : [];
      await persistViewColumns([...currentColumns, { ...cfg, key }]);
    },
    [view.config, persistViewColumns],
  );

  const handleRemoveReferenceAttributeColumn = useCallback(
    async (columnKey: string) => {
      const currentColumns = Array.isArray((view.config as { columns?: unknown[] } | null)?.columns)
        ? ((view.config as { columns: unknown[] }).columns as Array<Record<string, unknown>>)
        : [];
      const next = currentColumns.filter(
        (c) => !(c && c.kind === 'reference_attribute' && c.key === columnKey),
      );
      try {
        await persistViewColumns(next);
      } catch (err) {
        void message.error(err instanceof ApiError ? err.message : 'Не удалось удалить колонку');
      }
    },
    [view.config, persistViewColumns],
  );

  const handleUpdateReferenceAttributeColumn = useCallback(
    async (columnKey: string, cfg: Omit<ReferenceAttributeColumnConfig, 'key'>) => {
      const currentColumns = Array.isArray((view.config as { columns?: unknown[] } | null)?.columns)
        ? ((view.config as { columns: unknown[] }).columns as Array<Record<string, unknown>>)
        : [];
      const next = currentColumns.map((c) =>
        c && c.kind === 'reference_attribute' && c.key === columnKey
          ? { ...c, ...cfg, key: columnKey }
          : c,
      );
      await persistViewColumns(next);
    },
    [view.config, persistViewColumns],
  );

  const handleSubmitReferenceAttributeColumn = useCallback(
    async (cfg: Omit<ReferenceAttributeColumnConfig, 'key'>) => {
      if (editingReferenceAttributeColumn) {
        await handleUpdateReferenceAttributeColumn(editingReferenceAttributeColumn.key, cfg);
        setEditingReferenceAttributeColumn(null);
      } else {
        await handleAddReferenceAttributeColumn(cfg);
      }
    },
    [
      editingReferenceAttributeColumn,
      handleAddReferenceAttributeColumn,
      handleUpdateReferenceAttributeColumn,
    ],
  );

  // Предзагружаем справочники, на которые ссылаются reference_attribute-колонки —
  // иначе при первом рендере таблицы значения будут пусты до открытия фильтра.
  // Обходим всю цепочку path, грузим каждый промежуточный справочник.
  useEffect(() => {
    if (referenceAttributeColumns.length === 0) return;
    const seenDictIds = new Set<string>();
    for (const col of referenceAttributeColumns) {
      const refField = mergedFields.find((f) => f.slug === col.referenceFieldSlug);
      if (!refField) continue;
      const rootDictId = (refField.config as { dictionaryId?: string } | null)?.dictionaryId;
      if (!rootDictId) continue;
      if (!seenDictIds.has(rootDictId)) {
        seenDictIds.add(rootDictId);
        void ensureReferenceOptions(refField);
      }
      const path = getRefAttrPath(col);
      const chain = collectRefAttrDictionaryIds(rootDictId, path, dictionaryColumnsById);
      for (const dictId of chain) {
        if (seenDictIds.has(dictId)) continue;
        seenDictIds.add(dictId);
        void ensureReferenceOptions({ config: { dictionaryId: dictId } } as unknown as import('@lens/shared').FieldDefinition);
      }
    }
  }, [referenceAttributeColumns, mergedFields, ensureReferenceOptions, dictionaryColumnsById]);

  // Аналогично для канбана с группировкой по атрибуту справочника (todos.md #5):
  // если в config задан columnFieldPath, нужно подгрузить корневой и промежуточные справочники.
  useEffect(() => {
    if (!activeView || activeView.viewType !== 'kanban') return;
    const cfg = activeView.config as unknown as KanbanViewConfig;
    const path = cfg.columnFieldPath;
    if (!path || path.length === 0) return;
    const refField = mergedFields.find((f) => f.slug === cfg.columnField);
    if (!refField || refField.fieldType !== 'reference') return;
    const rootDictId = (refField.config as { dictionaryId?: string } | null)?.dictionaryId;
    if (!rootDictId) return;
    void ensureReferenceOptions(refField);
    const chain = collectRefAttrDictionaryIds(rootDictId, path, dictionaryColumnsById);
    for (const dictId of chain) {
      void ensureReferenceOptions({
        config: { dictionaryId: dictId },
      } as unknown as FieldDefinition);
    }
  }, [activeView, mergedFields, ensureReferenceOptions, dictionaryColumnsById]);

  const allFields = mergedFields;
  const fieldBySlug = useMemo(() => {
    const map = new Map<string, FieldDefinition>();
    allFields.forEach((f) => map.set(f.slug, f));
    return map;
  }, [allFields]);
  // Держим карту типов актуальной для автоширины (см. columnTypesRef выше).
  columnTypesRef.current = useMemo(() => {
    const types: Record<string, string> = {};
    allFields.forEach((f) => { types[f.slug] = f.fieldType; });
    return types;
  }, [allFields]);

  // Виртуальное поле «Тип записи» для FilterEditor в multi-type views.
  // В EAV не хранится — фильтр обрабатывается в query-builder напрямую по items.item_type_id.
  const filterFields = useMemo<FieldDefinition[]>(() => {
    const isMultiType = (view.itemTypeIds ?? []).length > 1;
    const base: FieldDefinition[] = isMultiType
      ? (() => {
          const options = (view.itemTypeIds ?? [])
            .map((tid) => allItemTypes.find((t) => t.id === tid))
            .filter((t): t is ItemType => Boolean(t))
            .map((t) => ({ value: t.id, label: t.name }));
          const synthetic: FieldDefinition = {
            id: '__item_type__',
            itemTypeId: '00000000-0000-0000-0000-000000000000',
            name: 'Тип записи',
            slug: '_item_type',
            fieldType: 'select',
            config: { options },
            required: true,
            sortOrder: -1,
            createdAt: '',
            updatedAt: '',
          };
          return [synthetic, ...allFields];
        })()
      : [...allFields];

    // view_computed колонки доступны для фильтра/сортировки как синтетические
    // FieldDefinition'ы со slug'ом `_vc:{key}`. Бэк применяет их в JS поверх SQL-результата.
    const vcSynthetic: FieldDefinition[] = viewComputedColumns.map((col) => ({
      id: `__vc__${col.key}`,
      itemTypeId: '00000000-0000-0000-0000-000000000000',
      name: col.label,
      slug: viewComputedColumnSlug(col.key),
      fieldType: col.returnType === 'number' ? 'number' : 'text',
      config: {},
      required: false,
      sortOrder: 9999,
      createdAt: '',
      updatedAt: '',
    }));

    // Правила связей как синтетические поля фильтра (slug `_lr:{id}`).
    // Операторы: is_empty / is_not_empty — обрабатываются в query-builder
    // через JOIN на item_links.created_by_rule_id (двунаправленно).
    const lrSynthetic: FieldDefinition[] = [];
    for (const typeId of (view.itemTypeIds ?? [])) {
      for (const rule of (linkRulesByType[typeId] ?? [])) {
        lrSynthetic.push({
          id: `__lr__${rule.id}`,
          itemTypeId: '00000000-0000-0000-0000-000000000000',
          name: `Связь: ${rule.name}`,
          slug: `_lr:${rule.id}`,
          fieldType: 'text',
          config: { _isLinkRule: true },
          required: false,
          sortOrder: 99999,
          createdAt: '',
          updatedAt: '',
        });
      }
    }

    // Link_aggregate колонки как синтетические поля фильтра (slug = col.key).
    // Фильтрация клиентская — значения приходят в linkedAggregatesMap.
    const laSynthetic: FieldDefinition[] = linkAggregateColumns.map((col) => ({
      id: `__la__${col.key}`,
      itemTypeId: '00000000-0000-0000-0000-000000000000',
      name: col.label,
      slug: col.key,
      fieldType: (col.mode === 'count' || col.mode === 'sum' || col.mode === 'avg') ? 'number' : 'text',
      config: {},
      required: false,
      sortOrder: 99999,
      createdAt: '',
      updatedAt: '',
    }));

    // Reference-attribute колонки как синтетические поля фильтра (slug `_refattr:{key}`).
    // Фильтрация клиентская — значения берутся из dictionaryEntryValues.
    // fieldType и config.options берём из dictionaryColumnsById, чтобы показывать
    // правильный виджет фильтра (выпадающий список для select/multi_select и т.п.).
    const raSynthetic: FieldDefinition[] = referenceAttributeColumns.map((col) => {
      const refField = allFields.find((f) => f.slug === col.referenceFieldSlug);
      const dictionaryId = (refField?.config as { dictionaryId?: string } | null)?.dictionaryId;
      // Тип берём с финальной (листовой) колонки в цепочке.
      const dictCol = dictionaryId
        ? getFinalRefAttrColumn(dictionaryId, getRefAttrPath(col), dictionaryColumnsById)
        : undefined;
      const columnType = dictCol?.columnType ?? 'text';
      type FT = FieldDefinition['fieldType'];
      const fieldTypeMap: Record<string, FT> = {
        text: 'text', textarea: 'text', number: 'number', date: 'date',
        boolean: 'boolean', select: 'select', multi_select: 'multi_select', url: 'url',
        reference: 'reference',
      };
      const fieldType: FT = fieldTypeMap[columnType] ?? 'text';
      // Для select/multi_select — options колонки справочника (выпадашка по фикс. вариантам).
      // Для reference-листа — dictionaryId связанного справочника, чтобы фильтр вёл себя
      // как обычное reference-поле (выпадашка со значениями справочника, фильтр по id).
      let config: Record<string, unknown> = {};
      if (columnType === 'select' || columnType === 'multi_select') {
        config = (dictCol?.config as Record<string, unknown>) ?? {};
      } else if (columnType === 'reference') {
        const nestedDictId = (dictCol?.config as { dictionaryId?: string } | null)?.dictionaryId;
        if (nestedDictId) config = { dictionaryId: nestedDictId };
      }
      return {
        id: `__ra__${col.key}`,
        itemTypeId: '00000000-0000-0000-0000-000000000000',
        name: col.label,
        slug: referenceAttributeColumnSlug(col.key),
        fieldType,
        config,
        required: false,
        sortOrder: 99999,
        createdAt: '',
        updatedAt: '',
      };
    });

    // Системные мета-поля — доступны как для фильтра, так и для сортировки.
    // На бэке их обрабатывает query-builder напрямую по items.* и users.display_name.
    const metaSynthetic: FieldDefinition[] = [
      {
        id: '__created_at__',
        itemTypeId: '00000000-0000-0000-0000-000000000000',
        name: 'Создано',
        slug: '_created_at',
        fieldType: 'date',
        config: {},
        required: false,
        sortOrder: -1,
        createdAt: '',
        updatedAt: '',
      },
      {
        id: '__updated_at__',
        itemTypeId: '00000000-0000-0000-0000-000000000000',
        name: 'Обновлено',
        slug: '_updated_at',
        fieldType: 'date',
        config: {},
        required: false,
        sortOrder: -1,
        createdAt: '',
        updatedAt: '',
      },
      {
        id: '__created_by__',
        itemTypeId: '00000000-0000-0000-0000-000000000000',
        name: 'Кто создал',
        slug: '_created_by',
        // text — оператор contains по display_name; eq/in по uuid также поддержан
        // на бэке, но вводить uuid руками пользователь не будет, так что в UI работает
        // contains/is_empty.
        fieldType: 'text',
        config: {},
        required: false,
        sortOrder: -1,
        createdAt: '',
        updatedAt: '',
      },
    ];

    return [...base, ...metaSynthetic, ...vcSynthetic, ...lrSynthetic, ...laSynthetic, ...raSynthetic];
  }, [view.itemTypeIds, allItemTypes, allFields, viewComputedColumns, linkRulesByType, linkAggregateColumns, referenceAttributeColumns, dictionaryColumnsById]);

  const handleColumnSort = (slug: string) => {
    setUserSorts((prev) => {
      const existing = prev.find((s) => s.fieldSlug === slug);
      if (!existing) return [{ fieldSlug: slug, direction: 'asc' as const }];
      if (existing.direction === 'asc') return prev.map((s) => s.fieldSlug === slug ? { ...s, direction: 'desc' as const } : s);
      return prev.filter((s) => s.fieldSlug !== slug);
    });
  };

  // ─── Фильтр из заголовка колонки (автофильтр) ────────────────────────────
  // Список значений берём из описания поля или справочника, а не из загруженных
  // строк: иначе после первого же фильтра список схлопнется до отобранных
  // значений и вернуть остальные будет нечем.
  const COLUMN_FILTER_TYPES = ['select', 'multi_select', 'boolean', 'reference', 'user'];

  const columnFilterOptions = (field: FieldDefinition): Array<{ value: string; label: string }> => {
    if (field.fieldType === 'boolean') {
      return [{ value: 'true', label: 'Да' }, { value: 'false', label: 'Нет' }];
    }
    if (field.fieldType === 'select' || field.fieldType === 'multi_select') {
      const options = (field.config as { options?: Array<{ value: string; label: string }> } | null)?.options;
      return options ?? [];
    }
    if (field.fieldType === 'user') return userOptions;
    const dictionaryId = (field.config as { dictionaryId?: string } | null)?.dictionaryId;
    return dictionaryId ? (dictionaryOptionsById[dictionaryId] ?? []) : [];
  };

  /** Оператор, которым автофильтр пишет условие для этого типа поля. */
  const columnFilterOperator = (field: FieldDefinition): string =>
    field.fieldType === 'boolean' ? 'eq' : field.fieldType === 'multi_select' ? 'contains_any' : 'in';

  /**
   * Операторы, которые считаем «фильтром по значениям колонки». Условие,
   * поставленное через панель фильтров (`eq`), воронка тоже показывает и
   * заменяет — иначе два условия по одной колонке сложились бы по И и таблица
   * оказалась бы пустой.
   */
  const columnFilterOps = (field: FieldDefinition): string[] =>
    field.fieldType === 'boolean'
      ? ['eq']
      : field.fieldType === 'multi_select'
        ? ['contains_any', 'eq']
        : ['in', 'eq'];

  const columnFilterSelected = (field: FieldDefinition): string[] | null => {
    const ops = columnFilterOps(field);
    const leaf = userFilters.find((f) => f.fieldSlug === field.slug && ops.includes(f.operator));
    if (!leaf) return null;
    if (field.fieldType === 'boolean') return [leaf.value === true ? 'true' : 'false'];
    if (Array.isArray(leaf.value)) return leaf.value.map((v) => String(v));
    return leaf.value === null || leaf.value === undefined ? null : [String(leaf.value)];
  };

  const applyColumnFilter = (field: FieldDefinition, values: string[] | null): void => {
    const operator = columnFilterOperator(field);
    const ops = columnFilterOps(field);
    const others = userFilters.filter((f) => !(f.fieldSlug === field.slug && ops.includes(f.operator)));
    if (!values || values.length === 0) {
      setUserFilters(others);
      return;
    }
    const value: unknown = field.fieldType === 'boolean' ? values[0] === 'true' : values;
    setUserFilters([
      ...others,
      {
        fieldSlug: field.slug,
        operator,
        value,
        ...(others.length > 0 ? { combinator: 'and' as const } : {}),
      } as FilterLeaf,
    ]);
  };

  // Build columns in the order defined by tableSettings
  const fields = useMemo(() =>
    tableSettings.orderedSlugs.map((s) => fieldBySlug.get(s)).filter(Boolean) as FieldDefinition[],
    [tableSettings.orderedSlugs, fieldBySlug],
  );

  const baseDataColumns: ColumnsType<ItemRow> = [
    ...fields.map((field) => {
      const currentSort = userSorts.find((s) => s.fieldSlug === field.slug);
      const savedWidth = tableSettings.settings.colWidths[field.slug];
      // Every column gets an explicit width so <Resizable> always wraps the <th>
      const colWidth = savedWidth ?? (field.fieldType === 'boolean' ? 80 : 150);
      const isPinned = tableSettings.settings.pinnedCols.includes(field.slug);
      const isNumericField = field.fieldType === 'number' || field.fieldType === 'computed';
      return {
        title: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%' }}>
            <span
              style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}
              onClick={() => {
                if (Date.now() < suppressHeaderSortUntil) return;
                handleColumnSort(field.slug);
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{field.name}</span>
              {currentSort && (
                <span style={{ fontSize: 10, color: 'var(--color-primary)' }}>
                  {currentSort.direction === 'asc' ? ' ▲' : ' ▼'}
                </span>
              )}
            </span>
            {COLUMN_FILTER_TYPES.includes(field.fieldType) && (
              <ColumnFilterPopover
                title={field.name}
                options={columnFilterOptions(field)}
                selected={columnFilterSelected(field)}
                onApply={(values) => applyColumnFilter(field, values)}
                onOpen={() => {
                  if (field.fieldType === 'reference' || field.fieldType === 'user') {
                    void ensureReferenceOptions(field);
                  }
                }}
              />
            )}
          </span>
        ),
        key: field.slug,
        dataIndex: ['fields', field.slug] as string[],
        width: colWidth,
        align: isNumericField ? ('right' as const) : undefined,
        fixed: isPinned ? ('left' as const) : undefined,
        onHeaderCell: () => ({
          width: colWidth,
          'data-col-slug': field.slug,
          onResize: (_e: React.SyntheticEvent, { size }: { size: { width: number } }) => {
            tableSettings.setColWidth(field.slug, size.width);
          },
        }),
        render: (_: unknown, record: ItemRow) => {
          const cell = (
            <CellEditor
              value={record.fields[field.slug] ?? null}
              field={field}
              itemId={record.id}
              onUploadImage={handleUploadImage}
              onSave={(val) => void handleFieldSave(record.id, field.slug, val)}
              resolvedRefs={resolvedRefs}
              referenceOptions={(() => {
                return referenceOptionsForField(field);
              })()}
              loadReferenceOptions={ensureReferenceOptions}
              onResolvedRefsLoaded={mergeResolvedRefs}
              userGroups={userGroups}
            />
          );
          // Рядом с названием — значок «есть доска».
          const board = field.slug === hermesTitleField?.slug ? whiteboardBoards[record.id] : undefined;
          if (!board) return cell;
          return (
            <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <div style={{ flex: '1 1 auto', minWidth: 0 }}>{cell}</div>
              <WhiteboardBadge board={board} />
            </div>
          );
        },
      };
    }),
    // Pseudo-колонка «Ключ» (displayKey) — включаема/скрываема через chooser.
    (() => {
      const slug = '_display_key';
      const w = tableSettings.settings.colWidths[slug] ?? 110;
      return {
        title: 'Ключ',
        key: slug,
        width: w,
        onHeaderCell: () => ({
          width: w,
          'data-col-slug': slug,
          onResize: (_e: React.SyntheticEvent, { size }: { size: { width: number } }) => {
            tableSettings.setColWidth(slug, size.width);
          },
        }),
        render: (_: unknown, record: ItemRow) => {
          const displayKey = (record as { displayKey?: string | null }).displayKey;
          if (!displayKey) return <span style={{ color: '#bbb' }}>—</span>;
          return (
            <Tag
              style={{
                margin: 0,
                fontFamily: 'monospace',
                fontSize: 11,
                cursor: 'copy',
                background: 'var(--color-bg-layout)',
                color: 'var(--color-text-secondary)',
                border: '1px solid var(--color-border)',
              }}
              onClick={(e) => {
                e.stopPropagation();
                const link = space?.slug
                  ? `${window.location.origin}/c/${space.slug}/${displayKey}`
                  : displayKey;
                void navigator.clipboard?.writeText(link);
                void message.success('Ссылка скопирована');
              }}
            >
              {displayKey}
            </Tag>
          );
        },
      };
    })(),
    // Pseudo-колонка «Тип записи» — доступна только для multi-type views.
    // Включаемая/отключаемая через стандартный chooser колонок.
    ...(!isSingleType ? [(() => {
      const slug = '_item_type';
      const w = tableSettings.settings.colWidths[slug] ?? 140;
      const currentSort = userSorts.find((s) => s.fieldSlug === slug);
      return {
        title: (
          <span
            style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            onClick={() => {
              if (Date.now() < suppressHeaderSortUntil) return;
              handleColumnSort(slug);
            }}
          >
            Тип записи
            {currentSort && (
              <span style={{ fontSize: 10, color: 'var(--color-primary)' }}>
                {currentSort.direction === 'asc' ? ' ▲' : ' ▼'}
              </span>
            )}
          </span>
        ),
        key: slug,
        width: w,
        onHeaderCell: () => ({
          width: w,
          'data-col-slug': slug,
          onResize: (_e: React.SyntheticEvent, { size }: { size: { width: number } }) => {
            tableSettings.setColWidth(slug, size.width);
          },
        }),
        render: (_: unknown, record: ItemRow) => {
          const it = allItemTypes.find((t) => t.id === record.itemTypeId);
          if (!it) return <span style={{ color: '#bbb' }}>—</span>;
          return <Tag>{it.name}</Tag>;
        },
      };
    })()] : []),
    // Метаданные: «Создано / Обновлено / Кто создал». По умолчанию скрыты,
    // включаются через chooser колонок.
    ...(['_created_at', '_updated_at', '_created_by'] as const).map((slug) => {
      const w = tableSettings.settings.colWidths[slug] ?? (slug === '_created_by' ? 160 : 150);
      const label = slug === '_created_at' ? 'Создано' : slug === '_updated_at' ? 'Обновлено' : 'Кто создал';
      const currentSort = userSorts.find((s) => s.fieldSlug === slug);
      return {
        title: (
          <span
            style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            onClick={() => {
              if (Date.now() < suppressHeaderSortUntil) return;
              handleColumnSort(slug);
            }}
          >
            {label}
            {currentSort && (
              <span style={{ fontSize: 10, color: 'var(--color-primary)' }}>
                {currentSort.direction === 'asc' ? ' ▲' : ' ▼'}
              </span>
            )}
          </span>
        ),
        key: slug,
        width: w,
        onHeaderCell: () => ({
          width: w,
          'data-col-slug': slug,
          onResize: (_e: React.SyntheticEvent, { size }: { size: { width: number } }) => {
            tableSettings.setColWidth(slug, size.width);
          },
        }),
        render: (_: unknown, record: ItemRow) => {
          if (slug === '_created_by') {
            const author = (record as { createdByDisplayName?: string | null }).createdByDisplayName;
            return author ? <span>{author}</span> : <span style={{ color: '#bbb' }}>—</span>;
          }
          const iso = slug === '_created_at' ? record.createdAt : record.updatedAt;
          if (!iso) return <span style={{ color: '#bbb' }}>—</span>;
          return (
            <Tooltip title={new Date(iso).toLocaleString('ru-RU')}>
              <span style={{ color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                {formatRelativeTime(iso)}
              </span>
            </Tooltip>
          );
        },
      };
    }),
    // M11.6: link_aggregate-колонки из view.config. Значения приходят в
    // основном response (linkedAggregatesMap) — отдельных запросов не делаем.
    ...linkAggregateColumns.map((col) => {
      const w = tableSettings.settings.colWidths[col.key] ?? (col.mode === 'list' ? 220 : 80);
      const align: 'center' | 'left' = col.mode === 'list' ? 'left' : 'center';
      const currentSort = userSorts.find((s) => s.fieldSlug === col.key);
      return {
        title: (
          <Dropdown
            trigger={['contextMenu']}
            menu={{
              items: [
                { key: 'edit', icon: <EditOutlined />, label: 'Настроить колонку' },
                { key: 'delete', icon: <DeleteOutlined />, label: 'Удалить колонку', danger: true },
              ],
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();
                if (key === 'edit') {
                  setEditingLinkAggregateColumn(col);
                  setShowColSettings(false);
                  setLinkAggregateModalOpen(true);
                }
                if (key === 'delete') void handleRemoveLinkAggregateColumn(col.key);
              },
            }}
          >
            <span
              style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
              onClick={() => {
                if (Date.now() < suppressHeaderSortUntil) return;
                handleColumnSort(col.key);
              }}
            >
              {col.label}
              {currentSort && (
                <span style={{ fontSize: 10, color: 'var(--color-primary)' }}>
                  {currentSort.direction === 'asc' ? ' ▲' : ' ▼'}
                </span>
              )}
            </span>
          </Dropdown>
        ),
        key: col.key,
        width: w,
        align,
        onHeaderCell: () => ({
          width: w,
          'data-col-slug': col.key,
          onResize: (_e: React.SyntheticEvent, { size }: { size: { width: number } }) => {
            tableSettings.setColWidth(col.key, size.width);
          },
        }),
        render: (_: unknown, record: ItemRow) => {
          const value = linkedAggregatesMap[record.id]?.[col.key];
          if (col.mode === 'list') {
            const entries = Array.isArray(value) ? (value as LinkedAggregateListEntry[]) : [];
            if (entries.length === 0) return <span style={{ color: '#bbb' }}>—</span>;
            return (
              <AntSpace size={[4, 4]} wrap>
                {entries.map((entry) => {
                  const text = entry.title ?? entry.displayKey ?? entry.itemId.slice(0, 8);
                  return (
                    <Tooltip key={entry.itemId} title={entry.displayKey ?? text}>
                      <Tag
                        closable={space?.myRole !== 'viewer' && Boolean(entry.linkId)}
                        style={{ cursor: 'pointer', margin: 0 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLinkedItemClick(entry.itemId, entry.spaceId);
                        }}
                        onClose={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (entry.linkId) void handleRemoveLinkAggregateLink(record.id, entry.linkId);
                        }}
                      >
                        {text}
                      </Tag>
                    </Tooltip>
                  );
                })}
              </AntSpace>
            );
          }
          if (typeof value !== 'number' || value === null) {
            return <span style={{ color: '#bbb' }}>—</span>;
          }
          if (col.mode === 'count' && value === 0) {
            return <span style={{ color: '#bbb' }}>0</span>;
          }
          const display = col.mode === 'avg' ? Math.round(value * 100) / 100 : value;
          return <Tag>{display}</Tag>;
        },
      };
    }),
    ...viewComputedColumns.map((col) => {
      const slug = viewComputedColumnSlug(col.key);
      const w = tableSettings.settings.colWidths[slug] ?? col.width ?? 160;
      const currentSort = userSorts.find((s) => s.fieldSlug === slug);
      return {
        title: (
          <Dropdown
            trigger={['contextMenu']}
            menu={{
              items: [{ key: 'delete', icon: <DeleteOutlined />, label: 'Удалить колонку', danger: true }],
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();
                if (key === 'delete') void handleRemoveViewComputedColumn(col.key);
              },
            }}
          >
            <span
              style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
              onClick={() => {
                if (Date.now() < suppressHeaderSortUntil) return;
                handleColumnSort(slug);
              }}
            >
              {col.label}
              {currentSort?.direction === 'asc' && <span style={{ fontSize: 10 }}>▲</span>}
              {currentSort?.direction === 'desc' && <span style={{ fontSize: 10 }}>▼</span>}
            </span>
          </Dropdown>
        ),
        key: slug,
        width: w,
        fixed: col.pinned,
        onHeaderCell: () => ({
          width: w,
          'data-col-slug': slug,
          onResize: (_e: React.SyntheticEvent, { size }: { size: { width: number } }) => {
            tableSettings.setColWidth(slug, size.width);
          },
        }),
        render: (_: unknown, record: ItemRow) => {
          const value = viewComputedMap[record.id]?.[col.key];
          if (value === null || value === undefined) {
            return <span style={{ color: '#bbb' }}>—</span>;
          }
          if (col.returnType === 'number') {
            if (typeof value !== 'number') return <span style={{ color: '#bbb' }}>—</span>;
            return <span>{formatViewComputedNumber(value, col.numberDecimals ?? 0)}</span>;
          }
          return <span>{String(value)}</span>;
        },
      };
    }),
    // Reference-attribute колонки: показывают значение колонки справочника
    // у entry, на которую указывает reference-поле текущей записи.
    ...referenceAttributeColumns.map((col) => {
      const slug = referenceAttributeColumnSlug(col.key);
      const w = tableSettings.settings.colWidths[slug] ?? col.width ?? 160;
      const refField = mergedFields.find((f) => f.slug === col.referenceFieldSlug);
      const dictionaryId = (refField?.config as { dictionaryId?: string } | null)?.dictionaryId;
      const currentSort = userSorts.find((s) => s.fieldSlug === slug);
      return {
        title: (
          <Dropdown
            trigger={['contextMenu']}
            menu={{
              items: [{ key: 'delete', icon: <DeleteOutlined />, label: 'Удалить колонку', danger: true }],
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();
                if (key === 'delete') void handleRemoveReferenceAttributeColumn(col.key);
              },
            }}
          >
            <span
              style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
              onClick={() => {
                if (Date.now() < suppressHeaderSortUntil) return;
                handleColumnSort(slug);
              }}
            >
              {col.label}
              {currentSort && (
                <span style={{ fontSize: 10, color: 'var(--color-primary)' }}>
                  {currentSort.direction === 'asc' ? ' ▲' : ' ▼'}
                </span>
              )}
            </span>
          </Dropdown>
        ),
        key: slug,
        width: w,
        fixed: col.pinned,
        onHeaderCell: () => ({
          width: w,
          'data-col-slug': slug,
          onResize: (_e: React.SyntheticEvent, { size }: { size: { width: number } }) => {
            tableSettings.setColWidth(slug, size.width);
          },
        }),
        render: (_: unknown, record: ItemRow) => {
          if (!refField) return <span style={{ color: '#bbb' }}>— нет поля</span>;
          if (!dictionaryId) return <span style={{ color: '#bbb' }}>—</span>;
          const refValue = record.fields[col.referenceFieldSlug];
          if (refValue === null || refValue === undefined || refValue === '') {
            return <span style={{ color: '#bbb' }}>—</span>;
          }
          const ids: string[] = Array.isArray(refValue)
            ? (refValue as unknown[]).filter((v): v is string => typeof v === 'string')
            : typeof refValue === 'string' ? [refValue] : [];
          if (ids.length === 0) return <span style={{ color: '#bbb' }}>—</span>;
          const ctx: RefAttrCtx = {
            dictionaryEntryValues,
            dictionaryColumnsById,
            dictionaryOptionsById,
            resolvedRefs,
          };
          const text = renderRefAttrPath(ids, getRefAttrPath(col), dictionaryId, ctx);
          if (!text) return <span style={{ color: '#bbb' }}>—</span>;
          return <span>{text}</span>;
        },
      };
    }),
    // Jira-колонка попадает в общий список вместе с остальными — это включает
    // её в DnD-перетаскивание, чекбокс видимости и пин.
    ...(space?.jiraIntegrationEnabled ? [(() => {
      const slug = '_jira';
      // Динамическая ширина: подгоняем под самый длинный ключ в текущем наборе.
      // Tag font 11 → ~7px/символ + ~18px padding тега + ~16px padding ячейки.
      // Если пользователь сам ресайзил колонку — уважаем его настройку.
      const maxKeyLen = (response?.items ?? []).reduce((m, r) => {
        const k = (r as { jiraIssueKey?: string | null }).jiraIssueKey;
        return k && k.length > m ? k.length : m;
      }, 0);
      const autoW = maxKeyLen === 0
        ? 60
        : Math.min(220, Math.max(80, maxKeyLen * 7 + 40));
      const w = tableSettings.settings.colWidths[slug] ?? autoW;
      return {
        title: 'Jira',
        key: slug,
        width: w,
        onHeaderCell: () => ({
          width: w,
          'data-col-slug': slug,
          onResize: (_e: React.SyntheticEvent, { size }: { size: { width: number } }) => {
            tableSettings.setColWidth(slug, size.width);
          },
        }),
        render: (_: unknown, record: ItemRow) => {
          const jiraKey = (record as { jiraIssueKey?: string | null }).jiraIssueKey;
          if (!jiraKey) return <span style={{ color: '#bbb' }}>&mdash;</span>;
          return <JiraIssueLink jiraKey={jiraKey} compact />;
        },
      };
    })()] : []),
    // Колонка статуса оценки Hermes (как и Jira — попадает в общий список,
    // DnD/видимость/пин работают единообразно).
    ...(space?.hermesIntegrationEnabled ? [(() => {
      const slug = '_hermes';
      // Теперь в ячейке только тег статуса — кнопка вынесена в соседнюю
      // колонку `_hermes_run`, поэтому минимум определяется самым длинным
      // тегом («В очереди»), а не тегом вместе с кнопкой.
      const HERMES_MIN_W = 104;
      const w = Math.max(tableSettings.settings.colWidths[slug] ?? 120, HERMES_MIN_W);
      const META: Record<string, { text: string; color: string }> = {
        pending: { text: 'В очереди', color: 'default' },
        running: { text: 'Считается', color: 'blue' },
        completed: { text: 'Оценено', color: 'green' },
        failed: { text: 'Ошибка', color: 'red' },
        timeout: { text: 'Таймаут', color: 'orange' },
        error: { text: 'Ошибка', color: 'red' },
      };
      return {
        title: 'Оценка',
        key: slug,
        width: w,
        onHeaderCell: () => ({
          width: w,
          'data-col-slug': slug,
          onResize: (_e: React.SyntheticEvent, { size }: { size: { width: number } }) => {
            tableSettings.setColWidth(slug, size.width);
          },
        }),
        render: (_: unknown, record: ItemRow) => {
          const st = (record as { hermesStatus?: string | null }).hermesStatus;
          return st ? (
            <Tag color={(META[st] ?? { color: 'default' }).color}>{(META[st] ?? { text: st }).text}</Tag>
          ) : (
            <span style={{ color: '#bbb' }}>&mdash;</span>
          );
        },
      };
    })()] : []),
    // Кнопка запуска интерактивной оценки — своя колонка фиксированной ширины,
    // чтобы кнопки во всех строках стояли ровно одна под другой независимо от
    // того, есть ли у записи статус и насколько широк его тег.
    ...(space?.hermesIntegrationEnabled && space?.myRole !== 'viewer' ? [(() => {
      const runSlug = '_hermes_run';
      // Ширина под одну иконку; ресайз оставлен как у остальных колонок, но
      // ниже 44px не пускаем — там кнопку начинает резать границей.
      const runW = Math.max(tableSettings.settings.colWidths[runSlug] ?? 44, 44);
      return {
        title: '',
        key: runSlug,
        width: runW,
        onHeaderCell: () => ({
          width: runW,
          'data-col-slug': runSlug,
          onResize: (_e: React.SyntheticEvent, { size }: { size: { width: number } }) => {
            tableSettings.setColWidth(runSlug, size.width);
          },
        }),
        render: (_: unknown, record: ItemRow) => {
          const dk = (record as { displayKey?: string | null }).displayKey ?? undefined;
          return (
            <Tooltip title="Интерактивная оценка">
              <Button
                type="text"
                size="small"
                icon={<MessageOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  openHermesSession({
                    spaceId,
                    itemId: record.id,
                    itemKey: dk,
                    title: hermesRowTitle(record, dk),
                  });
                }}
              />
            </Tooltip>
          );
        },
      };
    })()] : []),
  ];

  const dataColumnsByKey = useMemo(
    () => new Map(baseDataColumns.map((column) => [String(column.key), column])),
    [baseDataColumns],
  );

  // Слаги, по которым активна группировка — их колонки не дублируем в строках
  // (значение и так в заголовке группы). P1-2.
  const groupedFieldSlugs = useMemo(
    () => new Set(tableSettings.settings.grouping.map((l) => l.fieldSlug)),
    [tableSettings.settings.grouping],
  );
  const orderedDataColumns = useMemo(
    () =>
      tableSettings.orderedSlugs
        // P1-1: мета-колонки скрыты по умолчанию, рендерим только явно включённые.
        .filter((slug) => isMetaColShown(slug))
        // P1-2: колонку поля, по которому идёт группировка, прячем из строк.
        .filter((slug) => !groupedFieldSlugs.has(slug))
        .map((slug) => dataColumnsByKey.get(slug))
        .filter((column): column is NonNullable<typeof column> => Boolean(column)),
    [dataColumnsByKey, tableSettings.orderedSlugs, isMetaColShown, groupedFieldSlugs],
  );

  // Колонки оценки закреплены справа рядом с действиями: при широкой таблице
  // кнопка запуска оценки иначе уезжает за горизонтальный скролл. AntD требует,
  // чтобы fixed:'right' колонки шли в конце массива — поэтому вынимаем их из
  // общего порядка и ставим последними перед колонкой действий, статус слева
  // от кнопки.
  const HERMES_FIXED = ['_hermes', '_hermes_run'];
  const hermesColumns = HERMES_FIXED
    .map((key) => orderedDataColumns.find((column) => String(column.key) === key))
    .filter((column): column is NonNullable<typeof column> => Boolean(column));

  const columns: ColumnsType<ItemRow> = [
    ...orderedDataColumns.filter((column) => !HERMES_FIXED.includes(String(column.key))),
    ...hermesColumns.map((column) => ({ ...column, fixed: 'right' as const })),
    {
      title: '',
      key: 'actions',
      width: space?.myRole === 'viewer' ? 40 : 80,
      fixed: 'right' as const,
      render: (_: unknown, record: ItemRow) => {
        const jiraKey = (record as { jiraIssueKey?: string | null }).jiraIssueKey;
        const isAdmin = space?.myRole === 'admin';
        const rulesForRecordType = linkRulesByType[record.itemTypeId];

        const rulesSubmenu: NonNullable<
          NonNullable<React.ComponentProps<typeof Dropdown>['menu']>['items']
        > = rulesForRecordType === undefined
          ? [{ key: '__loading__', label: 'Загрузка…', disabled: true }]
          : rulesForRecordType.length === 0
            ? [{ key: '__empty__', label: 'Нет правил для этого типа', disabled: true }]
            : rulesForRecordType.map((rule) => ({
                key: `rule:${rule.id}`,
                label: rule.name,
              }));

        const items: NonNullable<
          NonNullable<React.ComponentProps<typeof Dropdown>['menu']>['items']
        > = [
          { key: 'duplicate', icon: <CopyOutlined />, label: 'Дублировать запись' },
          ...(space?.hermesIntegrationEnabled
            ? [{ key: 'hermesEstimate', icon: <MessageOutlined />, label: 'Интерактивная оценка' }]
            : []),
          ...(jiraKey
            ? [{ key: 'openJira', icon: <LinkOutlined />, label: 'Открыть в Jira' }]
            : []),
          ...(isAdmin && rulesForRecordType && rulesForRecordType.length > 0
            ? [{
                key: 'applyRule',
                icon: <ApiOutlined />,
                label: 'Связать',
                children: rulesSubmenu,
              }]
            : []),
          { type: 'divider' as const },
          { key: 'delete', icon: <DeleteOutlined />, label: 'Удалить', danger: true },
        ];

        return (
          <AntSpace size={0}>
            <span className="row-action-open">
              <Tooltip title="Открыть карточку">
                <Button
                  size="small"
                  type="text"
                  icon={<ExpandAltOutlined />}
                  onClick={() => openDrawer(record)}
                  aria-label="Открыть карточку записи"
                  data-testid={`open-item-${record.id}`}
                />
              </Tooltip>
            </span>
            {space?.myRole !== 'viewer' && (
              <span className="row-action-open">
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  onOpenChange={(open) => {
                    if (open && isAdmin && rulesForRecordType === undefined) {
                      void ensureLinkRulesFor(record.itemTypeId);
                    }
                  }}
                  menu={{
                    items,
                    onClick: async ({ key, domEvent }) => {
                      domEvent.stopPropagation();
                      if (key === 'duplicate') {
                        handleOpenDuplicateSingle(record);
                        return;
                      }
                      if (key === 'hermesEstimate') {
                        const dk = (record as { displayKey?: string | null }).displayKey ?? undefined;
                        openHermesSession({
                          spaceId,
                          itemId: record.id,
                          itemKey: dk,
                          title: hermesRowTitle(record, dk),
                        });
                        return;
                      }
                      if (key === 'openJira' && jiraKey) {
                        window.open(`/api/jira/issues/${encodeURIComponent(jiraKey)}`, '_blank', 'noreferrer');
                        return;
                      }
                      if (key === 'delete') {
                        Modal.confirm({
                          title: 'Удалить запись?',
                          okText: 'Удалить',
                          okButtonProps: { danger: true },
                          cancelText: 'Отмена',
                          onOk: () => handleDelete(record.id),
                        });
                        return;
                      }
                      if (typeof key === 'string' && key.startsWith('rule:')) {
                        const ruleId = key.slice('rule:'.length);
                        const rule = rulesForRecordType?.find((r) => r.id === ruleId);
                        if (!rule) return;
                        Modal.confirm({
                          title: `Связать по правилу «${rule.name}»?`,
                          okText: 'Применить',
                          cancelText: 'Отмена',
                          onOk: async () => {
                            try {
                              setApplyingLinkRuleId(rule.id);
                              const result = await applyLinkRuleBulk(spaceId, record.itemTypeId, rule.id, {
                                itemIds: [record.id],
                              });
                              void message.success(
                                `Обработано: ${result.itemsProcessed}, создано связей: ${result.linksCreated}`,
                              );
                              void load();
                            } catch (err) {
                              void message.error(err instanceof ApiError ? err.message : 'Ошибка применения правила');
                            } finally {
                              setApplyingLinkRuleId(null);
                            }
                          },
                        });
                        return;
                      }
                    },
                  }}
                >
                  <Tooltip title="Ещё">
                    <Button
                      size="small"
                      type="text"
                      icon={<MoreOutlined />}
                      data-testid={`row-more-${record.id}`}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Tooltip>
                </Dropdown>
              </span>
            )}
          </AntSpace>
        );
      },
    },
  ];

  // ─── Экспорт в Excel (.xlsx) ──────────────────────────────────────────────
  // «Что вижу на экране, то и выгружаю»: видимые колонки в текущем порядке,
  // текущий отфильтрованный/отсортированный набор строк (visibleItems), значения —
  // человекочитаемые (label справочников/опций, Да/Нет, дата), а не сырые id.
  const handleExportXlsx = useCallback(() => {
    const slugNameMap = new Map(columnSettingsOptions.map((c) => [c.slug, c.name]));
    const laBySlug = new Map(linkAggregateColumns.map((c) => [c.key, c]));
    const vcBySlug = new Map(viewComputedColumns.map((c) => [viewComputedColumnSlug(c.key), c]));
    const raBySlug = new Map(referenceAttributeColumns.map((c) => [referenceAttributeColumnSlug(c.key), c]));

    // Колонка с кнопкой запуска оценки данных не несёт — в выгрузку не идёт.
    const exportSlugs = tableSettings.orderedSlugs.filter((s) => s !== 'actions' && s !== '_hermes_run');

    const cellText = (slug: string, record: ItemRow): string => {
      if (slug === '_display_key') return record.displayKey ?? '';
      if (slug === '_created_by') return (record as { createdByDisplayName?: string | null }).createdByDisplayName ?? '';
      if (slug === '_created_at') return record.createdAt ? new Date(record.createdAt).toLocaleString('ru-RU') : '';
      if (slug === '_updated_at') return record.updatedAt ? new Date(record.updatedAt).toLocaleString('ru-RU') : '';
      if (slug === '_jira') return (record as { jiraIssueKey?: string | null }).jiraIssueKey ?? '';
      if (slug === '_hermes') {
        // Зеркалим человекочитаемый label статуса оценки из грид-колонки «Оценка».
        const st = (record as { hermesStatus?: string | null }).hermesStatus;
        if (!st) return '';
        const HERMES_LABELS: Record<string, string> = {
          pending: 'В очереди', running: 'Считается', completed: 'Оценено',
          failed: 'Ошибка', timeout: 'Таймаут', error: 'Ошибка',
        };
        return HERMES_LABELS[st] ?? st;
      }
      if (slug === '_item_type') return allItemTypes.find((t) => t.id === record.itemTypeId)?.name ?? '';
      // link_aggregate
      const la = laBySlug.get(slug);
      if (la) {
        const value = linkedAggregatesMap[record.id]?.[slug];
        if (la.mode === 'list') {
          const entries = Array.isArray(value) ? (value as LinkedAggregateListEntry[]) : [];
          return entries.map((e) => e.title ?? e.displayKey ?? e.itemId.slice(0, 8)).join(', ');
        }
        if (typeof value !== 'number') return '';
        return String(la.mode === 'avg' ? Math.round(value * 100) / 100 : value);
      }
      // view_computed
      const vc = vcBySlug.get(slug);
      if (vc) {
        const value = viewComputedMap[record.id]?.[vc.key];
        if (value === null || value === undefined) return '';
        if (vc.returnType === 'number') {
          return typeof value === 'number' ? formatViewComputedNumber(value, vc.numberDecimals ?? 0) : '';
        }
        return String(value);
      }
      // reference_attribute
      const ra = raBySlug.get(slug);
      if (ra) {
        const refField = mergedFields.find((f) => f.slug === ra.referenceFieldSlug);
        const dictionaryId = (refField?.config as { dictionaryId?: string } | null)?.dictionaryId;
        if (!refField || !dictionaryId) return '';
        const refValue = record.fields[ra.referenceFieldSlug];
        const ids: string[] = Array.isArray(refValue)
          ? (refValue as unknown[]).filter((v): v is string => typeof v === 'string')
          : typeof refValue === 'string' && refValue ? [refValue] : [];
        if (ids.length === 0) return '';
        const ctx: RefAttrCtx = { dictionaryEntryValues, dictionaryColumnsById, dictionaryOptionsById, resolvedRefs };
        return renderRefAttrPath(ids, getRefAttrPath(ra), dictionaryId, ctx) ?? '';
      }
      // обычное поле (включая computed-поля типа — их значения лежат в record.fields)
      const field = fieldBySlug.get(slug);
      if (field) return fieldValueToText(record.fields[slug] ?? null, field, resolvedRefs);
      return '';
    };

    const headers = exportSlugs.map((s) => slugNameMap.get(s) ?? s);
    const rows = visibleItems.map((record) => exportSlugs.map((s) => cellText(s, record)));
    const aoa = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    // Ширина колонок по самому длинному значению (с разумным потолком).
    ws['!cols'] = exportSlugs.map((_s, i) => {
      const maxLen = aoa.reduce((m, r) => Math.max(m, String(r[i] ?? '').length), 0);
      return { wch: Math.min(60, Math.max(10, maxLen + 2)) };
    });
    const wb = XLSX.utils.book_new();
    const sheetName = (view.name || 'Лист').slice(0, 31).replace(/[\\/?*[\]:]/g, ' ');
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const safeName = `${space?.name ? space.name + ' — ' : ''}${view.name || 'export'}`.replace(/[\\/:*?"<>|]/g, ' ').trim();
    XLSX.writeFile(wb, `${safeName}.xlsx`);
  }, [
    columnSettingsOptions, linkAggregateColumns, viewComputedColumns, referenceAttributeColumns,
    tableSettings.orderedSlugs, visibleItems, allItemTypes, linkedAggregatesMap, viewComputedMap,
    mergedFields, dictionaryEntryValues, dictionaryColumnsById, dictionaryOptionsById, resolvedRefs,
    fieldBySlug, view.name, space?.name,
  ]);

  // Динамическая высота тела таблицы: меряем реальное расстояние от верха
  // таблицы до низа окна, чтобы таблица занимала всю доступную высоту и снизу
  // не оставалось пустоты. Пересчёт при ресайзе, смене вида, изменении числа
  // строк и раскрытии панели фильтров (через ResizeObserver на родителе).
  useEffect(() => {
    const measure = (): void => {
      const el = tableAreaRef.current;
      if (!el) return;
      const gap = isFullscreen ? 12 : 24; // нижнее поле контента
      const wrapRect = el.getBoundingClientRect();
      const bodyEl = el.querySelector('.ant-table-body');
      let top: number;
      let below: number;
      if (bodyEl) {
        // Меряем по факту: тело начинается под шапкой таблицы, а под телом
        // лежат строка итогов и пагинация со своими отступами. Их высота от
        // scroll.y не зависит, поэтому пересчёт не зацикливается.
        const bodyRect = bodyEl.getBoundingClientRect();
        top = bodyRect.top;
        below = wrapRect.bottom - bodyRect.bottom;
      } else {
        // Первый рендер: тела ещё нет — берём прежнюю прикидку.
        top = wrapRect.top;
        below = isFullscreen ? 60 : 66;
      }
      const next = Math.max(180, Math.round(window.innerHeight - top - below - gap));
      setTableScrollY((prev) => (Math.abs(prev - next) > 2 ? next : prev));
    };
    measure();
    const raf = requestAnimationFrame(measure);
    const t = setTimeout(measure, 80);
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    const el = tableAreaRef.current;
    if (el) ro.observe(el);
    if (el?.parentElement) ro.observe(el.parentElement);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [isFullscreen, activeViewId, view.id, visibleItems.length]);

  // Compute total scroll width from column widths so header/body tables stay in sync
  const scrollX = useMemo(() => {
    let total = 40; // checkbox column
    for (const col of columns) {
      total += (typeof col.width === 'number' ? col.width : 150);
    }
    return total;
  }, [columns]);

  // ─── Ячейки как в Excel: рамка, стрелки, Enter/Tab ───────────────────────
  // Колонки для основной таблицы: к каждой ячейке добавляем координаты и
  // tabIndex, чтобы её можно было сфокусировать с клавиатуры. Рамку рисует
  // класс lens-cell-active.
  const excelColumns: ColumnsType<ItemRow> = columns.map((column, colIndex) => ({
    ...column,
    onCell: (record: ItemRow, rowIndex?: number) => {
      const inherited = typeof column.onCell === 'function' ? column.onCell(record, rowIndex) : {};
      const isActive = activeCell?.row === rowIndex && activeCell?.col === colIndex;
      return {
        ...inherited,
        tabIndex: -1,
        'data-cell-row': rowIndex,
        'data-cell-col': colIndex,
        className: [
          (inherited as { className?: string }).className,
          column.className,
          isActive ? 'lens-cell-active' : null,
        ]
          .filter(Boolean)
          .join(' '),
        onClick: (event: React.MouseEvent<HTMLElement>) => {
          (inherited as { onClick?: (e: React.MouseEvent<HTMLElement>) => void }).onClick?.(event);
          // Окна, открытые из ячейки (редактор markdown), рендерятся порталом, но
          // их клики всплывают сюда по дереву React. Без проверки ячейка
          // становилась активной и забирала фокус у редактора в окне.
          if (!event.currentTarget.contains(event.target as Node)) return;
          if (rowIndex !== undefined) setActiveCell({ row: rowIndex, col: colIndex });
        },
      } as React.HTMLAttributes<HTMLElement>;
    },
  }));

  // Страница управляется нами, значит и следить за выходом за границы тоже
  // нам: после фильтра или поиска записей может стать меньше, и текущая
  // страница окажется пустой.
  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(visibleItems.length / tableSettings.settings.pageSize));
    if (page > lastPage) {
      setPage(1);
      setActiveCell(null);
    }
  }, [visibleItems.length, tableSettings.settings.pageSize, page]);

  const focusCell = useCallback((cell: { row: number; col: number }) => {
    const el = tableAreaRef.current?.querySelector(
      `td[data-cell-row="${cell.row}"][data-cell-col="${cell.col}"]`,
    );
    if (!(el instanceof HTMLElement)) return;
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, []);

  useEffect(() => {
    if (!activeCell) return;
    focusCell(activeCell);
  }, [activeCell, focusCell]);

  const handleGridKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Нажатия из окон-порталов (редактор markdown из ячейки) — не таблице.
    if (!e.currentTarget.contains(e.target as Node)) return;
    const target = e.target as HTMLElement;
    const insideEditor = target.closest('input, textarea, [contenteditable="true"], .ant-select');

    // Внутри открытого редактора клавиши принадлежат ему. Перехватываем только
    // выход: Esc возвращает рамку на ту же ячейку, Enter — на ячейку ниже.
    if (insideEditor) {
      if (e.key !== 'Escape' && e.key !== 'Enter') return;
      if (target.closest('.ant-select')) return; // в списке Enter выбирает вариант
      const td = target.closest('td[data-cell-row]');
      if (!(td instanceof HTMLElement)) return;
      const row = Number(td.dataset.cellRow);
      const col = Number(td.dataset.cellCol);
      const nextRow = e.key === 'Enter' ? row + 1 : row;
      // Редактор закрывается своим обработчиком — рамку ставим следующим кадром.
      setTimeout(() => {
        const rowCount =
          tableAreaRef.current?.querySelectorAll('tbody .ant-table-row').length ?? 0;
        setActiveCell({ row: Math.min(nextRow, Math.max(rowCount - 1, 0)), col });
      }, 0);
      return;
    }

    const td = target.closest('td[data-cell-row]');
    if (!(td instanceof HTMLElement)) return;
    const row = Number(td.dataset.cellRow);
    const col = Number(td.dataset.cellCol);
    const rowCount = tableAreaRef.current?.querySelectorAll('tbody .ant-table-row').length ?? 0;
    const colCount = excelColumns.length;
    const move = (nextRow: number, nextCol: number) => {
      e.preventDefault();
      setActiveCell({
        row: Math.max(0, Math.min(nextRow, rowCount - 1)),
        col: Math.max(0, Math.min(nextCol, colCount - 1)),
      });
    };

    switch (e.key) {
      case 'ArrowDown':
        return move(row + 1, col);
      case 'ArrowUp':
        return move(row - 1, col);
      case 'ArrowRight':
        return move(row, col + 1);
      case 'ArrowLeft':
        return move(row, col - 1);
      case 'Tab':
        return move(row, e.shiftKey ? col - 1 : col + 1);
      case 'Home':
        return move(row, 0);
      case 'End':
        return move(row, colCount - 1);
      case 'PageDown':
        return move(row + 10, col);
      case 'PageUp':
        return move(row - 10, col);
      case 'Enter': {
        e.preventDefault();
        openCellEditor(td);
        return;
      }
      default:
        break;
    }

    // Печать символа сразу открывает правку и подставляет набранный символ —
    // как в Excel, где ввод затирает прежнее значение.
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      openCellEditor(td, e.key);
    }
  };

  /**
   * Открывает редактор ячейки (у него свой onClick на элементе-триггере) и,
   * если задан seed, кладёт в поле ввода набранный символ. Значение выставляем
   * через нативный setter: поле контролируемое, и присваивание input.value
   * React бы не заметил.
   */
  function openCellEditor(td: HTMLElement, seed?: string): void {
    const trigger = td.querySelector('[role="button"]');
    if (!(trigger instanceof HTMLElement)) return;
    trigger.click();
    if (seed === undefined) return;
    setTimeout(() => {
      const input = td.querySelector('input:not([type="checkbox"]), textarea');
      if (!(input instanceof HTMLInputElement) && !(input instanceof HTMLTextAreaElement)) return;
      const proto =
        input instanceof HTMLInputElement ? HTMLInputElement.prototype : HTMLTextAreaElement.prototype;
      Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(input, seed);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }, 0);
  }

  // ─── Multi-level grouping logic ──────────────────────────────────────────
  interface GroupNode {
    key: string;
    label: string;
    fieldName: string;
    fieldSlug: string;
    rawValue: string;
    depth: number;
    items: ItemRow[];
    children: GroupNode[] | null;
  }

  const resolveGroupLabel = useCallback((fieldSlug: string, rawKey: string): string => {
    if (rawKey === '__empty__') return 'Без значения';
    // Составной ключ многозначного поля: разрешаем каждое значение по
    // отдельности и собираем «A, B» (метки сортируем по алфавиту для
    // стабильного отображения, при том что сам ключ детерминирован).
    if (rawKey.includes(GROUP_MULTI_SEP)) {
      return rawKey
        .split(GROUP_MULTI_SEP)
        .map((part) => resolveGroupLabel(fieldSlug, part))
        .sort((a, b) => a.localeCompare(b, 'ru'))
        .join(', ');
    }
    if (fieldSlug === '_item_type') {
      return allItemTypes.find((t) => t.id === rawKey)?.name ?? rawKey;
    }
    if (fieldSlug === '_created_by') {
      // Ключ — либо userId, либо «name:<displayName>», либо id, для которого не
      // ходило displayName. Берём displayName из первого подходящего item'а
      // через items уже на стороне buildGroupTree → сюда приходит ключ; ищем
      // соответствующий item по visibleItems.
      if (rawKey.startsWith('name:')) return rawKey.slice('name:'.length);
      const match = visibleItems.find((it) => (it as { createdById?: string | null }).createdById === rawKey);
      const name = match ? (match as { createdByDisplayName?: string | null }).createdByDisplayName : null;
      return name ?? rawKey;
    }
    if (fieldSlug.startsWith('_refattr:')) {
      const colKey = fieldSlug.slice('_refattr:'.length);
      const col = referenceAttributeColumns.find((c) => c.key === colKey);
      if (!col) return rawKey;
      const refField = allFields.find((f) => f.slug === col.referenceFieldSlug);
      const dictionaryId = (refField?.config as { dictionaryId?: string } | null)?.dictionaryId;
      const dictCol = dictionaryId
        ? getFinalRefAttrColumn(dictionaryId, getRefAttrPath(col), dictionaryColumnsById)
        : undefined;
      if (dictCol?.columnType === 'boolean') return rawKey === 'true' ? 'Да' : 'Нет';
      if (dictCol?.columnType === 'select' || dictCol?.columnType === 'multi_select') {
        const opts = (dictCol.config as { options?: Array<{ value: string; label: string }> })?.options ?? [];
        return opts.find((o) => o.value === rawKey)?.label ?? rawKey;
      }
      if (dictCol?.columnType === 'reference') {
        const nestedDictId = (dictCol.config as { dictionaryId?: string } | null)?.dictionaryId;
        const nestedOpts = nestedDictId ? dictionaryOptionsById[nestedDictId] : undefined;
        return nestedOpts?.find((o) => o.value === rawKey)?.label ?? resolvedRefs[rawKey] ?? rawKey;
      }
      return rawKey;
    }
    const field = fieldBySlug.get(fieldSlug);
    if (!field) return rawKey;
    if (field.fieldType === 'boolean') return rawKey === 'true' ? 'Да' : 'Нет';
    if (field.fieldType === 'select') {
      const opts = (field.config as { options?: Array<{ value: string; label: string }> })?.options ?? [];
      return opts.find((o) => o.value === rawKey)?.label ?? rawKey;
    }
    if ((field.fieldType === 'reference' || field.fieldType === 'user') && resolvedRefs[rawKey]) return resolvedRefs[rawKey];
    return rawKey;
  }, [fieldBySlug, resolvedRefs, referenceAttributeColumns, allFields, dictionaryColumnsById, dictionaryOptionsById, visibleItems, allItemTypes]);

  const buildGroupTree = useCallback((
    items: ItemRow[],
    levels: GroupingLevel[],
    depth: number,
    parentKey: string,
  ): GroupNode[] | null => {
    if (levels.length === 0) return null;
    const [level, ...rest] = levels;
    if (!level) return null;

    const isRefAttr = level.fieldSlug.startsWith('_refattr:');
    const isCreatedBy = level.fieldSlug === '_created_by';
    const isItemType = level.fieldSlug === '_item_type';
    let fieldName: string;
    let groupField: FieldDefinition | undefined;
    let refAttrCol: ReferenceAttributeColumnConfig | undefined;
    if (isRefAttr) {
      const colKey = level.fieldSlug.slice('_refattr:'.length);
      refAttrCol = referenceAttributeColumns.find((c) => c.key === colKey);
      if (!refAttrCol) return null;
      fieldName = refAttrCol.label;
    } else if (isCreatedBy) {
      fieldName = 'Кто создал';
    } else if (isItemType) {
      fieldName = 'Тип записи';
    } else {
      const field = fieldBySlug.get(level.fieldSlug);
      if (!field) return null;
      fieldName = field.name;
      groupField = field;
    }

    const buckets = new Map<string, ItemRow[]>();
    const pushTo = (key: string, item: ItemRow) => {
      const arr = buckets.get(key) ?? [];
      arr.push(item);
      buckets.set(key, arr);
    };
    for (const item of items) {
      if (isRefAttr && refAttrCol) {
        const refField = mergedFields.find((f) => f.slug === refAttrCol!.referenceFieldSlug);
        const rootDictId = (refField?.config as { dictionaryId?: string } | null)?.dictionaryId;
        const refValue = item.fields[refAttrCol.referenceFieldSlug];
        const ids: string[] = Array.isArray(refValue)
          ? (refValue as unknown[]).filter((v): v is string => typeof v === 'string')
          : typeof refValue === 'string' && refValue ? [refValue] : [];
        const keys = new Set<string>();
        if (rootDictId) {
          const ctx: RefAttrCtx = {
            dictionaryEntryValues,
            dictionaryColumnsById,
            dictionaryOptionsById,
            resolvedRefs,
          };
          const leaves = walkRefAttrPath(ids, getRefAttrPath(refAttrCol), rootDictId, ctx);
          for (const raw of leaves) {
            if (raw === null || raw === undefined || raw === '') continue;
            if (typeof raw === 'boolean') keys.add(raw ? 'true' : 'false');
            else keys.add(String(raw));
          }
        }
        if (keys.size === 0) pushTo('__empty__', item);
        else pushTo([...keys].sort().join(GROUP_MULTI_SEP), item);
        continue;
      }
      if (isItemType) {
        const tid = (item as { itemTypeId?: string | null }).itemTypeId;
        pushTo(tid ?? '__empty__', item);
        continue;
      }
      if (isCreatedBy) {
        const cbId = (item as { createdById?: string | null }).createdById;
        const cbName = (item as { createdByDisplayName?: string | null }).createdByDisplayName;
        // Группируем по id если он есть, иначе по displayName, иначе пусто.
        // Кладём displayName в resolvedRefs[id] на лету, чтобы resolveGroupLabel
        // вернул читабельную метку.
        const key = cbId ?? (cbName ? `name:${cbName}` : '__empty__');
        pushTo(key, item);
        continue;
      }
      const raw = item.fields[level.fieldSlug];
      // Многозначные поля (reference / multi_select) кладут массив значений.
      // Запись с набором значений [A, B] образует ОДНУ группу «A, B» —
      // объединяем значения в детерминированный составной ключ (дедуп + сорт),
      // а не дублируем запись по группам A и B.
      if (Array.isArray(raw)) {
        const keys = (raw as unknown[]).filter(
          (v): v is string => typeof v === 'string' && v !== '',
        );
        if (keys.length === 0) pushTo('__empty__', item);
        else pushTo([...new Set(keys)].sort().join(GROUP_MULTI_SEP), item);
        continue;
      }
      let key: string;
      if (raw === null || raw === undefined || raw === '') key = '__empty__';
      else if (typeof raw === 'boolean') key = raw ? 'true' : 'false';
      else key = String(raw);
      pushTo(key, item);
    }

    const entries = Array.from(buckets.entries());
    if (level.order === 'count') {
      entries.sort((a, b) => b[1].length - a[1].length);
    } else {
      // select / multi_select — в порядке опций из настроек поля, остальное — по алфавиту.
      const cmp = makeGroupKeyComparator(groupField, GROUP_MULTI_SEP);
      entries.sort((a, b) => cmp(a[0], b[0]));
    }

    return entries.map(([rawKey, groupItems]) => {
      const compositeKey = parentKey ? `${parentKey}|${level.fieldSlug}=${rawKey}` : `${level.fieldSlug}=${rawKey}`;
      return {
        key: compositeKey,
        label: resolveGroupLabel(level.fieldSlug, rawKey),
        fieldName,
        fieldSlug: level.fieldSlug,
        rawValue: rawKey,
        depth,
        items: groupItems,
        children: rest.length > 0 ? buildGroupTree(groupItems, rest, depth + 1, compositeKey) : null,
      };
    });
  }, [fieldBySlug, resolveGroupLabel, referenceAttributeColumns, dictionaryEntryValues, dictionaryColumnsById, dictionaryOptionsById, resolvedRefs, mergedFields, allItemTypes]);

  const groupTree = useMemo(() => {
    const items = visibleItems;
    const levels = tableSettings.settings.grouping;
    if (levels.length === 0) return null;
    return buildGroupTree(items, levels, 0, '');
  }, [visibleItems, tableSettings.settings.grouping, buildGroupTree]);

  const GROUP_DEPTH_COLORS = isDark
    ? ['#1c1c22', '#1b1f2b', '#1a221c', '#241f17', '#241a20']
    : ['#fafafa', '#f0f5ff', '#f6ffed', '#fff7e6', '#fff0f6'];
  let groupTableHeaderShown = false;
  const canDragBetweenGroups = space?.myRole !== 'viewer';

  const dragHandleColumn: ColumnsType<ItemRow>[0] = {
    key: '__drag_handle__',
    width: 36,
    fixed: 'left',
    className: 'lens-drag-handle-col',
    title: '',
    render: () => <DragHandleCell />,
  };

  const groupedColumns: ColumnsType<ItemRow> = canDragBetweenGroups
    ? [dragHandleColumn, ...columns]
    : columns;

  // ─── Итоги по числовым колонкам (строка summary внизу таблицы) ───────────────
  const canEditAggregates = space?.myRole !== 'viewer';
  const AGG_MENU_ITEMS = [
    { key: 'sum', label: 'Сумма' },
    { key: 'avg', label: 'Среднее' },
    { key: 'min', label: 'Минимум' },
    { key: 'max', label: 'Максимум' },
  ];
  const AGG_SHORT: Record<'sum' | 'avg' | 'min' | 'max', string> = {
    sum: 'Σ',
    avg: 'avg',
    min: 'min',
    max: 'max',
  };
  // Колонка пригодна для числового итога: обычное number-поле, computed-поле
  // с числовыми значениями, либо view_computed-колонка с returnType='number'.
  // computed-значения лежат в r.fields[slug] как число (dto.ts), view_computed —
  // в viewComputedMap[itemId][key], а не в r.fields.
  const isAggregatableColumn = (slug: string, rows: ItemRow[]): boolean => {
    if (slug.startsWith('_vc:')) {
      const key = slug.slice('_vc:'.length);
      return viewComputedColumns.some((c) => c.key === key && c.returnType === 'number');
    }
    const field = fieldBySlug.get(slug);
    if (!field) return false;
    if (field.fieldType === 'number') return true;
    if (field.fieldType === 'computed') {
      return rows.some((r) => {
        const v = r.fields[slug];
        return typeof v === 'number' && Number.isFinite(v);
      });
    }
    return false;
  };
  const computeAggregate = (
    rows: ItemRow[],
    slug: string,
    fn: 'sum' | 'avg' | 'min' | 'max',
  ): number | null => {
    const nums: number[] = [];
    const vcKey = slug.startsWith('_vc:') ? slug.slice('_vc:'.length) : null;
    for (const r of rows) {
      const v = vcKey ? viewComputedMap[r.id]?.[vcKey] : r.fields[slug];
      if (typeof v === 'number' && Number.isFinite(v)) nums.push(v);
    }
    if (nums.length === 0) return null;
    if (fn === 'sum') return nums.reduce((a, b) => a + b, 0);
    if (fn === 'avg') return nums.reduce((a, b) => a + b, 0) / nums.length;
    if (fn === 'min') return Math.min(...nums);
    return Math.max(...nums);
  };
  const formatAggregate = (n: number, fn: 'sum' | 'avg' | 'min' | 'max'): string => {
    const maxFrac = fn === 'avg' ? 2 : Number.isInteger(n) ? 0 : 2;
    return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: maxFrac }).format(n);
  };
  const renderAggregateFooterCell = (
    slug: string,
    rows: ItemRow[],
    agg: 'sum' | 'avg' | 'min' | 'max' | null,
  ): React.ReactNode => {
    const value = agg ? computeAggregate(rows, slug, agg) : null;
    const content = agg ? (
      <span style={{ fontWeight: 600 }}>
        <span style={{ color: '#999', fontWeight: 400, marginRight: 4, fontSize: 11 }}>
          {AGG_SHORT[agg]}
        </span>
        {value == null ? '—' : formatAggregate(value, agg)}
      </span>
    ) : (
      <span style={{ color: '#ccc', fontSize: 11 }}>итог ▾</span>
    );
    if (!canEditAggregates) {
      return <span style={{ display: 'block', textAlign: 'right' }}>{agg ? content : null}</span>;
    }
    return (
      <Dropdown
        trigger={['click']}
        menu={{
          selectedKeys: agg ? [agg] : [],
          items: [
            ...AGG_MENU_ITEMS,
            ...(agg ? [{ type: 'divider' as const }, { key: 'none', label: 'Убрать итог' }] : []),
          ],
          onClick: ({ key, domEvent }) => {
            domEvent.stopPropagation();
            void handleSetColumnAggregate(slug, key === 'none' ? null : (key as 'sum' | 'avg' | 'min' | 'max'));
          },
        }}
      >
        <span
          className="lens-agg-cell"
          style={{ display: 'block', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
          title="Итог по колонке"
        >
          {content}
        </span>
      </Dropdown>
    );
  };
  const renderColumnsSummary = (
    rows: ItemRow[],
    colsArray: ColumnsType<ItemRow>,
    hasSelection: boolean,
  ): React.ReactNode => {
    const hasNumeric = colsArray.some((c) =>
      isAggregatableColumn(String((c as { key?: React.Key }).key ?? ''), rows),
    );
    if (!hasNumeric) return undefined;
    // Строка итогов занимает ~50px высоты. Держать её ради пустых плейсхолдеров
    // «итог ▾» дорого на ноутбуке: показываем, только если итог уже настроен
    // или пользователь включил строку кнопкой «Итоги» в настройках колонок.
    if (aggregateBySlug.size === 0 && !tableSettings.settings.showTotals) return undefined;
    let idx = 0;
    const cells: React.ReactNode[] = [];
    if (hasSelection) {
      cells.push(<Table.Summary.Cell key="__sel" index={idx++} />);
    }
    for (const c of colsArray) {
      const key = String((c as { key?: React.Key }).key ?? '');
      const cellIndex = idx++;
      if (isAggregatableColumn(key, rows)) {
        const agg = aggregateBySlug.get(key) ?? null;
        cells.push(
          <Table.Summary.Cell key={key} index={cellIndex} align="right">
            {renderAggregateFooterCell(key, rows, agg)}
          </Table.Summary.Cell>,
        );
      } else {
        cells.push(<Table.Summary.Cell key={key || `__c${cellIndex}`} index={cellIndex} />);
      }
    }
    return (
      <Table.Summary fixed>
        <Table.Summary.Row>{cells}</Table.Summary.Row>
      </Table.Summary>
    );
  };

  const renderGroupNodes = (nodes: GroupNode[], _isRoot: boolean): React.ReactNode => {
    return nodes.map((node) => {
      const collapsed = tableSettings.settings.collapsedGroups.includes(node.key);
      const depthColor = GROUP_DEPTH_COLORS[node.depth % GROUP_DEPTH_COLORS.length];
      const isLeaf = !node.children;

      // Чекбокс «выбрать все записи в группе». Без него при группировке
      // нельзя выделить все записи не в первой группе — header-чекбокс AntD
      // показывается только над первой таблицей (showHeader), а соседние
      // группы остаются недоступны для массовых операций.
      const groupItemIds = node.items.map((it) => it.id);
      const groupSelectedCount = groupItemIds.filter((id) => selectedRowKeys.includes(id)).length;
      const allGroupSelected = groupItemIds.length > 0 && groupSelectedCount === groupItemIds.length;
      const someGroupSelected = groupSelectedCount > 0 && !allGroupSelected;
      const toggleGroupSelection = (checked: boolean) => {
        const idSet = new Set(groupItemIds);
        setSelectedRowKeys((prev) => {
          const others = prev.filter((k) => !idSet.has(String(k)));
          return checked ? [...others, ...groupItemIds] : others;
        });
      };

      const headerContent = (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: `${4 + (node.depth === 0 ? 2 : 0)}px 12px`,
            background: depthColor,
            borderRadius: 4,
            cursor: 'pointer',
            border: '1px solid var(--color-border)',
            userSelect: 'none',
          }}
          onClick={() => tableSettings.toggleGroupCollapsed(node.key)}
          data-testid={`group-header-${node.key}`}
        >
          {space?.myRole !== 'viewer' && groupItemIds.length > 0 && (
            <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
              <Checkbox
                checked={allGroupSelected}
                indeterminate={someGroupSelected}
                onChange={(e) => toggleGroupSelection(e.target.checked)}
                data-testid={`group-select-${node.key}`}
              />
            </span>
          )}
          <span style={{
            fontSize: 11,
            color: isDark ? 'var(--color-text-secondary)' : '#999',
            transition: 'transform 0.2s',
            transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
          }}>
            ▼
          </span>
          <span style={{ fontSize: 11, color: isDark ? 'var(--color-text-secondary)' : '#888' }}>{node.fieldName}:</span>
          <span style={{ fontWeight: 600, fontSize: 13, ...(isDark ? { color: 'var(--color-text)' } : {}) }}>{node.label}</span>
          <Tag style={{ marginLeft: 4 }}>{node.items.length}</Tag>
          {/* P1-3: числовые агрегаты по группе (sum/avg/min/max из view.config). */}
          {(() => {
            const chips: React.ReactNode[] = [];
            for (const [slug, agg] of aggregateBySlug.entries()) {
              const v = computeAggregate(node.items, slug, agg);
              if (v == null) continue;
              const fieldName = fieldBySlug.get(slug)?.name ?? slug;
              chips.push(
                <span
                  key={slug}
                  style={{ fontSize: 11, color: isDark ? '#b8b8c2' : '#555', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                  title={`${fieldName}: ${AGG_SHORT[agg]}`}
                >
                  <span style={{ color: isDark ? '#85858f' : '#999', marginRight: 3 }}>{fieldName}</span>
                  <span style={{ color: isDark ? '#6d6d77' : '#bbb', marginRight: 2 }}>{AGG_SHORT[agg]}</span>
                  <span style={{ fontWeight: 600 }}>{formatAggregate(v, agg)}</span>
                </span>,
              );
            }
            return chips.length > 0 ? (
              <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 14, alignItems: 'center', paddingLeft: 12 }}>
                {chips}
              </span>
            ) : null;
          })()}
        </div>
      );

      return (
        <div key={node.key} style={{ marginLeft: node.depth * 16, marginBottom: 2 }}>
          {canDragBetweenGroups ? (
            <DroppableGroupSection
              groupKey={node.key}
              fieldSlug={node.fieldSlug}
              rawValue={node.rawValue}
              header={headerContent}
            >
              {!collapsed && isLeaf && (() => {
                const needHeader = !groupTableHeaderShown;
                groupTableHeaderShown = true;
                return (
                    <Table<ItemRow>
                      rowKey="id"
                      columns={groupedColumns}
                      dataSource={node.items}
                      pagination={false}
                      scroll={{ x: scrollX }}
                      tableLayout="fixed"
                      size="small"
                      showHeader={needHeader}
                      locale={{ emptyText: 'Нет записей' }}
                      rowSelection={space?.myRole !== 'viewer' ? {
                        selectedRowKeys,
                        // Сливаем выбор этой группы с выбором других групп —
                        // иначе onChange затирал бы записи, выбранные в соседних
                        // группах (каждая группа — отдельная AntD-таблица).
                        onChange: (keys) => {
                          const idSet = new Set(node.items.map((it) => it.id));
                          setSelectedRowKeys((prev) => [
                            ...prev.filter((k) => !idSet.has(String(k))),
                            ...keys,
                          ]);
                        },
                      } : undefined}
                      components={{
                        header: { cell: ResizableTitle },
                        ...(canDragBetweenGroups ? { body: { row: DraggableTableRow } } : {}),
                      }}
                      onChange={() => {}}
                      rowClassName={rowClassName}
                      className={`lens-items-table lens-items-table--excel${tableSettings.settings.wrapRows ? " lens-items-table--wrap" : ""}${tableSettings.settings.density === "compact" ? " lens-items-table--compact" : ""}`}
                    />
                );
              })()}
              {!collapsed && node.children && renderGroupNodes(node.children, false)}
            </DroppableGroupSection>
          ) : (
            <>
              {headerContent}
              {!collapsed && isLeaf && (() => {
                const needHeader = !groupTableHeaderShown;
                groupTableHeaderShown = true;
                return (
                  <Table<ItemRow>
                    rowKey="id"
                    columns={groupedColumns}
                    dataSource={node.items}
                    pagination={false}
                    scroll={{ x: scrollX }}
                    tableLayout="fixed"
                    size="small"
                    showHeader={needHeader}
                    locale={{ emptyText: 'Нет записей' }}
                    rowSelection={space?.myRole !== 'viewer' ? {
                      selectedRowKeys,
                      // Сливаем выбор этой группы с выбором других групп.
                      onChange: (keys) => {
                        const idSet = new Set(node.items.map((it) => it.id));
                        setSelectedRowKeys((prev) => [
                          ...prev.filter((k) => !idSet.has(String(k))),
                          ...keys,
                        ]);
                      },
                    } : undefined}
                    components={{
                      header: { cell: ResizableTitle },
                      ...(canDragBetweenGroups ? { body: { row: DraggableTableRow } } : {}),
                    }}
                    onChange={() => {}}
                    rowClassName={rowClassName}
                    className={`lens-items-table lens-items-table--excel${tableSettings.settings.wrapRows ? " lens-items-table--wrap" : ""}${tableSettings.settings.density === "compact" ? " lens-items-table--compact" : ""}`}
                  />
                );
              })()}
              {!collapsed && node.children && renderGroupNodes(node.children, false)}
            </>
          )}
        </div>
      );
    });
  };

  // Column settings popover content — ограничиваем колонками, разрешёнными
  // представлением (view.config.columns). Колонки, не упомянутые в view-конфиге,
  // редактируются только через «Настройки представления».
  const visibleColumnSettingsOptions = useMemo(() => {
    const allowed = new Set(fieldSlugs);
    return columnSettingsOptions.filter((c) => allowed.has(c.slug));
  }, [columnSettingsOptions, fieldSlugs]);
  const colSettingsContent = (
    <div style={{ maxHeight: 320, overflow: 'auto', minWidth: 220 }}>
      {visibleColumnSettingsOptions.map((column) => {
        // Мета-колонки (Создано/Обновлено/Кто создал) скрыты по умолчанию (P1-1)
        // и управляются отдельным набором metaShownByView, а не hiddenCols.
        const isMetaCol = (META_DEFAULT_HIDDEN as readonly string[]).includes(column.slug);
        const hidden = isMetaCol
          ? !metaShownSlugs.has(column.slug)
          : tableSettings.settings.hiddenCols.includes(column.slug);
        const pinned = tableSettings.settings.pinnedCols.includes(column.slug);
        const isVirtual = !column.field;
        return (
          <div key={column.slug} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
            <Checkbox
              checked={!hidden}
              onChange={() => (isMetaCol ? toggleMetaCol(column.slug) : tableSettings.toggleColVisibility(column.slug))}
            />
            <span style={{ flex: 1, opacity: hidden ? 0.4 : 1 }}>
              {column.name}
              {isVirtual ? <span style={{ color: '#999', marginLeft: 6 }}>(авто)</span> : null}
            </span>
            {!isVirtual ? (
              <Tooltip title={pinned ? 'Открепить' : 'Закрепить'}>
                <Button
                  size="small"
                  type="text"
                  icon={<PushpinOutlined style={{ color: pinned ? '#1677ff' : '#bbb' }} />}
                  onClick={() => tableSettings.togglePin(column.slug)}
                />
              </Tooltip>
            ) : (
              <Button
                size="small"
                type="text"
                disabled
                icon={<PushpinOutlined style={{ color: '#ddd' }} />}
              />
            )}
          </div>
        );
      })}
      <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 8, paddingTop: 8 }}>
        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>Связанные записи</div>
        {linkAggregateColumns.length === 0 ? (
          <div style={{ fontSize: 12, color: '#bbb', padding: '2px 0' }}>
            Колонок из связей пока нет
          </div>
        ) : (
          linkAggregateColumns.map((col) => (
            <div
              key={col.key}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}
            >
              <span style={{ flex: 1, fontSize: 13 }}>{col.label}</span>
              <Tooltip title="Настроить колонку">
                <Button
                  size="small"
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditingLinkAggregateColumn(col);
                    setShowColSettings(false);
                    setLinkAggregateModalOpen(true);
                  }}
                />
              </Tooltip>
              <Tooltip title="Удалить колонку">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => void handleRemoveLinkAggregateColumn(col.key)}
                />
              </Tooltip>
            </div>
          ))
        )}
      </div>
      <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 8, paddingTop: 8 }}>
        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>Расчётные колонки</div>
        {viewComputedColumns.length === 0 ? (
          <div style={{ fontSize: 12, color: '#bbb', padding: '2px 0' }}>
            Расчётных колонок пока нет
          </div>
        ) : (
          viewComputedColumns.map((col) => (
            <div
              key={col.key}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}
            >
              <span style={{ flex: 1, fontSize: 13 }}>{col.label}</span>
              <Tooltip title="Удалить колонку">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => void handleRemoveViewComputedColumn(col.key)}
                />
              </Tooltip>
            </div>
          ))
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            size="small"
            type="link"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingLinkAggregateColumn(null);
              setShowColSettings(false);
              setLinkAggregateModalOpen(true);
            }}
            style={{ paddingLeft: 0 }}
            data-testid="add-link-aggregate-column"
          >
            Колонка из связей
          </Button>
          <Button
            size="small"
            type="link"
            icon={<PlusOutlined />}
            onClick={() => setViewComputedModalOpen(true)}
            style={{ paddingLeft: 0 }}
            data-testid="add-view-computed-column"
          >
            Расчётная колонка
          </Button>
          <Button
            size="small"
            type="link"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingReferenceAttributeColumn(null);
              setReferenceAttributeModalOpen(true);
            }}
            style={{ paddingLeft: 0 }}
            data-testid="add-reference-attribute-column"
          >
            Атрибут справочника
          </Button>
        </div>
        {referenceAttributeColumns.length > 0 && (
          <div style={{ marginTop: 6 }}>
            {referenceAttributeColumns.map((col) => (
              <div
                key={col.key}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}
              >
                <span style={{ flex: 1, fontSize: 13 }}>{col.label}</span>
                <Tooltip title="Настроить колонку">
                  <Button
                    size="small"
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditingReferenceAttributeColumn(col);
                      setShowColSettings(false);
                      setReferenceAttributeModalOpen(true);
                    }}
                  />
                </Tooltip>
                <Tooltip title="Удалить колонку">
                  <Button
                    size="small"
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => void handleRemoveReferenceAttributeColumn(col.key)}
                  />
                </Tooltip>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 8, paddingTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button
          size="small"
          type="link"
          icon={<ExpandAltOutlined />}
          onClick={() => runAutoSize(autoSizeMode)}
          title={autoSizeMode === 'fit'
            ? 'Разделить ширину таблицы между колонками: перенос строк примерно поровну'
            : 'Подогнать колонки под текст в одну строку'}
        >
          Автоширина
        </Button>
        <Button
          size="small"
          type={tableSettings.settings.wrapRows ? 'primary' : 'link'}
          icon={<MenuOutlined />}
          onClick={tableSettings.toggleWrapRows}
          title="Подстраивать высоту строк под содержимое"
        >
          Перенос строк
        </Button>
        <Button
          size="small"
          type={tableSettings.settings.density === 'compact' ? 'primary' : 'link'}
          icon={<ColumnHeightOutlined />}
          onClick={tableSettings.toggleDensity}
          title="Компактные строки: меньше отступов и шрифт, «Описание» в одну строку"
        >
          Компактно
        </Button>
        <Button
          size="small"
          type={tableSettings.settings.showTotals ? 'primary' : 'link'}
          icon={<CalculatorOutlined />}
          onClick={tableSettings.toggleShowTotals}
          title="Строка итогов под таблицей: сумма/среднее/минимум/максимум по числовым колонкам"
          data-testid="toggle-totals-row"
        >
          Итоги
        </Button>
        <Button size="small" type="link" icon={<UndoOutlined />} onClick={tableSettings.resetSettings}>
          Сбросить
        </Button>
      </div>
    </div>
  );

  // View tab reordering via drag-and-drop
  const viewTabSensor = useSensor(PointerSensor, { activationConstraint: { distance: 5 } });
  const viewTabSensors = useSensors(viewTabSensor);

  const handleViewTabDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = views.findIndex((v) => v.id === active.id);
      const newIndex = views.findIndex((v) => v.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(views, oldIndex, newIndex);
      setViews(reordered);
      void reorderViews(spaceId, { orderedIds: reordered.map((v) => v.id) }).catch(() => {
        setViews(views); // rollback on error
      });
    },
    [views, spaceId],
  );

  const renderViewTab = (v: View) => {
    const iconDef = getSpaceIconDef(resolveSpaceIconKey(v.icon) ?? '');
    return (
    <AntSpace size={4}>
      {iconDef ? (
        <iconDef.Icon size={15} strokeWidth={1.9} color={resolveSpaceColor(v.icon) ?? 'currentColor'} style={{ verticalAlign: '-2px' }} />
      ) : v.viewType === 'kanban' ? (
        <AppstoreOutlined />
      ) : (
        <TableOutlined />
      )}
      <span>{v.name}</span>
      <Dropdown
        trigger={['click']}
        menu={{
          items: [
            { key: 'settings', icon: <SettingOutlined />, label: 'Настройки' },
            { key: 'duplicate', icon: <CopyOutlined />, label: 'Дублировать' },
            { type: 'divider' },
            { key: 'archive', icon: <InboxOutlined />, label: 'Архивировать' },
          ],
          onClick: ({ key, domEvent }) => {
            domEvent.stopPropagation();
            switch (key) {
              case 'settings':
                setEditingViewId(v.id);
                break;
              case 'duplicate':
                void handleDuplicateView(v.id);
                break;
              case 'archive':
                void handleArchiveView(v.id);
                break;
            }
          },
        }}
      >
        <SettingOutlined
          className="view-tab-action"
          style={{ fontSize: 11, cursor: 'pointer', opacity: 0.4, transition: 'opacity 0.2s' }}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={(e) => { (e.target as HTMLElement).style.opacity = '1'; }}
          onMouseLeave={(e) => { (e.target as HTMLElement).style.opacity = '0.4'; }}
        />
      </Dropdown>
    </AntSpace>
    );
  };

  return (
    <div>
      {error ? (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      {/* View tabs with drag-and-drop reordering */}
      {views.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 0, marginBottom: 8, borderBottom: '1px solid var(--color-border)' }} data-testid="view-tabs">
          {/* Вкладки не переносятся на вторую строку: когда их много, лента
              прокручивается вбок, а кнопка «+» остаётся справа. Перенос съедал
              ещё одну полосу высоты и уводил «+» под вкладки. */}
          <div className="lens-viewtabs-scroll" style={{ flex: '0 1 auto', minWidth: 0, display: 'flex', alignItems: 'center', overflowX: 'auto' }}>
          <DndContext sensors={viewTabSensors} collisionDetection={closestCenter} onDragEnd={handleViewTabDragEnd}>
            <SortableContext items={views.map((v) => v.id)} strategy={horizontalListSortingStrategy}>
              {views.map((v) => (
                <SortableViewTab key={v.id} id={v.id}>
                  <div
                    onClick={() => {
                      setActiveViewId(v.id);
                    }}
                    style={{
                      padding: `${spacing.xs}px ${spacing.md}px`,
                      cursor: 'pointer',
                      borderBottom: activeViewId === v.id ? `2px solid ${'var(--color-primary)'}` : '2px solid transparent',
                      color: activeViewId === v.id ? 'var(--color-primary)' : undefined,
                      fontWeight: activeViewId === v.id ? 500 : 400,
                      fontSize: 13,
                      whiteSpace: 'nowrap',
                      userSelect: 'none',
                    }}
                  >
                    {renderViewTab(v)}
                  </div>
                </SortableViewTab>
              ))}
            </SortableContext>
          </DndContext>
          </div>
          {/* Создание общих представлений — admin only (SRS §2.5.2:
              «Создание/редактирование общих представлений» = admin).
              Личные представления для member планируются в M-roles-migrations
              (см. CHANGELOG: refactor(roles)) — пока тоже скрываем. */}
          {space?.myRole === 'admin' ? (
            <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', paddingLeft: 8 }}>
              <Tooltip title="Создать представление">
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => setShowCreateView(true)}
                  aria-label="Создать представление"
                  data-testid="create-view-button"
                />
              </Tooltip>
            </div>
          ) : null}
        </div>
      )}

      {views.length === 0 && space?.myRole === 'admin' && (
        <div style={{ marginBottom: 12 }}>
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setShowCreateView(true)}
          >
            Создать представление
          </Button>
        </div>
      )}

      {/* Filter/Sort/Search + grouping + column settings + action buttons — single row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <FilterBar
            fields={filterFields}
            filters={userFilters}
            onFiltersChange={setUserFilters}
            sorts={userSorts}
            onSortsChange={setUserSorts}
            search={search}
            onSearchChange={setSearch}
            ensureReferenceOptions={ensureReferenceOptions}
            resolvedRefs={resolvedRefs}
            viewFilters={((view.config as { filters?: FilterLeaf[] } | null)?.filters ?? [])}
            sortNames={Object.fromEntries(linkAggregateColumns.map((c) => [c.key, c.label]))}
            extraButtons={!isKanban ? (
              <GroupingPopover
                levels={tableSettings.settings.grouping}
                fields={filterFields}
                onAdd={tableSettings.addGroupingLevel}
                onRemove={tableSettings.removeGroupingLevel}
                onReorder={tableSettings.reorderGrouping}
                onChangeOrder={tableSettings.setGroupingLevelOrder}
                onClearAll={() => tableSettings.setGrouping([])}
              />
            ) : null}
          />
        </div>
        <Tooltip title="Обновить таблицу">
          <Button
            icon={<ReloadOutlined />}
            size="small"
            onClick={() => void load()}
            loading={loading}
            data-testid="refresh-table-button"
            aria-label="Обновить таблицу"
          />
        </Tooltip>
        <Popover
          content={colSettingsContent}
          title="Настройки колонок"
          trigger="click"
          open={showColSettings}
          onOpenChange={setShowColSettings}
          placement="bottomRight"
        >
          <Tooltip title="Настройки колонок">
            <Button icon={<SettingOutlined />} size="small" data-testid="col-settings-button" aria-label="Настройки колонок" />
          </Tooltip>
        </Popover>
        <Tooltip title={isFullscreen ? 'Свернуть таблицу (Esc)' : 'Развернуть таблицу на весь экран'}>
          <Button
            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            size="small"
            onClick={() => setIsFullscreen((v) => !v)}
            data-testid="fullscreen-toggle-button"
            aria-label={isFullscreen ? 'Свернуть таблицу' : 'Развернуть таблицу'}
          />
        </Tooltip>

        {space?.myRole !== 'viewer' && (
          <>
            {activeViewId && (
              <Tooltip title="Lensy — помощник по данным текущего представления">
                <Button
                  size="small"
                  icon={<AiSparkIcon />}
                  onClick={() => openAiPanel({
                    viewId: activeViewId,
                    spaceName: space?.name,
                    viewName: activeView?.name,
                  })}
                  data-testid="ai-chat-button"
                  aria-label="Lensy"
                />
              </Tooltip>
            )}
            {space?.jiraIntegrationEnabled && (
              <Tooltip title="Синхронизация с Jira: поля, связи и импорт новых задач">
                <Button
                  size="small"
                  icon={<SwapOutlined />}
                  onClick={() => {
                    setSyncInboxRestrictIds(null);
                    setSyncInboxOpen(true);
                  }}
                  data-testid="sync-inbox-button"
                  aria-label="Синхронизация с Jira"
                />
              </Tooltip>
            )}
            {(() => {
              const hasWeight = isSingleType && mergedFields.some((f) => f.slug === 'weight');
              const moreItems: NonNullable<React.ComponentProps<typeof Dropdown>['menu']>['items'] = [];
              if (isSingleType) {
                moreItems.push({
                  key: 'xlsx-export',
                  icon: <DownloadOutlined />,
                  label: 'Экспорт Excel',
                  onClick: () => {
                    try {
                      handleExportXlsx();
                    } catch (err: unknown) {
                      message.error(err instanceof Error ? err.message : 'Ошибка экспорта');
                    }
                  },
                });
                moreItems.push({
                  key: 'csv-import',
                  icon: <UploadOutlined />,
                  label: 'Импорт CSV / Excel',
                  onClick: () => setCsvImportOpen(true),
                });
              }
              if (hasWeight) {
                moreItems.push({
                  key: 'weight-validation',
                  icon: <CalculatorOutlined />,
                  label: 'Проверка весов',
                  onClick: () => setWeightDrawerOpen(true),
                });
              }
              if (moreItems.length === 0) return null;
              return (
                <Dropdown menu={{ items: moreItems }} trigger={['click']} placement="bottomRight">
                  <Tooltip title="Ещё действия">
                    <Button
                      size="small"
                      icon={<MoreOutlined />}
                      data-testid="more-actions-button"
                      aria-label="Ещё действия"
                    />
                  </Tooltip>
                </Dropdown>
              );
            })()}
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              loading={creating}
              onClick={() => void handleCreate()}
              data-testid="create-item-button"
            >
              Добавить запись
            </Button>
          </>
        )}
      </div>

      {(quickFilters.length > 0 || space?.myRole === 'admin') && (
        <div style={{ marginBottom: 8 }}>
          <QuickFilterBar
            quickFilters={quickFilters}
            activeIds={activeQuickFilterIds}
            onToggle={toggleQuickFilter}
            canManage={space?.myRole === 'admin'}
            onManage={() => setQuickFiltersModalOpen(true)}
          />
        </div>
      )}

      {space?.myRole === 'admin' && (
        <QuickFiltersManagerModal
          open={quickFiltersModalOpen}
          onClose={() => setQuickFiltersModalOpen(false)}
          fields={filterFields}
          quickFilters={quickFilters}
          onSave={persistQuickFilters}
          ensureReferenceOptions={(field) => ensureReferenceOptions(field as FieldDefinition)}
          resolvedRefs={resolvedRefs}
          currentUserFilters={userFilters}
        />
      )}

      {selectedRowKeys.length > 0 && space?.myRole !== 'viewer' && (() => {
        const selectedIds = new Set(selectedRowKeys as string[]);
        const types = new Set<string>();
        for (const it of response?.items ?? []) {
          if (selectedIds.has(it.id)) types.add(it.itemTypeId);
        }
        const selectedTypeId: string | null = types.size === 1 ? [...types][0]! : null;
        const rulesForType = selectedTypeId ? linkRulesByType[selectedTypeId] : undefined;
        return (
        <div
          style={{
            marginBottom: 12,
            padding: '8px 12px',
            background: isDark ? 'var(--color-primary-bg)' : '#e6f4ff',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
          data-testid="selection-toolbar"
        >
          <span style={{ color: isDark ? 'var(--color-text)' : '#666' }}>{selectedRowKeys.length} выбрано</span>
          <Button
            size="small"
            data-testid="bulk-edit-button"
            onClick={() => {
              const first = mergedFields.find((f) => f.fieldType !== 'computed');
              setBulkEditOps([
                makeBulkEditOp(
                  first?.slug ?? null,
                  first ? TEXT_FIELD_TYPES.includes(first.fieldType) : false,
                ),
              ]);
              setBulkEditOpen(true);
            }}
          >
            Изменить
          </Button>
          <Button
            size="small"
            data-testid="bulk-duplicate-button"
            onClick={() => handleOpenDuplicateBulk(selectedRowKeys as string[])}
          >
            Дублировать
          </Button>
          <Popconfirm
            title={`Удалить ${selectedRowKeys.length} записей?`}
            okText="Удалить"
            cancelText="Отмена"
            okButtonProps={{ danger: true }}
            onConfirm={async () => {
              try {
                const result = await bulkDeleteItems(spaceId, { itemIds: selectedRowKeys as string[] });
                void message.success(`Удалено: ${result.deleted}`);
                setSelectedRowKeys([]);
                void load();
              } catch (err) {
                void message.error(err instanceof ApiError ? err.message : 'Ошибка удаления');
              }
            }}
          >
            <Button size="small" danger data-testid="bulk-delete-button">
              Удалить
            </Button>
          </Popconfirm>
          {space?.myRole === 'admin' && selectedTypeId && rulesForType && rulesForType.length > 0 && (
            <Dropdown
              trigger={['click']}
              menu={{
                items: rulesForType.map((rule) => ({
                        key: rule.id,
                        label: rule.name,
                        onClick: () => {
                          Modal.confirm({
                            title: `Связать по правилу «${rule.name}» для ${selectedRowKeys.length} записей?`,
                            okText: 'Применить',
                            cancelText: 'Отмена',
                            onOk: async () => {
                              try {
                                setApplyingLinkRuleId(rule.id);
                                const result = await applyLinkRuleBulk(spaceId, selectedTypeId, rule.id, {
                                  itemIds: selectedRowKeys as string[],
                                });
                                void message.success(
                                  `Обработано: ${result.itemsProcessed}, создано связей: ${result.linksCreated}`,
                                );
                                void load();
                              } catch (err) {
                                void message.error(err instanceof ApiError ? err.message : 'Ошибка применения правила');
                              } finally {
                                setApplyingLinkRuleId(null);
                              }
                            },
                          });
                        },
                      })),
              }}
            >
              <Button
                size="small"
                loading={applyingLinkRuleId !== null}
                data-testid="bulk-apply-link-rules"
              >
                Связать
              </Button>
            </Dropdown>
          )}
          {space?.jiraIntegrationEnabled && (
            <Tooltip title="Открыть Sync Inbox только для выбранных записей">
              <Button
                size="small"
                icon={<SwapOutlined />}
                onClick={() => {
                  setSyncInboxRestrictIds(selectedRowKeys as string[]);
                  setSyncInboxOpen(true);
                }}
                data-testid="bulk-jira-sync-button"
              >
                Sync с Jira
              </Button>
            </Tooltip>
          )}
          {space?.hermesIntegrationEnabled && selectedTypeId && (
            <Tooltip title="Оценить трудоёмкость выбранных записей через Hermes">
              <Button
                size="small"
                icon={<ThunderboltOutlined />}
                onClick={() => {
                  setEstimateInboxItemIds(selectedRowKeys as string[]);
                  setEstimateInboxOpen(true);
                }}
                data-testid="bulk-hermes-estimate-button"
              >
                Оценить выбранные
              </Button>
            </Tooltip>
          )}
          {space?.hermesIntegrationEnabled && (
            <Tooltip title="Открыть интерактивную оценку по каждой выбранной записи">
              <Button
                size="small"
                icon={<MessageOutlined />}
                onClick={() => {
                  const byId = new Map((response?.items ?? []).map((it) => [it.id, it]));
                  for (const id of selectedRowKeys as string[]) {
                    const it = byId.get(id);
                    const dk = (it as { displayKey?: string | null } | undefined)?.displayKey ?? undefined;
                    openHermesSession({ spaceId, itemId: id, itemKey: dk, title: hermesRowTitle(it, dk) });
                  }
                }}
                data-testid="bulk-hermes-interactive-button"
              >
                Интерактивно
              </Button>
            </Tooltip>
          )}
          <Button size="small" type="link" onClick={() => setSelectedRowKeys([])}>
            Снять выделение
          </Button>
        </div>
        );
      })()}

      {loading && response === null ? (
        <div style={{ textAlign: 'center', padding: 64 }} data-testid="items-loading">
          <Spin size="large" />
        </div>
      ) : isKanban && activeView ? (
        <WhiteboardSummaryContext.Provider value={whiteboardBoards}>
        <KanbanBoard
          items={response?.items ?? []}
          fields={fields}
          config={activeView.config as unknown as KanbanViewConfig}
          onCardDrop={handleCardDrop}
          orderLocked={kanbanOrderLocked}
          onAddCard={space?.myRole !== 'viewer' ? handleAddCardToColumn : undefined}
          onCardClick={(item) => openDrawer(item)}
          resolvedRefs={resolvedRefs}
          userAvatarById={userAvatarById}
          itemTypeNameById={
            isSingleType
              ? undefined
              : Object.fromEntries(allItemTypes.map((t) => [t.id, t.name]))
          }
          dictionaryColumnsById={dictionaryColumnsById}
          dictionaryEntryValues={dictionaryEntryValues}
          dictionaryOptionsById={dictionaryOptionsById}
        />
        </WhiteboardSummaryContext.Provider>
      ) : groupTree ? (
        <DndContext
          sensors={groupDragSensors}
          collisionDetection={pointerWithin}
          onDragStart={handleGroupDragStart}
          onDragEnd={(event) => {
            handleGroupDragEnd(event);
            handleDragEnd(event);
          }}
        >
          <SortableContext items={tableSettings.orderedSlugs} strategy={horizontalListSortingStrategy}>
            <div data-testid="grouped-items-table">
              {renderGroupNodes(groupTree, true)}
              {groupTree.length === 0 && (
                <div style={{ textAlign: 'center', padding: 48, color: '#999' }}>
                  Нет записей. Нажмите «Добавить запись».
                </div>
              )}
            </div>
          </SortableContext>
          <DragOverlay dropAnimation={null}>
            {draggedItem && (
              <div style={{
                padding: '6px 12px',
                background: 'var(--color-bg-container)',
                border: '1px solid #1677ff',
                borderRadius: 4,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                fontSize: 13,
                maxWidth: 300,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {(() => {
                  // Find first non-empty text field value as title
                  for (const fd of fields) {
                    if (['text', 'textarea'].includes(fd.fieldType)) {
                      const val = draggedItem.fields[fd.slug];
                      if (val && typeof val === 'string') return val;
                    }
                  }
                  return 'Запись';
                })()}
              </div>
            )}
          </DragOverlay>
        </DndContext>
      ) : (
        <div
          ref={tableAreaRef}
          onKeyDown={handleGridKeyDown}
          style={{ '--lens-tbl-y': `${tableScrollY}px` } as React.CSSProperties}
        >
        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={tableSettings.orderedSlugs} strategy={horizontalListSortingStrategy}>
            <Table<ItemRow>
              rowKey="id"
              columns={excelColumns}
              dataSource={visibleItems}
              pagination={{
                current: page,
                pageSize: tableSettings.settings.pageSize,
                pageSizeOptions: ['25', '50', '100', '200'],
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}–${range[1]} из ${total}`,
                size: 'small',
                position: ['bottomRight'],
                onShowSizeChange: (_current, size) => tableSettings.setPageSize(size),
                onChange: (nextPage, size) => {
                  setPage(nextPage);
                  setActiveCell(null);
                  if (size && size !== tableSettings.settings.pageSize) {
                    tableSettings.setPageSize(size);
                  }
                },
              }}
              scroll={{ x: scrollX, y: tableScrollY }}
              tableLayout="fixed"
              size="small"
              locale={{ emptyText: 'Нет записей. Нажмите «Добавить запись».' }}
              rowSelection={space?.myRole !== 'viewer' ? {
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
              } : undefined}
              summary={() => renderColumnsSummary(visibleItems, columns, space?.myRole !== 'viewer')}
              components={{ header: { cell: ResizableTitle } }}
              onChange={() => {}}
              rowClassName={rowClassName}
              className={`lens-items-table lens-items-table--excel${tableSettings.settings.wrapRows ? " lens-items-table--wrap" : ""}${tableSettings.settings.density === "compact" ? " lens-items-table--compact" : ""}`}
              data-testid="items-table"
            />
          </SortableContext>
        </DndContext>
        </div>
      )}

      {/* Create view modal */}
      <CreateViewModal
        open={showCreateView}
        allItemTypes={allItemTypes}
        defaultItemTypeIds={view.itemTypeIds ?? []}
        ensureReferenceOptions={ensureReferenceOptions}
        resolvedRefs={resolvedRefs}
        onCancel={() => setShowCreateView(false)}
        onCreate={(val) => void handleCreateView(val)}
      />

      {/* Edit view settings modal */}
      {editingViewId && (
        <EditViewModal
          open
          view={views.find((v) => v.id === editingViewId) ?? views[0]!}
          fields={mergedFields}
          groupingFields={filterFields}
          allItemTypes={allItemTypes}
          ensureReferenceOptions={ensureReferenceOptions}
          resolvedRefs={resolvedRefs}
          onCancel={() => setEditingViewId(null)}
          onSave={async (config, extra) => {
            try {
              const updated = await updateView(spaceId, editingViewId, { config, ...(extra ?? {}) });
              setViews((prev) => prev.map((v) => v.id === editingViewId ? updated : v));
              setEditingViewId(null);
            } catch (err) {
              void message.error(err instanceof ApiError ? err.message : 'Не удалось сохранить');
            }
          }}
        />
      )}

      {/* Pagination */}
      {response?.nextCursor && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Button onClick={() => void loadMore()} loading={loading}>
            Загрузить ещё
          </Button>
        </div>
      )}

      {/* Sync Inbox modal (новый управляемый flow push/pull) */}
      {space?.jiraIntegrationEnabled && (
        <SyncInboxModal
          open={syncInboxOpen}
          spaceId={spaceId}
          viewId={view.id}
          viewName={view.name}
          singleTypeId={isSingleType ? singleTypeId : null}
          singleTypeName={isSingleType ? (allItemTypes.find((t) => t.id === singleTypeId)?.name ?? null) : null}
          restrictToItemIds={syncInboxRestrictIds}
          onClose={() => {
            setSyncInboxOpen(false);
            setSyncInboxRestrictIds(null);
          }}
          onApplied={() => void load()}
        />
      )}

      {/* Групповая оценка Hermes для выбранных записей. */}
      {space?.hermesIntegrationEnabled && (
        <EstimateInboxModal
          open={estimateInboxOpen}
          spaceId={spaceId}
          itemIds={estimateInboxItemIds}
          onClose={() => {
            setEstimateInboxOpen(false);
            setEstimateInboxItemIds([]);
          }}
          onApplied={() => void load()}
        />
      )}

      {/* Объединённое массовое редактирование (todos #4): задать значение /
          заменить подстроку, со списком операций. */}
      <Modal
        title={`Изменить ${selectedRowKeys.length} записей`}
        open={bulkEditOpen}
        onCancel={() => setBulkEditOpen(false)}
        okText="Применить"
        cancelText="Отмена"
        width={680}
        okButtonProps={{
          disabled: bulkEditOps.every((op) => {
            if (!op.fieldSlug) return true;
            const f = mergedFields.find((x) => x.slug === op.fieldSlug);
            const isText = f ? TEXT_FIELD_TYPES.includes(f.fieldType) : false;
            if (isText && op.mode === 'replace') return op.search.length === 0;
            return false;
          }),
        }}
        onOk={async () => {
          const fields: Record<string, unknown> = {};
          const replacements: FieldReplacement[] = [];
          for (const op of bulkEditOps) {
            if (!op.fieldSlug) continue;
            const f = mergedFields.find((x) => x.slug === op.fieldSlug);
            if (!f || f.fieldType === 'computed') continue;
            const isText = TEXT_FIELD_TYPES.includes(f.fieldType);
            if (isText && op.mode === 'replace') {
              if (op.search.length > 0) {
                replacements.push({ fieldSlug: op.fieldSlug, search: op.search, replace: op.replace });
              }
            } else {
              let v: unknown = op.value;
              if (v === '' || v === undefined) v = null;
              else if (f.fieldType === 'number') {
                const n = Number(v);
                v = Number.isNaN(n) ? null : n;
              }
              fields[op.fieldSlug] = v;
            }
          }
          if (Object.keys(fields).length === 0 && replacements.length === 0) {
            setBulkEditOpen(false);
            return;
          }
          try {
            const result = await bulkUpdateItems(spaceId, {
              itemIds: selectedRowKeys as string[],
              fields,
              replacements: replacements.length > 0 ? replacements : undefined,
            });
            const errorsCount = result.errors?.length ?? 0;
            const skipped = result.skipped ?? 0;
            if (errorsCount > 0) {
              void message.warning(
                `Обновлено: ${result.updated}; с ошибкой: ${errorsCount}` +
                  (skipped > 0 ? `; пропущено: ${skipped}` : ''),
              );
            } else if (skipped > 0) {
              void message.warning(`Обновлено: ${result.updated}; пропущено: ${skipped}`);
            } else {
              void message.success(`Обновлено: ${result.updated}`);
            }
            setBulkEditOpen(false);
            setSelectedRowKeys([]);
            void load();
          } catch (err) {
            void message.error(err instanceof ApiError ? err.message : 'Ошибка обновления');
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
          {bulkEditOps.map((op, idx) => {
            const field = mergedFields.find((f) => f.slug === op.fieldSlug) ?? null;
            const isText = field ? TEXT_FIELD_TYPES.includes(field.fieldType) : false;
            const update = (patch: Partial<BulkEditOp>) =>
              setBulkEditOps((prev) => prev.map((o) => (o.key === op.key ? { ...o, ...patch } : o)));
            return (
              <div
                key={op.key}
                style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 8, border: '1px solid var(--color-border)', borderRadius: 6 }}
                data-testid={`bulk-edit-op-${idx}`}
              >
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Select
                    style={{ flex: 1 }}
                    placeholder="Поле"
                    showSearch
                    value={op.fieldSlug ?? undefined}
                    data-testid={`bulk-edit-field-${idx}`}
                    filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                    onChange={(slug) => {
                      const f = mergedFields.find((x) => x.slug === slug);
                      const t = f ? TEXT_FIELD_TYPES.includes(f.fieldType) : false;
                      update({ fieldSlug: slug, mode: t ? 'replace' : 'set', value: undefined, search: '', replace: '' });
                    }}
                    options={mergedFields
                      .filter((f) => f.fieldType !== 'computed')
                      .map((f) => ({ value: f.slug, label: f.name }))}
                  />
                  {isText && (
                    <Select
                      style={{ width: 200 }}
                      value={op.mode}
                      data-testid={`bulk-edit-mode-${idx}`}
                      onChange={(m: 'set' | 'replace') => update({ mode: m })}
                      options={[
                        { value: 'replace', label: 'Заменить подстроку' },
                        { value: 'set', label: 'Задать значение' },
                      ]}
                    />
                  )}
                  {bulkEditOps.length > 1 && (
                    <Button
                      size="small"
                      type="text"
                      danger
                      data-testid={`bulk-edit-remove-${idx}`}
                      onClick={() => setBulkEditOps((prev) => prev.filter((o) => o.key !== op.key))}
                    >
                      ✕
                    </Button>
                  )}
                </div>
                {field && isText && op.mode === 'replace' ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Input
                      placeholder="найти подстроку"
                      value={op.search}
                      onChange={(e) => update({ search: e.target.value })}
                      data-testid={`bulk-edit-search-${idx}`}
                    />
                    <Input
                      placeholder="заменить на"
                      value={op.replace}
                      onChange={(e) => update({ replace: e.target.value })}
                      data-testid={`bulk-edit-replace-${idx}`}
                    />
                  </div>
                ) : field ? (
                  renderBulkSetEditor(field, op.value, (v) => update({ value: v }))
                ) : null}
              </div>
            );
          })}
          <Button
            size="small"
            type="dashed"
            data-testid="bulk-edit-add-op"
            onClick={() => {
              const first = mergedFields.find((f) => f.fieldType !== 'computed');
              setBulkEditOps((prev) => [
                ...prev,
                makeBulkEditOp(first?.slug ?? null, first ? TEXT_FIELD_TYPES.includes(first.fieldType) : false),
              ]);
            }}
          >
            + Добавить
          </Button>
          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
            Несколько замен применяются по очереди. Пустое значение в режиме «Задать значение» очистит поле.
          </span>
        </div>
      </Modal>

      {/* Weight validation drawer (Wave 2) */}
      {isSingleType && (
        <WeightValidationDrawer
          open={weightDrawerOpen}
          onClose={() => setWeightDrawerOpen(false)}
          spaceId={spaceId}
          itemTypeId={singleTypeId!}
          quarter={
          (activeView?.config as any)?.filters?.find(
            (f: any) => f.fieldSlug === 'quarter',
          )?.value as string ?? 'Q2 2026'
        }
        />
      )}

      {/* CSV import modal (M10) */}
      {isSingleType && (
        <CsvImportModal
          open={csvImportOpen}
          spaceId={spaceId}
          itemTypeId={singleTypeId!}
          fields={fields}
          viewFilters={((view.config as { filters?: FilterLeaf[] } | null)?.filters ?? [])}
          userFilters={[...userFilters, ...activeQuickFilters.flatMap((q) => q.filters)]}
          dictionaryOptionsById={dictionaryOptionsById}
          ensureReferenceOptions={ensureReferenceOptions}
          onClose={() => setCsvImportOpen(false)}
          onComplete={() => { setCsvImportOpen(false); void load(); }}
        />
      )}

      {/* Item drawer (M9) */}
      <ItemDrawer
        open={!!drawerItem}
        onClose={closeDrawer}
        item={drawerItem}
        fields={drawerItem ? (allItemTypes.find((itemType) => itemType.id === drawerItem.itemTypeId)?.fields ?? allFields) : allFields}
        spaceId={spaceId}
        spaceSlug={space?.slug}
        itemTypeId={(drawerItem as any)?.itemTypeId ?? singleTypeId ?? (view.itemTypeIds ?? [])[0]}
        currentUserId={user?.id ?? ''}
        myRole={space?.myRole}
        jiraEnabled={space?.jiraIntegrationEnabled ?? false}
        hermesEnabled={space?.hermesIntegrationEnabled ?? false}
        resolvedRefs={resolvedRefs}
        onFieldSave={(fieldSlug, val) => {
          const target = drawerItem ?? lastDrawerItemRef.current;
          if (target) void handleFieldSave(target.id, fieldSlug, val);
        }}
        onDelete={handleDelete}
        onDuplicate={(itemId) => {
          // Открываем модалку с предзаполненными значениями исходной записи.
          // drawerItem гарантированно совпадает с itemId (это его itemId).
          const source = drawerItem ?? (response?.items ?? []).find((it) => it.id === itemId);
          if (!source) return;
          handleOpenDuplicateSingle(source);
        }}
        onLinkedItemClick={handleLinkedItemClick}
        onNavigate={handleDrawerNavigate}
        navPosition={drawerNav}
        availableTypes={allItemTypes}
        onTypeChanged={() => {
          // После смены типа запись может выпасть из фильтра текущего view —
          // перечитываем список целиком.
          void load();
        }}
      />

      {/* M11.6: Колонка из связей */}
      <LinkAggregateColumnModal
        open={linkAggregateModalOpen}
        spaceId={spaceId}
        itemTypeIds={view.itemTypeIds ?? []}
        availableItemTypeIds={allItemTypes.map((t) => t.id)}
        itemTypeNames={Object.fromEntries(allItemTypes.map((t) => [t.id, t.name]))}
        initialConfig={editingLinkAggregateColumn}
        onClose={() => {
          setLinkAggregateModalOpen(false);
          setEditingLinkAggregateColumn(null);
        }}
        onSubmit={handleSubmitLinkAggregateColumn}
      />

      <ViewComputedColumnModal
        open={viewComputedModalOpen}
        spaceId={spaceId}
        itemTypeIds={view.itemTypeIds ?? []}
        itemTypeNames={Object.fromEntries(allItemTypes.map((t) => [t.id, t.name]))}
        onClose={() => setViewComputedModalOpen(false)}
        onSubmit={handleAddViewComputedColumn}
      />

      <ReferenceAttributeColumnModal
        open={referenceAttributeModalOpen}
        fields={mergedFields}
        initialConfig={editingReferenceAttributeColumn}
        onClose={() => {
          setReferenceAttributeModalOpen(false);
          setEditingReferenceAttributeColumn(null);
        }}
        onSubmit={handleSubmitReferenceAttributeColumn}
      />

      {/* Create / Duplicate record modal */}
      <Modal
        title={
          createModalMode === 'duplicate-single'
            ? 'Дублировать запись'
            : createModalMode === 'duplicate-bulk'
            ? `Дублировать ${createModalSourceItemIds?.length ?? 0} записей`
            : 'Новая запись'
        }
        open={createModalOpen}
        onCancel={handleCreateModalCancel}
        onOk={() => void handleCreateConfirm()}
        okText={createModalMode === 'create' ? 'Создать' : 'Дублировать'}
        cancelText="Отмена"
        confirmLoading={creating}
        width={1100}
      >
        {createModalExpandedField ? (
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: '60vh' }}>
            <MarkdownEditorModal
              embedded
              open
              value={createModalExpandedField.value}
              title={createModalExpandedField.name}
              mode={createModalExpandedField.mode}
              onUploadImage={handleUploadImage}
              onClose={() => setCreateModalExpandedField(null)}
              onSave={(next) => {
                const slug = createModalExpandedField.slug;
                setCreateModalFields((prev) => ({ ...prev, [slug]: next.trim() === '' ? undefined : next }));
              }}
            />
          </div>
        ) : (() => {
          const typeFields = createModalTypeId
            ? allItemTypes.find((t) => t.id === createModalTypeId)?.fields ?? mergedFields
            : mergedFields;
          const editableFields = typeFields.filter(
            (f) => f.fieldType !== 'computed',
          );
          if (editableFields.length === 0) {
            return (
              <span style={{ color: '#999' }}>Нет полей для заполнения. Запись будет создана пустой.</span>
            );
          }
          const hint =
            createModalMode === 'duplicate-single'
              ? 'Пустые поля скопируются из исходной записи. Изменённые — установятся новыми значениями.'
              : createModalMode === 'duplicate-bulk'
              ? 'Заполненные поля будут установлены во всех копиях. Пустые — скопируются из каждой исходной записи.'
              : null;
          const canPickType = createModalMode === 'create' && allItemTypes.length > 1;
          return (
            <>
            {canPickType && (
              <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: isDark ? 'var(--color-text)' : '#666', fontSize: 13 }}>Тип записи:</span>
                <Select
                  size="small"
                  style={{ minWidth: 220 }}
                  value={createModalTypeId ?? undefined}
                  onChange={(val) => handleChangeCreateType(val)}
                  options={allItemTypes.map((t) => ({
                    value: t.id,
                    label: `${t.icon ?? ''} ${t.name}`.trim(),
                  }))}
                  data-testid="create-type-select"
                />
              </div>
            )}
            {hint && (
              <div
                style={{
                  marginBottom: 12,
                  padding: '8px 12px',
                  background: isDark ? 'rgba(22,119,255,0.12)' : '#f0f7ff',
                  border: `1px solid ${isDark ? 'rgba(145,202,255,0.35)' : '#91caff'}`,
                  borderRadius: 4,
                  fontSize: 12,
                  color: isDark ? '#69b1ff' : '#1677ff',
                }}
                data-testid="duplicate-modal-hint"
              >
                {hint}
              </div>
            )}
            <Descriptions className="lens-create-fields" column={1} size="small" bordered>
              {editableFields.map((field) => {
                const wasAutofilled = field.slug in createModalAutofilled;
                const stillMatches =
                  wasAutofilled &&
                  createModalFields[field.slug] === createModalAutofilled[field.slug] &&
                  createModalFields[field.slug] !== undefined;
                const isModified = wasAutofilled && !stillMatches;
                const isDuplicateMode =
                  createModalMode === 'duplicate-single' || createModalMode === 'duplicate-bulk';
                const isCleared = createModalClearedSlugs.has(field.slug);
                const toggleCleared = () => {
                  setCreateModalClearedSlugs((prev) => {
                    const next = new Set(prev);
                    if (next.has(field.slug)) next.delete(field.slug);
                    else next.add(field.slug);
                    return next;
                  });
                  // При активации «очистить» убираем текущее значение из модалки —
                  // иначе оно уехало бы как override вместо null.
                  if (!isCleared) {
                    setCreateModalFields((prev) => {
                      const next = { ...prev };
                      delete next[field.slug];
                      return next;
                    });
                  }
                };
                return (
                <Descriptions.Item
                  key={field.slug}
                  label={
                    <span>
                      {field.name}{field.required ? ' *' : ''}
                      {stillMatches && (
                        <span
                          style={{ marginLeft: 6, color: '#1677ff', fontSize: 11, fontWeight: 'normal' }}
                          title="Значение подставлено из фильтра представления (FR-4.1)"
                          data-testid={`autofill-hint-${field.slug}`}
                        >
                          · из фильтра
                        </span>
                      )}
                      {isModified && (
                        <span
                          style={{ marginLeft: 6, color: '#d4380d', fontSize: 11, fontWeight: 'normal' }}
                          title="Значение отличается от фильтра представления — запись не будет видна в этом представлении (FR-4.1)"
                          data-testid={`autofill-warning-${field.slug}`}
                        >
                          ⚠ запись не попадёт в это представление
                        </span>
                      )}
                      {isDuplicateMode && (
                        <a
                          onClick={(e) => { e.preventDefault(); toggleCleared(); }}
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            fontWeight: 'normal',
                            color: isCleared ? '#d4380d' : '#999',
                            cursor: 'pointer',
                          }}
                          title={isCleared
                            ? 'Снять отметку «Очистить» — поле снова будет копироваться из источника'
                            : 'Пометить поле как «Очистить» — в копии будет пусто'}
                          data-testid={`duplicate-clear-toggle-${field.slug}`}
                        >
                          {isCleared ? '↺ не очищать' : '✕ очистить'}
                        </a>
                      )}
                    </span>
                  }
                >
                  {isCleared ? (
                    <span
                      data-testid={`duplicate-cleared-${field.slug}`}
                      style={{
                        display: 'inline-block',
                        padding: '4px 11px',
                        color: isDark ? '#ff7875' : '#d4380d',
                        background: isDark ? 'rgba(212,56,13,0.15)' : '#fff1f0',
                        border: `1px dashed ${isDark ? 'rgba(255,163,158,0.4)' : '#ffa39e'}`,
                        borderRadius: 4,
                        fontSize: 12,
                      }}
                    >
                      будет очищено
                    </span>
                  ) : field.fieldType === 'text' || field.fieldType === 'url' ? (
                    <Input
                      placeholder={field.name}
                      value={(createModalFields[field.slug] as string) ?? ''}
                      onChange={(e) => setCreateModalFields((prev) => ({ ...prev, [field.slug]: e.target.value || undefined }))}
                    />
                  ) : field.fieldType === 'markdown' || field.fieldType === 'textarea' ? (
                    <CreateMarkdownField
                      field={field}
                      value={(createModalFields[field.slug] as string) ?? ''}
                      onChange={(next) => setCreateModalFields((prev) => ({ ...prev, [field.slug]: next.trim() === '' ? undefined : next }))}
                      onExpand={(val) => setCreateModalExpandedField({
                        slug: field.slug,
                        name: field.name,
                        value: val,
                        mode: field.fieldType === 'markdown' ? 'markdown' : 'textarea',
                      })}
                      onUploadImage={handleUploadImage}
                    />
                  ) : field.fieldType === 'number' ? (
                    <Input
                      type="number"
                      placeholder={field.name}
                      value={createModalFields[field.slug] != null ? String(createModalFields[field.slug]) : ''}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        setCreateModalFields((prev) => ({
                          ...prev,
                          [field.slug]: e.target.value.trim() === '' ? undefined : Number.isNaN(n) ? undefined : n,
                        }));
                      }}
                    />
                  ) : field.fieldType === 'select' ? (
                    <Select
                      style={{ width: '100%' }}
                      placeholder={field.name}
                      allowClear
                      value={(createModalFields[field.slug] as string) ?? undefined}
                      onChange={(v) => setCreateModalFields((prev) => ({ ...prev, [field.slug]: v ?? undefined }))}
                      options={((field.config as { options?: Array<{ value: string; label: string }> })?.options ?? []).map((o) => ({
                        value: o.value, label: o.label || o.value,
                      }))}
                    />
                  ) : field.fieldType === 'multi_select' ? (
                    <Select
                      mode="multiple"
                      style={{ width: '100%' }}
                      placeholder={field.name}
                      value={(createModalFields[field.slug] as string[]) ?? []}
                      onChange={(v) => setCreateModalFields((prev) => ({ ...prev, [field.slug]: v.length > 0 ? v : undefined }))}
                      options={((field.config as { options?: Array<{ value: string; label: string }> })?.options ?? []).map((o) => ({
                        value: o.value, label: o.label || o.value,
                      }))}
                    />
                  ) : field.fieldType === 'boolean' ? (
                    <Select
                      style={{ width: '100%' }}
                      placeholder={field.name}
                      allowClear
                      value={createModalFields[field.slug] as boolean | undefined}
                      onChange={(v) => setCreateModalFields((prev) => ({ ...prev, [field.slug]: v ?? undefined }))}
                      options={[{ value: true, label: 'Да' }, { value: false, label: 'Нет' }]}
                    />
                  ) : field.fieldType === 'date' ? (
                    <DatePicker
                      style={{ width: '100%' }}
                      showTime={(field.config as { includeTime?: boolean })?.includeTime ?? false}
                      format={datePickerFormat((field.config as { includeTime?: boolean })?.includeTime ?? false)}
                      value={createModalFields[field.slug] ? dayjs(createModalFields[field.slug] as string) : null}
                      onChange={(d) => setCreateModalFields((prev) => ({ ...prev, [field.slug]: dateFieldValue(d, (field.config as { includeTime?: boolean })?.includeTime ?? false) ?? undefined }))}
                    />
                  ) : field.fieldType === 'reference' || field.fieldType === 'user' ? (
                    (() => {
                      const isMulti = ((field.config as { multiple?: boolean })?.multiple) ?? false;
                      const opts = referenceOptionsForField(field) ?? [];
                      return (
                        <Select
                          style={{ width: '100%' }}
                          placeholder={field.name}
                          allowClear
                          showSearch
                          mode={isMulti ? 'multiple' : undefined}
                          value={createModalFields[field.slug] as string | string[] | undefined}
                          onChange={(v) => {
                            // Как в редактировании карточки: выбранная группа сразу
                            // разворачивается в её участников.
                            const next = field.fieldType === 'user' ? expandUserGroupSelection(v, userGroups) : v;
                            setCreateModalFields((prev) => ({ ...prev, [field.slug]: next ?? undefined }));
                          }}
                          popupRender={
                            field.fieldType === 'user' && isMulti
                              ? (menu) =>
                                  renderUserBulkPopup(menu, opts.map((o) => o.value), createModalFields[field.slug], (next) =>
                                    setCreateModalFields((prev) => ({ ...prev, [field.slug]: next.length > 0 ? next : undefined })),
                                  )
                              : undefined
                          }
                          onFocus={() => { void ensureReferenceOptions(field); }}
                          filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                          options={
                            (field.fieldType === 'user'
                              ? withUserGroupOptions(opts, userGroups, isMulti)
                              : opts) as DefaultOptionType[]
                          }
                        />
                      );
                    })()
                  ) : (
                    <Input
                      placeholder={field.name}
                      value={(createModalFields[field.slug] as string) ?? ''}
                      onChange={(e) => setCreateModalFields((prev) => ({ ...prev, [field.slug]: e.target.value || undefined }))}
                    />
                  )}
                  {isDuplicateMode &&
                    !isCleared &&
                    ['text', 'url', 'textarea', 'markdown'].includes(field.fieldType) && (
                      <div style={{ marginTop: 8 }} data-testid={`duplicate-replace-${field.slug}`}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Input
                            size="small"
                            placeholder="найти подстроку"
                            value={createModalReplacements[field.slug]?.search ?? ''}
                            onChange={(e) =>
                              setCreateModalReplacements((prev) => ({
                                ...prev,
                                [field.slug]: { search: e.target.value, replace: prev[field.slug]?.replace ?? '' },
                              }))
                            }
                            data-testid={`duplicate-replace-search-${field.slug}`}
                          />
                          <Input
                            size="small"
                            placeholder="заменить на"
                            value={createModalReplacements[field.slug]?.replace ?? ''}
                            onChange={(e) =>
                              setCreateModalReplacements((prev) => ({
                                ...prev,
                                [field.slug]: { search: prev[field.slug]?.search ?? '', replace: e.target.value },
                              }))
                            }
                            data-testid={`duplicate-replace-value-${field.slug}`}
                          />
                        </div>
                        <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: 'var(--color-text-secondary)' }}>
                          ↪ заменит все вхождения в копируемом значении каждой записи (напр. Q2 → Q3)
                        </span>
                      </div>
                    )}
                </Descriptions.Item>
                );
              })}
            </Descriptions>
            </>
          );
        })()}
      </Modal>
    </div>
  );
}

// ─── Markdown/textarea editor for create modal ───────────────────────────────

interface CreateMarkdownFieldProps {
  field: FieldDefinition;
  value: string;
  onChange: (next: string) => void;
  onExpand: (value: string) => void;
  onUploadImage?: (file: File) => Promise<string>;
}

function CreateMarkdownField({ field, value, onChange, onExpand, onUploadImage }: CreateMarkdownFieldProps): JSX.Element {
  const isMarkdown = field.fieldType === 'markdown';
  if (isMarkdown) {
    return (
      <div style={{ position: 'relative' }}>
        <MarkdownInlineEditor
          value={value}
          onChange={onChange}
          placeholder={field.name}
          onUploadImage={onUploadImage}
        />
        <Tooltip title="Открыть в большом окне">
          <Button
            size="small"
            type="text"
            icon={<ExpandAltOutlined />}
            onClick={() => onExpand(value)}
            style={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}
          />
        </Tooltip>
      </div>
    );
  }
  return (
    <div style={{ position: 'relative' }}>
      <Input.TextArea
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        placeholder={field.name}
        style={{ paddingRight: 28, resize: 'vertical' }}
      />
      <Tooltip title="Открыть в большом окне">
        <Button
          size="small"
          type="text"
          icon={<ExpandAltOutlined />}
          onClick={() => onExpand(value)}
          style={{ position: 'absolute', top: 2, right: 2 }}
        />
      </Tooltip>
    </div>
  );
}

// ─── Sortable view tab (drag-and-drop reordering) ────────────────────────────

function SortableViewTab({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

interface CreateViewFormValue {
  name: string;
  icon?: string;
  viewType: 'table' | 'kanban';
  itemTypeIds: string[];
  columnField?: string;
  columnFieldPath?: string[];
  cardTitleField?: string;
  cardSubtitleField?: string;
  filters: FilterLeaf[];
}

function CreateViewModal({
  open,
  allItemTypes,
  defaultItemTypeIds,
  onCancel,
  onCreate,
  ensureReferenceOptions,
  resolvedRefs,
}: {
  open: boolean;
  allItemTypes: ItemType[];
  defaultItemTypeIds: string[];
  onCancel: () => void;
  onCreate: (value: CreateViewFormValue) => void;
  ensureReferenceOptions?: (field: FieldDefinition) => Promise<Array<{ value: string; label: string }>>;
  resolvedRefs?: Record<string, string>;
}): JSX.Element {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState<string | null>(null);
  const [viewType, setViewType] = useState<'table' | 'kanban'>('table');
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[]>(defaultItemTypeIds);
  const [columnField, setColumnField] = useState<string | undefined>();
  const [columnFieldPath, setColumnFieldPath] = useState<string[]>([]);
  const [cardTitleField, setCardTitleField] = useState<string | undefined>();
  const [cardSubtitleField, setCardSubtitleField] = useState<string | undefined>();
  const [filters, setFilters] = useState<FilterLeaf[]>([]);

  const reset = () => {
    setName('');
    setIcon(null);
    setViewType('table');
    setSelectedTypeIds(defaultItemTypeIds);
    setColumnField(undefined);
    setColumnFieldPath([]);
    setCardTitleField(undefined);
    setCardSubtitleField(undefined);
    setFilters([]);
  };

  // Fields common across all selected types (for kanban selectors)
  const commonFields = useMemo(() => {
    if (selectedTypeIds.length === 0) return [];
    const fieldArrays = selectedTypeIds.map((tid) => {
      const it = allItemTypes.find((t) => t.id === tid);
      return it?.fields ?? [];
    });
    if (fieldArrays.length === 1) return fieldArrays[0]!;
    // Intersection by slug
    const firstSlugs = new Set(fieldArrays[0]!.map((f) => f.slug));
    for (let i = 1; i < fieldArrays.length; i++) {
      const slugs = new Set(fieldArrays[i]!.map((f) => f.slug));
      for (const s of firstSlugs) {
        if (!slugs.has(s)) firstSlugs.delete(s);
      }
    }
    return fieldArrays[0]!.filter((f) => firstSlugs.has(f.slug));
  }, [selectedTypeIds, allItemTypes]);

  // All fields merged (dedup by slug) for non-kanban field selectors
  const mergedFieldsForModal = useMemo(() => {
    const seen = new Set<string>();
    const result: FieldDefinition[] = [];
    for (const tid of selectedTypeIds) {
      const it = allItemTypes.find((t) => t.id === tid);
      if (!it) continue;
      for (const f of it.fields) {
        if (!seen.has(f.slug)) {
          seen.add(f.slug);
          result.push(f);
        }
      }
    }
    return result;
  }, [selectedTypeIds, allItemTypes]);

  const selectableFields = commonFields.filter((f) => f.fieldType === 'select' || f.fieldType === 'reference');

  const isValid =
    name.trim() &&
    selectedTypeIds.length > 0 &&
    (viewType === 'table' || (columnField && cardTitleField));

  return (
    <Modal
      title="Новое представление"
      open={open}
      onCancel={() => { reset(); onCancel(); }}
      onOk={() => {
        if (!isValid) return;
        onCreate({
          name: name.trim(),
          icon: icon ?? undefined,
          viewType,
          itemTypeIds: selectedTypeIds,
          columnField,
          columnFieldPath: columnFieldPath.length > 0 ? columnFieldPath : undefined,
          cardTitleField,
          cardSubtitleField,
          filters,
        });
        reset();
      }}
      okText="Создать"
      cancelText="Отмена"
      okButtonProps={{ disabled: !isValid }}
    >
      <Form layout="vertical">
        <Form.Item label="Название">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <SpaceIconPicker iconOnly value={icon} onChange={setIcon} />
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: Бэклог, Канбан по статусу"
              autoFocus
              style={{ flex: 1 }}
            />
          </div>
        </Form.Item>
        <Form.Item label="Типы записей" required>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Выберите типы записей"
            value={selectedTypeIds}
            onChange={setSelectedTypeIds}
            options={allItemTypes.map((t) => ({
              value: t.id,
              label: `${t.icon ? t.icon + ' ' : ''}${t.name}`,
            }))}
          />
        </Form.Item>
        <Form.Item label="Тип">
          <Select
            value={viewType}
            onChange={setViewType}
            options={[
              { value: 'table', label: <span><TableOutlined /> Таблица</span> },
              { value: 'kanban', label: <span><AppstoreOutlined /> Канбан</span> },
            ]}
          />
        </Form.Item>
        {viewType === 'kanban' && (
          <>
            <Form.Item
              label="Раскладывать по полю"
              required
              extra="Поле-список, по значениям которого встанут колонки. Должно быть у всех выбранных типов записей."
            >
              <Select
                placeholder="Выберите поле"
                value={columnField}
                onChange={(v) => { setColumnField(v); setColumnFieldPath([]); }}
                options={selectableFields.map((f) => ({ value: f.slug, label: f.name }))}
                allowClear
              />
            </Form.Item>
            {(() => {
              const cf = commonFields.find((f) => f.slug === columnField);
              if (!cf || cf.fieldType !== 'reference') return null;
              return (
                <Form.Item
                  label="Брать значение из справочника"
                  extra="Не обязательно. Колонки можно строить не по самому полю, а по колонке справочника — и дальше по цепочке, до 5 шагов."
                >
                  <KanbanColumnPathPicker
                    referenceField={cf}
                    value={columnFieldPath}
                    onChange={setColumnFieldPath}
                  />
                </Form.Item>
              );
            })()}
            <Form.Item label="Заголовок карточки" required>
              <Select
                placeholder="Выберите поле"
                value={cardTitleField}
                onChange={setCardTitleField}
                options={[
                  ...(selectedTypeIds.length > 1 ? [{ value: ITEM_TYPE_SLUG, label: 'Тип записи' }] : []),
                  ...mergedFieldsForModal.map((f) => ({ value: f.slug, label: f.name })),
                ]}
                allowClear
              />
            </Form.Item>
            <Form.Item label="Вторая строка карточки">
              <Select
                placeholder="Не выбрано"
                value={cardSubtitleField}
                onChange={setCardSubtitleField}
                options={[
                  ...(selectedTypeIds.length > 1 ? [{ value: ITEM_TYPE_SLUG, label: 'Тип записи' }] : []),
                  ...mergedFieldsForModal.map((f) => ({ value: f.slug, label: f.name })),
                ]}
                allowClear
              />
            </Form.Item>
          </>
        )}
        <Form.Item label="Стартовые фильтры">
          <FilterEditor
            fields={mergedFieldsForModal}
            filters={filters}
            onUpdate={(idx, patch) => setFilters((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)))}
            onRemove={(idx) => setFilters((prev) => prev.filter((_, i) => i !== idx))}
            onAdd={() => {
              const firstField = mergedFieldsForModal[0];
              if (!firstField) return;
              const ops = getOperatorsForFieldType(firstField.fieldType);
              setFilters((prev) => [
                ...prev,
                {
                  fieldSlug: firstField.slug,
                  operator: ops[0] ?? 'eq',
                  value: undefined,
                  ...(prev.length > 0 ? { combinator: 'and' as const } : {}),
                },
              ]);
            }}
            onClear={() => setFilters([])}
            ensureReferenceOptions={ensureReferenceOptions}
            resolvedRefs={resolvedRefs}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── Edit view settings modal ─────────────────────────────────────────────────

function SortableColumnChip({ slug, label, onRemove }: { slug: string; label: string; onRemove: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: slug });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 6px 2px 4px',
    background: 'var(--color-bg-layout)',
    border: '1px solid var(--color-border)',
    borderRadius: 4,
    fontSize: 13,
    cursor: 'grab',
    userSelect: 'none',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <HolderOutlined style={{ color: '#bbb', fontSize: 11 }} />
      <span>{label}</span>
      <span
        role="button"
        aria-label="Убрать колонку"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        style={{ display: 'inline-flex', cursor: 'pointer', color: '#999', padding: '0 2px' }}
      >
        <CloseOutlined style={{ fontSize: 10 }} />
      </span>
    </div>
  );
}

function VisibleColumnsEditor({
  visibleColumns,
  fields,
  onChange,
}: {
  visibleColumns: string[];
  fields: FieldDefinition[];
  onChange: (next: string[]) => void;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const fieldNameMap = new Map(fields.map((f) => [f.slug, f.name]));
  const availableFields = fields.filter((f) => !visibleColumns.includes(f.slug));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = visibleColumns.indexOf(active.id as string);
    const newIdx = visibleColumns.indexOf(over.id as string);
    if (oldIdx === -1 || newIdx === -1) return;
    onChange(arrayMove(visibleColumns, oldIdx, newIdx));
  };

  return (
    <div>
      {visibleColumns.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visibleColumns} strategy={rectSortingStrategy}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
              {visibleColumns.map((slug) => (
                <SortableColumnChip
                  key={slug}
                  slug={slug}
                  label={fieldNameMap.get(slug) ?? slug}
                  onRemove={() => onChange(visibleColumns.filter((s) => s !== slug))}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <Select
        size="small"
        style={{ width: '100%' }}
        placeholder={visibleColumns.length === 0 ? 'Выберите поля' : '+ Добавить колонку'}
        value={undefined}
        onChange={(slug) => { if (slug) onChange([...visibleColumns, slug]); }}
        options={availableFields.map((f) => ({ value: f.slug, label: f.name }))}
        disabled={availableFields.length === 0}
      />
    </div>
  );
}

interface EditViewModalProps {
  open: boolean;
  view: View;
  fields: FieldDefinition[];
  /** Поля для уровней группировки — тот же список, что у попапа «Группировка»
   *  на панели: с колонками-атрибутами справочников (`_refattr:`), «Тип записи»
   *  и мета-полями. Если не передан — берутся `fields`. */
  groupingFields?: FieldDefinition[];
  allItemTypes: ItemType[];
  onCancel: () => void;
  onSave: (config: Record<string, unknown>, extra?: { isDefault?: boolean; icon?: string | null; name?: string; itemTypeIds?: string[] }) => void;
  ensureReferenceOptions?: (field: FieldDefinition) => Promise<Array<{ value: string; label: string }>>;
  resolvedRefs?: Record<string, string>;
}

/**
 * Упорядочивает опции статусного поля по сохранённому порядку колонок канбана.
 * Значения, которых нет в `order`, идут после перечисленных в порядке опций поля.
 * Та же логика, что в buildKanbanLayout — иначе список в настройках и доска разойдутся.
 */
function sortKanbanOptions<T extends { value: string }>(options: T[], order: string[]): T[] {
  if (order.length === 0) return options;
  const pos = new Map(order.map((v, i) => [v, i]));
  return options
    .map((o, idx) => ({ o, r: pos.get(o.value) ?? order.length + idx }))
    .sort((a, b) => a.r - b.r)
    .map((x) => x.o);
}

function SortableKanbanColumnRow({
  value,
  label,
  color,
  checked,
  onToggle,
}: {
  value: string;
  label: string;
  color?: string;
  checked: boolean;
  onToggle: (on: boolean) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: value });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    marginBottom: 4,
    padding: '4px 8px',
  };
  return (
    <div ref={setNodeRef} style={style} className="lens-group-row" data-testid={`kanban-col-row-${value}`}>
      <span {...attributes} {...listeners} className="lens-group-row__handle" aria-label="Перетащить">
        <HolderOutlined />
      </span>
      <Checkbox checked={checked} onChange={(e) => onToggle(e.target.checked)} />
      {color && (
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
      )}
      <span
        className="lens-group-row__name"
        style={{ fontWeight: 500, color: checked ? 'var(--color-text)' : 'var(--color-text-secondary)' }}
      >
        {label}
      </span>
    </div>
  );
}

/**
 * Список колонок канбана в настройках представления: галочка = показывать,
 * перетаскивание за ручку = порядок на доске. Скрытые колонки остаются в списке
 * и тоже сортируются — чтобы при включении встать на нужное место.
 */
function KanbanColumnsEditor({
  options,
  checked,
  order,
  onToggle,
  onReorder,
}: {
  options: Array<{ value: string; label: string; color?: string }>;
  checked: string[];
  order: string[];
  onToggle: (value: string, on: boolean) => void;
  onReorder: (order: string[]) => void;
}) {
  const sorted = sortKanbanOptions(options, order);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = sorted.map((o) => o.value);
    const oldIdx = ids.indexOf(String(active.id));
    const newIdx = ids.indexOf(String(over.id));
    if (oldIdx === -1 || newIdx === -1) return;
    onReorder(arrayMove(ids, oldIdx, newIdx));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={sorted.map((o) => o.value)} strategy={verticalListSortingStrategy}>
        {sorted.map((o) => (
          <SortableKanbanColumnRow
            key={o.value}
            value={o.value}
            label={o.label}
            color={o.color}
            checked={checked.includes(o.value)}
            onToggle={(on) => onToggle(o.value, on)}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}

/** Отступы внутри окна настроек представления — одни на все поля, чтобы блоки не «плавали». */
const VIEW_SETTINGS_GAP = 20;
const VIEW_SETTINGS_ITEM: React.CSSProperties = { marginBottom: 12 };
const VIEW_SETTINGS_ITEM_LAST: React.CSSProperties = { marginBottom: 0 };

/** Заголовок смысловой группы полей в окне настроек представления. */
function ViewSettingsSection({
  title,
  last,
  children,
}: {
  title: string;
  last?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section style={{ marginBottom: last ? VIEW_SETTINGS_GAP : VIEW_SETTINGS_GAP + 4 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: 'var(--color-text-secondary)',
          paddingBottom: 6,
          marginBottom: 12,
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        {title}
      </div>
      {children}
    </section>
  );
}

function EditViewModal({ open, view, fields, groupingFields, allItemTypes, onCancel, onSave, ensureReferenceOptions, resolvedRefs }: EditViewModalProps): JSX.Element {
  const config = view.config as Record<string, unknown>;
  const isKanban = view.viewType === 'kanban';

  const [icon, setIcon] = useState<string | null>(() => (view as any).icon ?? null);
  const [name, setName] = useState<string>(view.name);
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[]>(() => view.itemTypeIds ?? []);

  // Filters
  const [filters, setFilters] = useState<FilterLeaf[]>(
    () => (config.filters as FilterLeaf[] | undefined) ?? [],
  );
  // Sorts (table only)
  const [sorts, setSorts] = useState<SortEntry[]>(
    () => (config.sorts as SortEntry[] | undefined) ?? [],
  );
  // Columns visibility (table only)
  // Модалка редактирует только набор и порядок field-колонок; view-only колонки
  // (связи, расчётные, атрибуты справочников) при сохранении переносятся как есть —
  // см. buildColumnsConfig.
  const savedColumns = useMemo(() => readSavedColumns(config.columns), [config.columns]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const fieldColumns = savedColumns.filter(isFieldColumn);
    return fieldColumns.length > 0 ? fieldColumns.map((c) => c.fieldSlug) : fields.map((f) => f.slug);
  });
  // Kanban settings
  const [columnField, setColumnField] = useState<string>(
    () => (config.columnField as string) ?? '',
  );
  const [columnFieldPath, setColumnFieldPath] = useState<string[]>(
    () => (config.columnFieldPath as string[] | undefined) ?? [],
  );
  const [cardTitleField, setCardTitleField] = useState<string>(
    () => (config.cardTitleField as string) ?? '',
  );
  const [cardSubtitleField, setCardSubtitleField] = useState<string | undefined>(
    () => (config.cardSubtitleField as string | undefined) ?? undefined,
  );
  // Kanban: белый список видимых колонок-статусов. Пустой = показаны все опции
  // (включая добавленные позже) — иначе новая опция статуса «пропадает» с доски.
  const [visibleColumnValues, setVisibleColumnValues] = useState<string[]>(
    () => (config.visibleColumnValues as string[] | undefined) ?? [],
  );
  // Kanban: порядок колонок доски (включая скрытые — чтобы при включении колонка
  // вставала на своё место). Пусто = порядок опций поля.
  const [columnOrder, setColumnOrder] = useState<string[]>(
    () => (config.columnOrder as string[] | undefined) ?? [],
  );
  const [isDefault, setIsDefault] = useState(view.isDefault);

  // Grouping (table only) — multi-level
  const [groupingLevels, setGroupingLevels] = useState<GroupingLevel[]>(() => {
    const raw = config.grouping;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw as GroupingLevel[];
    // Single legacy format
    const single = raw as { fieldSlug?: string; order?: 'name' | 'count' };
    return single.fieldSlug ? [{ fieldSlug: single.fieldSlug, order: single.order ?? 'name' }] : [];
  });

  const selectableFields = fields.filter((f) => f.fieldType === 'select' || f.fieldType === 'reference');
  // Системное мета-поле «Кто создал» — допускаем как уровень группировки.
  // На рендере таблицы группировка решается клиентски в buildGroupTree, который
  // знает про специальный slug `_created_by`.
  const createdBySynthetic: FieldDefinition = {
    id: '__created_by__',
    itemTypeId: '00000000-0000-0000-0000-000000000000',
    name: 'Кто создал',
    slug: '_created_by',
    fieldType: 'text',
    config: {},
    required: false,
    sortOrder: -1,
    createdAt: '',
    updatedAt: '',
  };
  // Список для группировки — тот же, что у попапа на панели (в нём уже есть
  // `_refattr:`, `_item_type`, `_created_by`); «Кто создал» добавляем, только
  // если его там нет, чтобы не задвоить.
  const groupingSource = groupingFields ?? fields;
  const groupableFields = [
    ...groupingSource.filter(
      (f) =>
        ['select', 'multi_select', 'boolean', 'reference', 'user'].includes(f.fieldType)
        || f.slug.startsWith('_refattr:')
        || f.slug === '_created_by',
    ),
    ...(groupingSource.some((f) => f.slug === '_created_by') ? [] : [createdBySynthetic]),
  ];

  const handleSave = () => {
    let newConfig: Record<string, unknown>;
    if (isKanban) {
      newConfig = {
        ...config,
        kind: 'kanban',
        columnField,
        columnFieldPath: columnFieldPath.length > 0 ? columnFieldPath : undefined,
        cardTitleField,
        cardSubtitleField,
        filters,
        sorts,
        visibleColumnValues,
        columnOrder,
      };
      // Чистим пустой path, чтобы не плодить undefined-ключ.
      if (columnFieldPath.length === 0) delete (newConfig as { columnFieldPath?: unknown }).columnFieldPath;
    } else {
      newConfig = {
        ...config,
        kind: 'table',
        columns: buildColumnsConfig(savedColumns, visibleColumns),
        filters,
        sorts,
        grouping: groupingLevels.length > 0
          ? groupingLevels.map((l) => ({ fieldSlug: l.fieldSlug, order: l.order, defaultExpanded: true }))
          : undefined,
      };
    }
    const extra: { isDefault?: boolean; icon?: string | null; name?: string; itemTypeIds?: string[] } = {};
    if (isDefault !== view.isDefault) extra.isDefault = isDefault;
    if (icon !== ((view as any).icon ?? null)) extra.icon = icon;
    const trimmedName = name.trim();
    if (trimmedName && trimmedName !== view.name) extra.name = trimmedName;
    const prevTypeIds = view.itemTypeIds ?? [];
    const typesChanged =
      selectedTypeIds.length > 0
      && (selectedTypeIds.length !== prevTypeIds.length
        || selectedTypeIds.some((id) => !prevTypeIds.includes(id)));
    if (typesChanged) extra.itemTypeIds = selectedTypeIds;
    onSave(newConfig, Object.keys(extra).length > 0 ? extra : undefined);
  };

  return (
    <Modal
      title={`Настройки: ${view.name}`}
      open={open}
      onCancel={onCancel}
      onOk={handleSave}
      okText="Сохранить"
      cancelText="Отмена"
      okButtonProps={{ disabled: selectedTypeIds.length === 0 }}
      width={560}
      styles={{ body: { maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', paddingRight: 8 } }}
    >
      <Form layout="vertical" size="small" style={{ marginBottom: -VIEW_SETTINGS_GAP }}>
        <ViewSettingsSection title="Общее">
          <Form.Item label="Название" style={VIEW_SETTINGS_ITEM} required>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <SpaceIconPicker iconOnly value={icon} onChange={setIcon} />
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Название представления"
                style={{ flex: 1 }}
              />
            </div>
          </Form.Item>
          <Form.Item label="Типы записей" style={VIEW_SETTINGS_ITEM} required>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Выберите типы записей"
              value={selectedTypeIds}
              onChange={setSelectedTypeIds}
              options={allItemTypes.map((t) => ({
                value: t.id,
                label: `${t.icon ? t.icon + ' ' : ''}${t.name}`,
              }))}
            />
          </Form.Item>
          <Form.Item style={VIEW_SETTINGS_ITEM_LAST}>
            <Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)}>
              Открывать это представление первым
            </Checkbox>
          </Form.Item>
        </ViewSettingsSection>

        {/* Table: column visibility */}
        {!isKanban && (
          <ViewSettingsSection title="Таблица">
            <Form.Item label="Видимые колонки" style={VIEW_SETTINGS_ITEM_LAST}>
              <VisibleColumnsEditor
                visibleColumns={visibleColumns}
                fields={fields}
                onChange={setVisibleColumns}
              />
            </Form.Item>
          </ViewSettingsSection>
        )}

        {/* Kanban settings */}
        {isKanban && (
          <>
            <ViewSettingsSection title="Колонки доски">
              <Form.Item
                label="Раскладывать по полю"
                style={VIEW_SETTINGS_ITEM}
                required
              >
                <Select
                  value={columnField || undefined}
                  onChange={(v) => { setColumnField(v); setColumnFieldPath([]); setVisibleColumnValues([]); }}
                  options={selectableFields.map((f) => ({ value: f.slug, label: f.name }))}
                  placeholder="Выберите поле"
                />
              </Form.Item>
              {(() => {
                const cf = fields.find((f) => f.slug === columnField);
                if (!cf || cf.fieldType !== 'reference') return null;
                return (
                  <Form.Item
                    label="Брать значение из справочника"
                    style={VIEW_SETTINGS_ITEM_LAST}
                    extra="Не обязательно. Колонки можно строить не по самому полю, а по колонке справочника — и дальше по цепочке, до 5 шагов."
                  >
                    <KanbanColumnPathPicker
                      referenceField={cf}
                      value={columnFieldPath}
                      onChange={setColumnFieldPath}
                    />
                  </Form.Item>
                );
              })()}
              {(() => {
                const cf = fields.find((f) => f.slug === columnField);
                if (!cf || cf.fieldType !== 'select') return null;
                const opts =
                  ((cf.config as { options?: Array<{ value: string; label: string; color?: string }> }).options) ?? [];
                if (opts.length === 0) return null;
                // Пустой белый список = показаны все опции. В UI это все отмеченные чекбоксы.
                const checked = visibleColumnValues.length === 0 ? opts.map((o) => o.value) : visibleColumnValues;
                return (
                  <Form.Item
                    label="Какие колонки показывать"
                    style={VIEW_SETTINGS_ITEM_LAST}
                    extra="Порядок — перетаскиванием за ручку. Снятая галочка убирает колонку с доски. Пока отмечены все, новый статус появится на доске сам."
                  >
                    <KanbanColumnsEditor
                      options={opts}
                      checked={checked}
                      order={columnOrder}
                      onToggle={(value, on) => {
                        const next = on
                          ? [...checked, value]
                          : checked.filter((v) => v !== value);
                        // Все отмечены → храним [], чтобы будущие опции показывались автоматически.
                        setVisibleColumnValues(next.length === opts.length ? [] : next);
                      }}
                      onReorder={setColumnOrder}
                    />
                  </Form.Item>
                );
              })()}
            </ViewSettingsSection>

            <ViewSettingsSection title="Карточка">
              <Form.Item label="Заголовок" style={VIEW_SETTINGS_ITEM} required>
                <Select
                  value={cardTitleField || undefined}
                  onChange={setCardTitleField}
                  options={[
                    ...(selectedTypeIds.length > 1 ? [{ value: ITEM_TYPE_SLUG, label: 'Тип записи' }] : []),
                    ...fields.map((f) => ({ value: f.slug, label: f.name })),
                  ]}
                  placeholder="Выберите поле"
                />
              </Form.Item>
              <Form.Item label="Вторая строка" style={VIEW_SETTINGS_ITEM_LAST}>
                <Select
                  value={cardSubtitleField}
                  onChange={setCardSubtitleField}
                  options={[
                    ...(selectedTypeIds.length > 1 ? [{ value: ITEM_TYPE_SLUG, label: 'Тип записи' }] : []),
                    ...fields.map((f) => ({ value: f.slug, label: f.name })),
                  ]}
                  placeholder="Не выбрано"
                  allowClear
                />
              </Form.Item>
            </ViewSettingsSection>
          </>
        )}

        <ViewSettingsSection title="Отбор и порядок" last>
          {/* Saved filters */}
          <Form.Item label="Показывать только записи, где" style={VIEW_SETTINGS_ITEM}>
            <FilterEditor
              fields={fields}
              filters={filters}
              onUpdate={(idx, patch) => setFilters((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)))}
              onRemove={(idx) => setFilters((prev) => prev.filter((_, i) => i !== idx))}
              onAdd={() => {
                const firstField = fields[0];
                if (!firstField) return;
                const ops = getOperatorsForFieldType(firstField.fieldType);
                setFilters((prev) => [
                  ...prev,
                  {
                    fieldSlug: firstField.slug,
                    operator: ops[0] ?? 'eq',
                    value: undefined,
                    ...(prev.length > 0 ? { combinator: 'and' as const } : {}),
                  },
                ]);
              }}
              onClear={() => setFilters([])}
              ensureReferenceOptions={ensureReferenceOptions}
              resolvedRefs={resolvedRefs}
            />
          </Form.Item>

          {/* Saved sorts — общие для всех, кто открывает представление */}
          <Form.Item
            label={isKanban ? 'Порядок карточек в колонке' : 'Порядок строк'}
            style={isKanban ? VIEW_SETTINGS_ITEM_LAST : VIEW_SETTINGS_ITEM}
            extra={
              isKanban
                ? 'Один на всех, кто открывает доску. Пока он задан, карточки внутри колонки нельзя переставлять мышкой.'
                : undefined
            }
          >
            {sorts.map((s, i) => (
              <Tag
                key={i}
                closable
                onClose={() => setSorts((prev) => prev.filter((_, idx) => idx !== i))}
                color="cyan"
                style={{ marginBottom: 4 }}
              >
                {fields.find((fd) => fd.slug === s.fieldSlug)?.name ?? s.fieldSlug}{' '}
                {s.direction === 'asc' ? '↑' : '↓'}
              </Tag>
            ))}
            <SortAddInline
              fields={fields}
              onAdd={(slug, dir) => setSorts((prev) => [...prev, { fieldSlug: slug, direction: dir }])}
            />
          </Form.Item>

          {/* Grouping (table only) — multi-level */}
          {!isKanban && (
            <Form.Item label="Группировка" style={VIEW_SETTINGS_ITEM_LAST}>
              {groupingLevels.map((level, idx) => {
                const fieldDef = groupableFields.find((f) => f.slug === level.fieldSlug);
                return (
                  <div key={level.fieldSlug} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                    <Tag color="blue" style={{ margin: 0 }}>
                      {idx + 1}. {fieldDef?.name ?? level.fieldSlug}
                    </Tag>
                    <Select
                      size="small"
                      style={{ width: 110 }}
                      value={level.order}
                      onChange={(order) => {
                        setGroupingLevels((prev) =>
                          prev.map((l) => l.fieldSlug === level.fieldSlug ? { ...l, order } : l),
                        );
                      }}
                      options={[
                        { value: 'name', label: 'По имени' },
                        { value: 'count', label: 'По кол-ву' },
                      ]}
                    />
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<CloseOutlined style={{ fontSize: 10 }} />}
                      onClick={() => setGroupingLevels((prev) => prev.filter((l) => l.fieldSlug !== level.fieldSlug))}
                    />
                  </div>
                );
              })}
              <Select
                size="small"
                style={{ width: '100%' }}
                placeholder={groupingLevels.length === 0 ? 'Не группировать' : '+ Добавить уровень'}
                value={undefined}
                onChange={(slug) => {
                  if (slug && !groupingLevels.some((l) => l.fieldSlug === slug)) {
                    setGroupingLevels((prev) => [...prev, { fieldSlug: slug, order: 'name' }]);
                  }
                }}
                options={groupableFields
                  .filter((f) => !groupingLevels.some((l) => l.fieldSlug === f.slug))
                  .map((f) => ({ value: f.slug, label: f.name }))}
              />
            </Form.Item>
          )}
        </ViewSettingsSection>
      </Form>
    </Modal>
  );
}

function SortAddInline({
  fields,
  onAdd,
}: {
  fields: FieldDefinition[];
  onAdd: (slug: string, dir: 'asc' | 'desc') => void;
}): JSX.Element {
  const [adding, setAdding] = useState(false);
  const [fieldSlug, setFieldSlug] = useState<string | null>(null);
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc');

  if (!adding) {
    return (
      <Button size="small" icon={<SortAscendingOutlined />} onClick={() => setAdding(true)}>
        Добавить сортировку
      </Button>
    );
  }

  return (
    <AntSpace size="small">
      <Select
        size="small"
        style={{ width: 140 }}
        placeholder="Поле"
        value={fieldSlug}
        onChange={setFieldSlug}
        options={fields.map((f) => ({ value: f.slug, label: f.name }))}
      />
      <Select
        size="small"
        style={{ width: 100 }}
        value={direction}
        onChange={setDirection}
        options={[
          { value: 'asc', label: 'По возр.' },
          { value: 'desc', label: 'По убыв.' },
        ]}
      />
      <Button
        size="small"
        type="primary"
        disabled={!fieldSlug}
        onClick={() => {
          if (fieldSlug) {
            onAdd(fieldSlug, direction);
            setFieldSlug(null);
            setAdding(false);
          }
        }}
      >
        OK
      </Button>
      <Button size="small" onClick={() => setAdding(false)}>
        Отмена
      </Button>
    </AntSpace>
  );
}

// ─── CSV Import modal (M10) ──────────────────────────────────────────────────

function CsvImportModal({
  open,
  spaceId,
  itemTypeId,
  fields,
  viewFilters,
  userFilters,
  dictionaryOptionsById,
  ensureReferenceOptions,
  onClose,
  onComplete,
}: {
  open: boolean;
  spaceId: string;
  itemTypeId: string;
  fields: FieldDefinition[];
  viewFilters: FilterLeaf[];
  userFilters: FilterLeaf[];
  dictionaryOptionsById: Record<string, Array<{ value: string; label: string }>>;
  ensureReferenceOptions: (field: FieldDefinition) => Promise<Array<{ value: string; label: string }>>;
  onClose: () => void;
  onComplete: () => void;
}): JSX.Element {
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('');
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const { registerJob } = useJobs();
  // Ключ маппинга для upsert: composite-key из нескольких скалярных
  // полей (запись считается совпадающей, если ВСЕ выбранные поля
  // совпадают со значениями строки CSV). `__id__` — поиск по id записи
  // (валидно только как единственный элемент). Пустой массив — режим
  // «только создание».
  const [keyFieldSlugs, setKeyFieldSlugs] = useState<string[]>([]);

  // Дефолты для всех импортируемых записей (FR-4.1).
  // Приоритет применения: CSV-колонка (если смаплена и непуста)
  // > значение из этой формы > defaultValue из FieldConfig (на бэке).
  const filterAutofill = useMemo(
    () => defaultsFromFilters([...viewFilters, ...userFilters], fields),
    [viewFilters, userFilters, fields],
  );
  const [defaults, setDefaults] = useState<Record<string, unknown>>({});
  const [refOptionsByField, setRefOptionsByField] = useState<Record<string, Array<{ value: string; label: string }>>>({});

  const ensureOptionsForField = useCallback(async (field: FieldDefinition) => {
    const opts = await ensureReferenceOptions(field);
    setRefOptionsByField((prev) => ({ ...prev, [field.slug]: opts }));
    return opts;
  }, [ensureReferenceOptions]);

  const referenceOptionsForCsvField = useCallback((field: FieldDefinition) => {
    if (refOptionsByField[field.slug]) return refOptionsByField[field.slug];
    const dictionaryId = (field.config as { dictionaryId?: string } | null)?.dictionaryId;
    return dictionaryId ? (dictionaryOptionsById[dictionaryId] ?? []) : [];
  }, [dictionaryOptionsById, refOptionsByField]);

  // При открытии модалки пересобираем дефолты: фильтр представления
  // > defaultValue из конфига поля. Сохраняем пользовательские
  // правки в рамках одной открытой сессии модалки (state не сбрасывается
  // в onChange), но при повторном открытии — стартуем с актуальных
  // фильтров. configDefaults тоже подмешиваем, чтобы пользователь
  // видел стартовое значение, заданное в типе.
  useEffect(() => {
    if (!open) return;
    const configDefaults = defaultsFromFieldConfigs(fields);
    setDefaults({ ...configDefaults, ...filterAutofill });
    // Подгружаем опции для reference-полей, у которых есть дефолт,
    // чтобы AntD Select показал label, а не сырой uuid.
    for (const f of fields) {
      if (f.fieldType !== 'reference' && f.fieldType !== 'user') continue;
      if (!(f.slug in filterAutofill)) continue;
      void ensureOptionsForField(f);
    }
  }, [open, fields, filterAutofill, ensureOptionsForField]);

  // Парсим .csv / .xlsx / .xls / Google Sheets единообразно: читаем
  // SheetJS, берём первый лист, фильтруем полностью пустые строки и
  // конвертируем обратно в CSV-строку — backend
  // (`POST /api/spaces/:id/import/csv`) ждёт именно CSV-текст, и менять
  // серверный контракт ради этого UI-апгрейда не нужно.
  //
  // Важно: `XLSX.utils.sheet_to_csv` сохраняет пустые строки исходного
  // листа as-is (Excel часто оставляет 1000 «фантомных» строк под
  // последней заполненной). Если их не отфильтровать — на бэке
  // создастся 1000 пустых айтемов. Поэтому перед csv-конверсией
  // сжимаем aoa до строк, в которых есть хотя бы одна непустая ячейка.
  const handleFile = (file: File, sheetName?: string): void => {
    void readWorkbook(file)
      .then((wb) => {
        const name = resolveSheetName(wb, sheetName);
        if (!name) {
          void message.warning('Файл пустой или не содержит листов');
          return;
        }
        const sheet = wb.Sheets[name]!;
        const aoa = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
          header: 1,
          blankrows: false,
          defval: '',
          raw: false,
        });
        if (aoa.length === 0) {
          void message.warning('Файл пустой');
          return;
        }
        const headers = ((aoa[0] as unknown[]) ?? []).map((v) => String(v ?? '').trim());
        const dataRows = aoa
          .slice(1)
          .filter((r) => (r as unknown[]).some((c) => String(c ?? '').trim() !== ''));
        if (dataRows.length === 0) {
          void message.warning('В файле только заголовок — нет данных');
          return;
        }
        const filteredSheet = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
        const csv = XLSX.utils.sheet_to_csv(filteredSheet);
        setCsvText(csv);
        setFileName(`${file.name} · ${dataRows.length} строк`);
        setCsvHeaders(headers);
        // Сопоставление заголовков с полями терпимо к регистру,
        // неразрывным и двойным пробелам, «ё» и пояснению в скобках —
        // раньше сравнение было побайтовым, и колонка с виду совпадающим
        // названием оставалась несопоставленной. Ищем только среди полей,
        // доступных в выпадающем списке: вычисляемые импортировать
        // нельзя, и подставленное значение всё равно не отобразилось бы.
        const targets = fields.filter((f) => f.fieldType !== 'computed');
        const matched = matchHeaders(headers, targets, (f) => [f.slug, f.name]);
        const autoMap: Record<string, string> = {};
        for (const [header, field] of matched) autoMap[header] = field.slug;
        setMapping(autoMap);
      })
      .catch((err) => {
        void message.error(
          err instanceof Error ? `Не удалось прочитать файл: ${err.message}` : 'Не удалось прочитать файл',
        );
      });
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      // Очищаем дефолты от пустых/undefined-значений — пустую строку
      // или null отправлять как «дефолт» бессмысленно (это «не задано»),
      // но число 0 и boolean false должны проходить.
      const cleanedDefaults: Record<string, unknown> = {};
      for (const [slug, value] of Object.entries(defaults)) {
        if (value === undefined || value === null) continue;
        if (typeof value === 'string' && value.trim() === '') continue;
        if (Array.isArray(value) && value.length === 0) continue;
        cleanedDefaults[slug] = value;
      }
      const { jobId } = await importItemsCsv(spaceId, {
        itemTypeId,
        mapping,
        csv: csvText,
        defaults: Object.keys(cleanedDefaults).length > 0 ? cleanedDefaults : undefined,
        keyFieldSlugs: keyFieldSlugs.length > 0 ? keyFieldSlugs : undefined,
      });
      registerJob(jobId, 'Импорт записей из CSV');
      void message.success('Импорт запущен');
      onComplete();
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Ошибка импорта');
    } finally {
      setImporting(false);
    }
  };

  const editableFields = useMemo(
    () => fields.filter((f) => f.fieldType !== 'computed'),
    [fields],
  );

  return (
    <Modal
      title="Импорт CSV / Excel"
      open={open}
      onCancel={onClose}
      onOk={() => void handleImport()}
      okText="Импортировать"
      cancelText="Отмена"
      okButtonProps={{ loading: importing, disabled: !csvText || Object.keys(mapping).length === 0 }}
      width={720}
    >
      <Form layout="vertical">
        <Form.Item label="Источник данных">
          <RemoteSourcePanel
            accept=".csv,.tsv,.txt,.xlsx,.xls"
            formatsHint="Поддерживается .csv, .xlsx, .xls"
            onLoaded={handleFile}
          />
          {fileName && (
            <div style={{ marginTop: 8, color: 'var(--color-text-secondary)', fontSize: 12 }}>
              📄 {fileName} · {csvHeaders.length} колонок
            </div>
          )}
        </Form.Item>
        {csvHeaders.length > 0 && (
          <Form.Item label="Маппинг колонок">
            <Descriptions className="lens-create-fields" column={1} size="small" bordered>
              {csvHeaders.map((h) => (
                <Descriptions.Item key={h} label={h}>
                  <Select
                    style={{ width: '100%' }}
                    value={mapping[h] || undefined}
                    onChange={(v) => setMapping((prev) => ({ ...prev, [h]: v }))}
                    allowClear
                    onClear={() => setMapping((prev) => { const next = { ...prev }; delete next[h]; return next; })}
                    options={editableFields.map((f) => ({ value: f.slug, label: f.name }))}
                    placeholder="Не импортировать"
                  />
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Form.Item>
        )}
        {csvHeaders.length > 0 && (
          <Form.Item
            label="Ключ обновления существующих записей"
            help="Можно выбрать несколько полей — запись считается совпадающей, если ВСЕ выбранные поля равны значениям строки. Пусто — все строки создаются как новые. «id записи» работает только в одиночку."
          >
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              value={keyFieldSlugs}
              onChange={(v) => {
                // Если в наборе появился `__id__`, оставляем только его
                // (id уникален сам по себе, комбинировать его с другими
                // полями бессмысленно — бэкенд это и так отбрасывает с
                // ошибкой). Если убрали `__id__` — оставляем остальные.
                const arr = (v ?? []) as string[];
                const hasId = arr.includes('__id__');
                const hadId = keyFieldSlugs.includes('__id__');
                if (hasId && !hadId) {
                  setKeyFieldSlugs(['__id__']);
                } else if (hasId && arr.length > 1) {
                  setKeyFieldSlugs(arr.filter((s) => s !== '__id__'));
                } else {
                  setKeyFieldSlugs(arr);
                }
              }}
              allowClear
              placeholder="Не обновлять — только создавать"
              options={[
                { value: '__id__', label: 'id записи (колонка id из экспорта)' },
                ...editableFields
                  .filter((f) => ['text', 'textarea', 'url', 'number', 'date', 'select'].includes(f.fieldType))
                  .map((f) => ({ value: f.slug, label: f.name })),
              ]}
            />
          </Form.Item>
        )}
        {editableFields.length > 0 && (
          <Form.Item
            label="Значения по умолчанию"
            help="Применяются ко всем создаваемым записям. Если поле смаплено на колонку CSV и колонка не пуста — выигрывает CSV."
          >
            <Descriptions className="lens-create-fields" column={1} size="small" bordered>
              {editableFields.map((field) => {
                const fromFilter = field.slug in filterAutofill;
                const stillMatches = fromFilter && defaults[field.slug] === filterAutofill[field.slug];
                const isModified = fromFilter && !stillMatches;
                const setValue = (v: unknown): void =>
                  setDefaults((prev) => ({ ...prev, [field.slug]: v }));
                return (
                  <Descriptions.Item
                    key={field.slug}
                    label={
                      <span>
                        {field.name}
                        {stillMatches && (
                          <span
                            style={{ marginLeft: 6, color: '#1677ff', fontSize: 11, fontWeight: 'normal' }}
                            title="Значение взято из фильтра представления (FR-4.1)"
                          >
                            · из фильтра
                          </span>
                        )}
                        {isModified && (
                          <span
                            style={{ marginLeft: 6, color: '#d4380d', fontSize: 11, fontWeight: 'normal' }}
                            title="Значение отличается от фильтра представления — записи могут не попасть в это представление"
                          >
                            ⚠ изменено
                          </span>
                        )}
                      </span>
                    }
                  >
                    {field.fieldType === 'text' || field.fieldType === 'url' || field.fieldType === 'textarea' || field.fieldType === 'markdown' ? (
                      <Input
                        placeholder={field.name}
                        value={(defaults[field.slug] as string) ?? ''}
                        onChange={(e) => setValue(e.target.value || undefined)}
                      />
                    ) : field.fieldType === 'number' ? (
                      <Input
                        type="number"
                        placeholder={field.name}
                        value={defaults[field.slug] != null ? String(defaults[field.slug]) : ''}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          setValue(e.target.value.trim() === '' ? undefined : Number.isNaN(n) ? undefined : n);
                        }}
                      />
                    ) : field.fieldType === 'select' ? (
                      <Select
                        style={{ width: '100%' }}
                        placeholder={field.name}
                        allowClear
                        value={(defaults[field.slug] as string) ?? undefined}
                        onChange={(v) => setValue(v ?? undefined)}
                        options={((field.config as { options?: Array<{ value: string; label: string }> })?.options ?? []).map((o) => ({
                          value: o.value, label: o.label || o.value,
                        }))}
                      />
                    ) : field.fieldType === 'multi_select' ? (
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder={field.name}
                        value={(defaults[field.slug] as string[]) ?? []}
                        onChange={(v) => setValue(v.length > 0 ? v : undefined)}
                        options={((field.config as { options?: Array<{ value: string; label: string }> })?.options ?? []).map((o) => ({
                          value: o.value, label: o.label || o.value,
                        }))}
                      />
                    ) : field.fieldType === 'boolean' ? (
                      <Select
                        style={{ width: '100%' }}
                        placeholder={field.name}
                        allowClear
                        value={defaults[field.slug] as boolean | undefined}
                        onChange={(v) => setValue(v ?? undefined)}
                        options={[{ value: true, label: 'Да' }, { value: false, label: 'Нет' }]}
                      />
                    ) : field.fieldType === 'date' ? (
                      <DatePicker
                        style={{ width: '100%' }}
                        showTime={(field.config as { includeTime?: boolean })?.includeTime ?? false}
                        format={datePickerFormat((field.config as { includeTime?: boolean })?.includeTime ?? false)}
                        value={defaults[field.slug] ? dayjs(defaults[field.slug] as string) : null}
                        onChange={(d) => setValue(dateFieldValue(d, (field.config as { includeTime?: boolean })?.includeTime ?? false) ?? undefined)}
                      />
                    ) : field.fieldType === 'reference' || field.fieldType === 'user' ? (
                      <Select
                        style={{ width: '100%' }}
                        placeholder={field.name}
                        allowClear
                        showSearch
                        mode={((field.config as { multiple?: boolean })?.multiple) ? 'multiple' : undefined}
                        value={defaults[field.slug] as string | string[] | undefined}
                        onChange={(v) => setValue(v ?? undefined)}
                        onFocus={() => { void ensureOptionsForField(field); }}
                        filterOption={(input, opt) => (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())}
                        options={referenceOptionsForCsvField(field)}
                      />
                    ) : (
                      <Input
                        placeholder={field.name}
                        value={(defaults[field.slug] as string) ?? ''}
                        onChange={(e) => setValue(e.target.value || undefined)}
                      />
                    )}
                  </Descriptions.Item>
                );
              })}
            </Descriptions>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

// ─── SpacePage ───────────────────────────────────────────────────────────────

export function SpacePage(): JSX.Element {
  // URL может прийти в двух формах: короткой `/s/:spaceSlug` (канон) и legacy
  // `/spaces/:id` (uuid). `id` — рабочий uuid, по которому идут ВСЕ запросы к
  // вложенным ресурсам. При slug-роуте резолвим slug→uuid один раз.
  const {
    id: routeId,
    viewId: urlViewId,
    spaceSlug: routeSlug,
  } = useParams<{ id?: string; viewId?: string; spaceSlug?: string }>();
  const navigate = useNavigate();
  const [id, setId] = useState<string | undefined>(routeId);
  const [space, setSpace] = useState<SpaceDto | null>(null);
  const [allItemTypes, setAllItemTypes] = useState<ItemType[]>([]);
  const [views, setViews] = useState<View[]>([]);
  const [activeViewId, setActiveViewIdState] = useState<string | null>(null);
  const [showCreateView, setShowCreateView] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);

  // Резолв slug→uuid для короткой ссылки `/s/:slug`. Для legacy `/spaces/:id`
  // берём uuid из URL напрямую.
  useEffect(() => {
    if (routeId) {
      setId(routeId);
      return;
    }
    if (!routeSlug) return;
    let cancelled = false;
    resolveSpaceSlug(routeSlug)
      .then((r) => {
        if (!cancelled) setId(r.id);
      })
      .catch((err) => {
        if (cancelled) return;
        setPageError(err instanceof ApiError ? err.message : 'Пространство не найдено');
        setPageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [routeId, routeSlug]);

  // Канонический базовый путь пространства для навигации: всегда slug, если
  // пространство уже загружено; иначе — то, что есть в URL.
  const spaceBasePath = space
    ? `/s/${space.slug}`
    : routeSlug
      ? `/s/${routeSlug}`
      : id
        ? `/spaces/${id}`
        : '';

  // Wrapper: при смене активного view синхронизирует URL, чтобы ссылка
  // указывала на конкретное представление, а не только на пространство.
  const setActiveViewId = useCallback(
    (newId: string | null, opts?: { replace?: boolean }) => {
      setActiveViewIdState(newId);
      if (!spaceBasePath) return;
      const target = newId ? `${spaceBasePath}/views/${newId}` : spaceBasePath;
      navigate(target, { replace: opts?.replace ?? false });
    },
    [spaceBasePath, navigate],
  );

  useEffect(() => {
    if (!id) return;

    setPageLoading(true);
    Promise.all([getSpace(id), listItemTypes(id), listViews(id)])
      .then(async ([s, typesList, viewsRes]) => {
        setSpace(s);
        // Записываем «недавнее» только при успешной загрузке — здесь
        // доступны человекочитаемые name/slug/icon (не сырой id).
        pushRecentSpace({ id: s.id, slug: s.slug, name: s.name, icon: s.icon });
        setViews(viewsRes.views);

        // Load all item types with fields
        const fullTypes = await Promise.all(
          typesList.map((t) => getItemType(id, t.id)),
        );
        setAllItemTypes(fullTypes);

        // Выбор активного view: приоритет — viewId из URL, иначе default/первый.
        const fromUrl = urlViewId && viewsRes.views.find((v) => v.id === urlViewId);
        const activeId = fromUrl
          ? fromUrl.id
          : (viewsRes.views.find((v) => v.isDefault) ?? viewsRes.views[0] ?? null)?.id ?? null;
        setActiveViewIdState(activeId);
        // Канонизируем URL на slug-форму (`/s/:slug/views/:viewId`). Это
        // одновременно авто-апгрейдит legacy `/spaces/:uuid` ссылки и
        // проставляет default view, если во входном URL его не было.
        const search = window.location.search;
        const target = activeId ? `/s/${s.slug}/views/${activeId}` : `/s/${s.slug}`;
        navigate(`${target}${search}`, { replace: true });

        setPageLoading(false);
      })
      .catch((err) => {
        setPageError(err instanceof ApiError ? err.message : 'Не удалось загрузить пространство');
        setPageLoading(false);
      });
  }, [id]);

  // Синхронизация state ← URL для back/forward и внешних ссылок.
  useEffect(() => {
    if (!urlViewId || views.length === 0) return;
    if (urlViewId !== activeViewId && views.some((v) => v.id === urlViewId)) {
      setActiveViewIdState(urlViewId);
    }
  }, [urlViewId, views, activeViewId]);

  const activeView = views.find((v) => v.id === activeViewId) ?? null;

  // Название пространства во вкладке: при десятке открытых вкладок только по
  // нему и видно, какой проект где открыт.
  useDocumentTitle(space?.name, activeView?.name);

  if (pageLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }} data-testid="space-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (pageError || !space) {
    return (
      <Alert
        type="error"
        message={pageError ?? 'Пространство не найдено'}
        showIcon
        data-testid="space-error"
      />
    );
  }

  return (
    <div>
      {/* Шапка пространства — одна строка: «назад», имя, справа «Настройки».
          Раньше это были две строки по 16px отступа каждая (~104px); на
          ноутбуке они съедали высоту, которой не хватало самой таблице. */}
      <div data-fullscreen-hide style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <Tooltip title="Все пространства">
          <Link
            to="/"
            aria-label="Все пространства"
            style={{ display: 'inline-flex', alignItems: 'center', color: 'inherit', fontSize: 16, lineHeight: 1 }}
          >
            <ArrowLeftOutlined />
          </Link>
        </Tooltip>
        <h2 style={{ margin: 0, fontSize: 20, lineHeight: '28px' }} data-testid="space-page-title">
          {space.name}
        </h2>
        {space.isArchived ? <Tag color="orange">Архив</Tag> : null}
        {/* Кнопка «Настройки» — admin-only (SRS §2.5.2). Сервер всё равно
            отдаст 403 на PATCH/DELETE, но UI не должен показывать вход в
            недоступную страницу. Скрываем для member и viewer. */}
        {space.myRole === 'admin' ? (
          <Link
            to={`/s/${space.slug}/settings`}
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, color: '#595959', fontWeight: 500 }}
            data-testid="space-settings-link"
          >
            <SettingOutlined />
            Настройки
          </Link>
        ) : null}
      </div>

      {allItemTypes.length === 0 ? (
        <Alert
          type="info"
          message="В пространстве нет типов записей"
          description={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
              <span>
                Перейдите в{' '}
                <Link to={`/s/${space.slug}/settings?tab=item-types`}>настройки → Типы и поля</Link>{' '}
                и создайте первый тип.
              </span>
              {space.myRole === 'admin' ? (
                <Link to={`/s/${space.slug}/settings?tab=item-types`}>
                  <Button type="primary" icon={<PlusOutlined />} data-testid="create-item-type-cta">
                    Создать тип записи
                  </Button>
                </Link>
              ) : null}
            </div>
          }
          showIcon
          data-testid="space-no-types"
        />
      ) : views.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 64 }}>
          <p style={{ color: '#999', marginBottom: 16 }}>Нет представлений</p>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowCreateView(true)}
          >
            Создать представление
          </Button>
          <CreateViewModal
            open={showCreateView}
            allItemTypes={allItemTypes}
            defaultItemTypeIds={allItemTypes.length > 0 ? [allItemTypes[0]!.id] : []}
            onCancel={() => setShowCreateView(false)}
            onCreate={async (val) => {
              try {
                const config =
                  val.viewType === 'kanban'
                    ? {
                        kind: 'kanban' as const,
                        columnField: val.columnField!,
                        ...(val.columnFieldPath && val.columnFieldPath.length > 0
                          ? { columnFieldPath: val.columnFieldPath }
                          : {}),
                        visibleColumnValues: [],
                        filters: val.filters,
                        cardTitleField: val.cardTitleField!,
                        cardSubtitleField: val.cardSubtitleField,
                        cardExtraFields: [],
                      }
                    : { kind: 'table' as const, columns: [], filters: val.filters, sorts: [] };

                const created = await createView(space.id, {
                  itemTypeIds: val.itemTypeIds,
                  name: val.name,
                  icon: val.icon,
                  viewType: val.viewType,
                  config,
                  isDefault: true,
                });
                setViews((prev) => [...prev, created]);
                setActiveViewId(created.id);
                setShowCreateView(false);
                message.success('Представление создано');
                emitSpacesChanged();
              } catch (err) {
                void message.error(err instanceof ApiError ? err.message : 'Не удалось создать представление');
              }
            }}
          />
        </div>
      ) : activeView ? (
        <ItemsTable
          spaceId={space.id}
          view={activeView}
          allItemTypes={allItemTypes}
          views={views}
          setViews={setViews}
          activeViewId={activeViewId}
          setActiveViewId={setActiveViewId}
          showCreateView={showCreateView}
          setShowCreateView={setShowCreateView}
          space={space}
        />
      ) : (
        <div style={{ textAlign: 'center', padding: 64 }}>
          <Spin size="large" />
        </div>
      )}
    </div>
  );
}
